import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  FileText,
  Plus,
  ArrowRight,
  ArrowLeft,
  Check,
  Loader2,
  CalendarClock,
  Trash2,
  Pencil,
  Play as PlayIcon,
  Download,
  Upload,
  RefreshCw,
  ExternalLink,
  Hash,
  AtSign,
  Users,
  Zap,
  Sparkles,
  ChevronRight,
  Film,
  Layers,
  Image as ImageIcon,
  Radio,
  BarChart3,
  LayoutGrid,
  Target,
  Bookmark,
  Info,
  Eye,
  EyeOff,
  Heart,
  MessageCircle,
  FolderOpen,
  Search,
  KeyRound,
  ShieldAlert,
  TrendingUp,
  TrendingDown,
  Minus,
  GitCompareArrows,
  X as XIcon,
  Clock,
  RefreshCcw,
  Trophy,
  Activity,
} from "lucide-react";
import { CampaignTimeline } from "../components/CampaignTimeline";
import { CreatorDrawer } from "../components/CreatorDrawer";
import { CreatorTiers } from "../components/CreatorTiers";
import { TopPerformers } from "../components/TopPerformers";
import { PostedBifurcation, CreatorStatusBadge, creatorStatus } from "../components/PostedBifurcation";
import { ScheduleModal } from "../components/ScheduleModal";
import { RapidApiConnectionBanner } from "../components/RapidApiConnectionBanner";
import { Button } from "@/components/ui/button";
import { useAuth } from "../auth/AuthContext";
import { Input } from "@/components/ui/input";
import { DateField } from "@/components/DateField";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@/components/ui/hover-card";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Vivid avatar palettes for report rows (Influencers-Suite style initials).
const AVATAR_COLORS = [
  "bg-pink-100 text-pink-600",
  "bg-purple-100 text-purple-600",
  "bg-rose-100 text-rose-600",
  "bg-emerald-100 text-emerald-600",
  "bg-sky-100 text-sky-600",
  "bg-amber-100 text-amber-600",
];

// Two branching flows. Users pick Handles vs URLs on step 2 and the rest
// of the wizard follows the branch — no "N/A" prompts for the other flow.
const HANDLES_STEP_TITLES = [
  { n: 1, label: "Name" },
  { n: 2, label: "Mode" },
  { n: 3, label: "Track Terms" },
  { n: 4, label: "Handles" },
  { n: 5, label: "Data & Timeline" },
  { n: 6, label: "Schedule" },
];
const URLS_STEP_TITLES = [
  { n: 1, label: "Name" },
  { n: 2, label: "Mode" },
  { n: 3, label: "Paste URLs" },
  { n: 4, label: "Provider" },
];

const parseList = (t) =>
  (t || "").split(/[\n,]+/).map((s) => s.trim()).filter(Boolean);

function StepBadge({ n, active, done, label }) {
  return (
    <div className="flex items-center gap-3">
      <div
        className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold font-mono transition-all ${
          done
            ? "bg-emerald-600 text-white"
            : active
              ? "bg-slate-900 text-white amber-glow"
              : "bg-white border border-slate-200 text-slate-400"
        }`}
      >
        {done ? <Check size={14} /> : n}
      </div>
      <div>
        <div className="text-[10px] font-mono uppercase tracking-widest text-slate-400">
          Step {n}
        </div>
        <div
          className={`text-xs font-medium ${
            active ? "text-pink-600" : done ? "text-emerald-600" : "text-slate-500"
          }`}
        >
          {label}
        </div>
      </div>
    </div>
  );
}

function Wizard({ open, onClose, onCreated, groups }) {
  const [step, setStep] = useState(1);
  const [mode, setMode] = useState(""); // "handles" | "urls"
  const [name, setName] = useState("");
  const [keywordsText, setKeywordsText] = useState("");
  const [handlesText, setHandlesText] = useState("");
  const [matchMode, setMatchMode] = useState("substring");
  const [sinceDate, setSinceDate] = useState(""); // NEW — historical anchor for INITIAL fetch
  const [trackerMode, setTrackerMode] = useState("both"); // "posts" | "stories" | "both"
  const [storiesMatchAny, setStoriesMatchAny] = useState(false);
  const [fetchHighlights, setFetchHighlights] = useState(true);
  const [fetchLive, setFetchLive] = useState(true);
  const [sessionCookie, setSessionCookie] = useState("");
  const [showCookie, setShowCookie] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [groupSearch, setGroupSearch] = useState("");
  // URL-mode fields
  const [urlsText, setUrlsText] = useState("");
  const [urlProvider, setUrlProvider] = useState("rapidapi");
  const [urlKeywordsText, setUrlKeywordsText] = useState("");
  // ── Scheduling (v2) — user picks BEFORE creating the report ──
  const [autoTrack, setAutoTrack] = useState(false);
  const [storiesCron, setStoriesCron] = useState("");
  const [postsCron, setPostsCron] = useState("");
  const [postsLookback, setPostsLookback] = useState(""); // NO default — user must set per-report

  useEffect(() => {
    if (!open) {
      // reset on close
      setTimeout(() => {
        setStep(1);
        setMode("");
        setName("");
        setKeywordsText("");
        setHandlesText("");
        setMatchMode("substring");
        setSinceDate("");
        setTrackerMode("both");
        setStoriesMatchAny(false);
        setFetchHighlights(true);
        setFetchLive(true);
        setSessionCookie("");
        setShowCookie(false);
        setGroupSearch("");
        setUrlsText("");
        setUrlProvider("apify");
        setUrlKeywordsText("");
        setAutoTrack(false);
        setStoriesCron("");
        setPostsCron("");
        setPostsLookback("");
      }, 300);
    }
  }, [open]);

  const handles = useMemo(() => parseList(handlesText), [handlesText]);
  const keywords = useMemo(() => parseList(keywordsText), [keywordsText]);
  const urlStats = useMemo(() => {
    // Real-time paste analysis: valid IG URLs, extracted unique shortcodes,
    // and count of duplicates so users see the actual fetch cost.
    const lines = (urlsText || "").split(/[\n,]+/).map((s) => s.trim()).filter(Boolean);
    const codeRe = /instagram\.com\/(?:p|reel|reels|tv)\/([A-Za-z0-9_-]+)/i;
    const valid = [];
    const invalid = [];
    const seen = new Set();
    const dupCodes = new Set();
    for (const l of lines) {
      const m = l.match(codeRe);
      if (!m) { invalid.push(l); continue; }
      const code = m[1];
      valid.push({ line: l, code });
      if (seen.has(code)) dupCodes.add(code);
      seen.add(code);
    }
    return {
      totalPasted: lines.length,
      validCount: valid.length,
      invalidCount: invalid.length,
      uniqueCount: seen.size,
      duplicateCount: valid.length - seen.size,
      dupCodes: Array.from(dupCodes).slice(0, 5),
    };
  }, [urlsText]);

  const urls = useMemo(() => {
    // The submitted set is only the deduped, valid ones.
    const codeRe = /instagram\.com\/(?:p|reel|reels|tv)\/([A-Za-z0-9_-]+)/i;
    const seen = new Set();
    const out = [];
    for (const line of (urlsText || "").split(/[\n,]+/).map((s) => s.trim())) {
      const m = line.match(codeRe);
      if (!m || seen.has(m[1])) continue;
      seen.add(m[1]);
      out.push(line);
    }
    return out;
  }, [urlsText]);

  const STEP_TITLES = mode === "urls" ? URLS_STEP_TITLES : HANDLES_STEP_TITLES;

  const filteredGroups = useMemo(() => {
    const q = groupSearch.trim().toLowerCase();
    if (!q) return groups;
    return groups.filter(
      (g) =>
        (g.name || "").toLowerCase().includes(q) ||
        (g.handles || []).some((h) => (h || "").toLowerCase().includes(q))
    );
  }, [groups, groupSearch]);

  const canProceed = () => {
    if (mode === "urls") {
      if (step === 1) return name.trim().length > 0;
      if (step === 2) return !!mode;
      if (step === 3) return urls.length > 0;
      if (step === 4) return !!urlProvider;
      return false;
    }
    // Handles branch (default)
    if (step === 1) return name.trim().length > 0;
    if (step === 2) return !!mode;
    if (step === 3) return keywords.length > 0;
    if (step === 4) return handles.length > 0;
    if (step === 5) return true;
    if (step === 6) return true;
    return false;
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      if (mode === "urls") {
        // 1) Create an empty URL-mode report (no handles, no scheduler)
        const payload = {
          name: name.trim(),
          handles: [],
          keywords: parseList(urlKeywordsText),
          match_mode: "substring",
          since_date: null,
          include_stories: false,
          stories_only: false,
          fetch_highlights: false,
          fetch_live: false,
          enabled: true,
          provider: urlProvider,
          auto_track: false,
          posts_lookback_limit: null,
          report_type: "urls",
        };
        const { data } = await axios.post(`${API}/reports`, payload);
        const rpt = data.report;
        // 2) Immediately kick off the URL batch fetch
        try {
          await axios.post(`${API}/reports/${rpt.id}/fetch-urls`, {
            urls,
            provider: urlProvider,
            keywords: [],   // no keyword filter in URL-mode wizard (all pasted URLs kept)
          });
          toast.success(`Report "${rpt.name}" created — fetching ${urls.length} URL(s) via ${urlProvider === "apify" ? "Apify" : "RapidAPI"}…`);
        } catch (e) {
          toast.warning(`Report created but URL fetch failed to start: ${e.response?.data?.detail || e.message}`);
        }
        onCreated(rpt);
        onClose();
        return;
      }
      // Handles branch (unchanged)
      const wantStories = trackerMode !== "posts";
      const isStoriesOnly = trackerMode === "stories";
      const payload = {
        name: name.trim(),
        handles,
        keywords,
        match_mode: matchMode,
        since_date: sinceDate || null,
        include_stories: wantStories,
        stories_only: isStoriesOnly,
        stories_match_any: wantStories && storiesMatchAny,
        fetch_highlights: wantStories && fetchHighlights,
        fetch_live: wantStories && fetchLive,
        session_cookie: wantStories && sessionCookie.trim() ? sessionCookie.trim() : null,
        enabled: true,
        provider: "rapidapi",
        auto_track: autoTrack,
        stories_cron: storiesCron.trim() || null,
        posts_cron: postsCron.trim() || null,
        posts_lookback_limit: postsLookback ? Number(postsLookback) : null,
      };
      const { data } = await axios.post(`${API}/reports`, payload);
      toast.success(`Report "${data.report.name}" created`);
      onCreated(data.report);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not create report");
    } finally {
      setSubmitting(false);
    }
  };

  const applyGroup = (g) => {
    setHandlesText((g.handles || []).join("\n"));
    if ((g.keywords || []).length && !keywordsText.trim()) {
      setKeywordsText(g.keywords.join("\n"));
    }
    toast.success(`Loaded group "${g.name}"`);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className="bg-white border-slate-200 text-slate-900 max-w-3xl p-0 overflow-hidden"
        data-testid="report-wizard-dialog"
      >
        <div className="p-6 border-b border-slate-200">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl">
              <Sparkles size={18} className="text-pink-600" />
              New Report
            </DialogTitle>
            <DialogDescription className="text-slate-400 text-xs">
              Set your date, pick what to track, and choose the auto-refresh cadence.
            </DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-5 gap-3 mt-5">
            {STEP_TITLES.map((s) => (
              <StepBadge
                key={s.n}
                n={s.n}
                label={s.label}
                active={step === s.n}
                done={step > s.n}
              />
            ))}
          </div>
        </div>

        <div className="p-6 min-h-[320px]">
          {step === 1 && (
            <div className="space-y-4 fade-in-up">
              <label className="text-[10px] uppercase font-mono tracking-widest text-slate-400 flex items-center gap-1.5">
                <FileText size={11} /> Report Name
              </label>
              <Input
                data-testid="wizard-report-name"
                autoFocus
                value={name}
                onChange={(e) => setName(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && canProceed() && setStep(2)}
                placeholder="e.g., Q1 Fitness Creator Watch"
                className="bg-white border-slate-200 font-mono text-base h-11"
              />
              <p className="text-xs text-slate-400">
                A short label so you can spot this report in the list later. You can rename it any time.
              </p>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-5 fade-in-up" data-testid="wizard-mode-step">
              <div>
                <label className="text-[10px] uppercase font-mono tracking-widest text-slate-400">
                  How do you want to build this report?
                </label>
                <p className="text-[11px] text-slate-400 mt-1">
                  Either continuously track a set of handles, or one-shot pull data for specific post URLs you already have.
                </p>
              </div>
              <div className="grid md:grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => setMode("handles")}
                  data-testid="wizard-mode-handles"
                  className={`text-left rounded-xl border p-5 transition-all ${
                    mode === "handles"
                      ? "border-pink-300 bg-gradient-to-br from-pink-500/10 to-transparent shadow-[0_0_0_1px_rgba(245,158,11,0.15)]"
                      : "border-slate-200 bg-white hover:border-slate-300"
                  }`}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <div className="p-1.5 rounded-md bg-pink-50">
                      <Users size={13} className="text-pink-600" />
                    </div>
                    <div className="text-sm font-semibold text-slate-900">Track Handles</div>
                  </div>
                  <p className="text-[11px] text-slate-500 leading-snug">
                    Enter creator handles + keywords. We continuously scrape every new post/story that matches, on your schedule.
                  </p>
                  <p className="text-[10px] text-slate-400 mt-2 font-mono">
                    Best for ongoing monitoring
                  </p>
                </button>
                <button
                  type="button"
                  onClick={() => setMode("urls")}
                  data-testid="wizard-mode-urls"
                  className={`text-left rounded-xl border p-5 transition-all ${
                    mode === "urls"
                      ? "border-emerald-500/60 bg-gradient-to-br from-emerald-500/10 to-transparent shadow-[0_0_0_1px_rgba(16,185,129,0.15)]"
                      : "border-slate-200 bg-white hover:border-slate-300"
                  }`}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <div className="p-1.5 rounded-md bg-emerald-500/15">
                      <ExternalLink size={13} className="text-emerald-600" />
                    </div>
                    <div className="text-sm font-semibold text-slate-900">Fetch Specific URLs</div>
                  </div>
                  <p className="text-[11px] text-slate-500 leading-snug">
                    Paste up to 2,000 Instagram post/reel URLs. We pull them once — no handles, no stories, no schedule.
                  </p>
                  <p className="text-[10px] text-slate-400 mt-2 font-mono">
                    Best for one-off analysis
                  </p>
                </button>
              </div>
            </div>
          )}

          {/* URL branch — step 3: paste URLs */}
          {step === 3 && mode === "urls" && (
            <div className="space-y-3 fade-in-up" data-testid="wizard-urls-step">
              <label className="text-[10px] uppercase font-mono tracking-widest text-slate-400 flex items-center gap-1.5">
                <ExternalLink size={11} /> Instagram URLs
                <span className="text-emerald-600 ml-1">{urls.length}</span>
              </label>
              <Textarea
                data-testid="wizard-urls-input"
                autoFocus
                value={urlsText}
                onChange={(e) => setUrlsText(e.target.value)}
                placeholder={"https://www.instagram.com/p/…\nhttps://www.instagram.com/reel/…\n\nPaste as many as you like — one per line or comma-separated."}
                className="bg-white border-slate-200 font-mono text-xs h-56 resize-none"
              />
              <div className="grid grid-cols-4 gap-2 text-[10px] font-mono" data-testid="wizard-urls-stats">
                <div className="rounded-lg border border-slate-200 bg-white p-2.5">
                  <div className="text-slate-400 uppercase tracking-widest">Pasted</div>
                  <div className="text-slate-900 text-lg font-semibold mt-0.5">
                    {urlStats.totalPasted}
                  </div>
                </div>
                <div className="rounded-lg border border-emerald-500/25 bg-emerald-50 p-2.5">
                  <div className="text-emerald-600/80 uppercase tracking-widest">Will fetch</div>
                  <div className="text-emerald-600 text-lg font-semibold mt-0.5">
                    {urlStats.uniqueCount}
                  </div>
                </div>
                <div className={`rounded-lg border p-2.5 ${
                  urlStats.duplicateCount > 0
                    ? "border-pink-200 bg-pink-50"
                    : "border-slate-200 bg-white"
                }`}>
                  <div className={urlStats.duplicateCount > 0 ? "text-pink-600/80 uppercase tracking-widest" : "text-slate-400 uppercase tracking-widest"}>
                    Duplicates
                  </div>
                  <div className={`text-lg font-semibold mt-0.5 ${urlStats.duplicateCount > 0 ? "text-pink-600" : "text-slate-900"}`}>
                    {urlStats.duplicateCount}
                  </div>
                </div>
                <div className={`rounded-lg border p-2.5 ${
                  urlStats.invalidCount > 0
                    ? "border-red-200 bg-red-50"
                    : "border-slate-200 bg-white"
                }`}>
                  <div className={urlStats.invalidCount > 0 ? "text-red-600/80 uppercase tracking-widest" : "text-slate-400 uppercase tracking-widest"}>
                    Invalid
                  </div>
                  <div className={`text-lg font-semibold mt-0.5 ${urlStats.invalidCount > 0 ? "text-red-600" : "text-slate-900"}`}>
                    {urlStats.invalidCount}
                  </div>
                </div>
              </div>
              {urlStats.duplicateCount > 0 && (
                <div className="text-[10px] font-mono text-pink-600/90 leading-relaxed pl-1">
                  {urlStats.duplicateCount} duplicate link{urlStats.duplicateCount === 1 ? "" : "s"} auto-removed — we'll only fetch {urlStats.uniqueCount} unique post{urlStats.uniqueCount === 1 ? "" : "s"} (saves you {urlStats.duplicateCount} API call{urlStats.duplicateCount === 1 ? "" : "s"}).
                </div>
              )}
              {urls.length > 2000 && (
                <div className="text-[10px] font-mono text-pink-600 pl-1">
                  ⚠ Over 2,000 URLs — the fetch will still run but may take several minutes
                </div>
              )}
            </div>
          )}

          {/* URL branch — step 4: provider */}
          {step === 4 && mode === "urls" && (
            <div className="space-y-4 fade-in-up" data-testid="wizard-url-provider-step">
              <label className="text-[10px] uppercase font-mono tracking-widest text-slate-400">
                Choose provider
              </label>
              <div className="grid md:grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => setUrlProvider("apify")}
                  data-testid="wizard-url-provider-apify"
                  className={`text-left rounded-xl border p-4 transition-all ${
                    urlProvider === "apify"
                      ? "border-emerald-500/60 bg-gradient-to-br from-emerald-500/10 to-transparent"
                      : "border-slate-200 bg-white hover:border-slate-300"
                  }`}
                >
                  <div className="text-sm font-semibold text-slate-900">Apify · Data Slayer</div>
                  <div className="text-[11px] text-slate-500 mt-1 leading-snug">
                    Likes, comments, views <b className="text-emerald-600">+ Shares & Reposts</b>
                    <br /><span className="text-slate-400">Reels: both metrics · Static/Carousel: reposts only (IG hides shares on feed posts)</span>
                  </div>
                  <div className="text-[10px] text-slate-400 mt-2 font-mono">~$0.0015 / post · recommended</div>
                </button>
                <button
                  type="button"
                  onClick={() => setUrlProvider("rapidapi")}
                  data-testid="wizard-url-provider-rapidapi"
                  className={`text-left rounded-xl border p-4 transition-all ${
                    urlProvider === "rapidapi"
                      ? "border-pink-300 bg-gradient-to-br from-pink-500/10 to-transparent"
                      : "border-slate-200 bg-white hover:border-slate-300"
                  }`}
                >
                  <div className="text-sm font-semibold text-slate-900">RapidAPI</div>
                  <div className="text-[11px] text-slate-500 mt-1 leading-snug">
                    Likes, comments, views, plays. No share/repost counts.
                  </div>
                  <div className="text-[10px] text-slate-400 mt-2 font-mono">counts against your RapidAPI quota</div>
                </button>
              </div>
              <div className="bg-white border border-slate-200 rounded-lg p-3 text-[11px] text-slate-500 leading-relaxed">
                On <b>Create &amp; Fetch</b>, we&apos;ll create the report and immediately batch-download every URL — Stories are skipped. All {urls.length} post(s) will appear in this report's Excel exports the moment they finish.
              </div>
            </div>
          )}

          {/* Handles branch — step 3: keywords (was step 2) */}
          {step === 3 && mode === "handles" && (
            <div className="space-y-4 fade-in-up">
              <label className="text-[10px] uppercase font-mono tracking-widest text-slate-400 flex items-center gap-1.5">
                <Hash size={11} /> Track Terms — keywords, #hashtags, @mentions
                <span className="text-pink-600 ml-1">{keywords.length}</span>
              </label>
              <Textarea
                data-testid="wizard-keywords"
                autoFocus
                value={keywordsText}
                onChange={(e) => setKeywordsText(e.target.value)}
                placeholder={"#running\nairmax\n@nike\ncollab"}
                className="bg-white border-slate-200 font-mono h-40 resize-none"
              />
              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={() => setMatchMode("substring")}
                  className={`px-3 py-2 rounded-md text-xs font-medium transition-all ${
                    matchMode === "substring"
                      ? "bg-slate-900 text-white"
                      : "bg-white border border-slate-200 text-slate-500 hover:border-pink-300"
                  }`}
                  data-testid="wizard-match-substring"
                >
                  Substring match
                </button>
                <button
                  onClick={() => setMatchMode("exact")}
                  className={`px-3 py-2 rounded-md text-xs font-medium transition-all ${
                    matchMode === "exact"
                      ? "bg-slate-900 text-white"
                      : "bg-white border border-slate-200 text-slate-500 hover:border-pink-300"
                  }`}
                  data-testid="wizard-match-exact"
                >
                  Exact match
                </button>
              </div>
            </div>
          )}

          {step === 4 && mode === "handles" && (
            <div className="space-y-4 fade-in-up">
              {groups.length > 0 && (
                <div className="bg-gradient-to-br from-pink-500/10 to-pink-500/5 border border-pink-200 rounded-lg p-4 amber-glow">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <FolderOpen size={13} className="text-pink-600" />
                      <span className="text-[11px] font-mono uppercase tracking-widest text-pink-600 font-semibold">
                        Quick load — Saved Handle Groups
                      </span>
                      <Badge className="bg-pink-100 text-pink-600 border-pink-200 text-[9px]">
                        {groups.length}
                      </Badge>
                    </div>
                    <span className="text-[10px] font-mono text-pink-600/60">
                      one-click fill ↓
                    </span>
                  </div>
                  <div className="relative mb-2">
                    <Search
                      size={12}
                      className="absolute left-2.5 top-1/2 -translate-y-1/2 text-pink-600/60"
                    />
                    <Input
                      value={groupSearch}
                      onChange={(e) => setGroupSearch(e.target.value)}
                      placeholder="Search groups by name…"
                      className="bg-white border-pink-200 focus-visible:border-pink-300 pl-7 h-8 text-xs font-mono"
                      data-testid="wizard-group-search"
                    />
                  </div>
                  <div className="flex flex-wrap gap-1.5 max-h-32 overflow-y-auto">
                    {filteredGroups.length === 0 ? (
                      <div className="text-[11px] text-slate-400 font-mono py-2 w-full text-center">
                        No groups match "{groupSearch}"
                      </div>
                    ) : (
                      filteredGroups.map((g) => (
                        <button
                          key={g.id}
                          onClick={() => applyGroup(g)}
                          className="px-2.5 py-1.5 rounded-md bg-white border border-pink-200 hover:border-pink-300 hover:bg-pink-50 text-xs text-slate-800 hover:text-pink-600 transition-all font-medium"
                          data-testid={`wizard-group-${g.id.slice(0, 8)}`}
                        >
                          {g.name}{" "}
                          <span className="text-pink-600/60 font-mono text-[10px]">
                            ({g.handles.length})
                          </span>
                        </button>
                      ))
                    )}
                  </div>
                </div>
              )}

              <label className="text-[10px] uppercase font-mono tracking-widest text-slate-400 flex items-center gap-1.5">
                <Users size={11} /> Instagram Handles
                <span className="text-pink-600 ml-1">{handles.length}</span>
                {groups.length > 0 && (
                  <span className="text-slate-400 ml-1">· or type below</span>
                )}
              </label>
              <Textarea
                data-testid="wizard-handles"
                autoFocus={groups.length === 0}
                value={handlesText}
                onChange={(e) => setHandlesText(e.target.value)}
                placeholder={"@nike\n@adidas\ngymshark"}
                className="bg-white border-slate-200 font-mono h-40 resize-none"
              />
            </div>
          )}

          {step === 5 && mode === "handles" && (
            <div className="space-y-5 fade-in-up">
              {/* ── Historical anchor date ── */}
              <div className="bg-white border border-slate-200 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <CalendarClock size={14} className="text-pink-600" />
                  <div>
                    <div className="text-sm text-slate-800 font-medium">
                      Track posts from date
                    </div>
                    <div className="text-[11px] text-slate-400 font-mono">
                      The initial run pulls <b>every post</b> published on or after this date. Refreshes only scan the newest few.
                    </div>
                  </div>
                </div>
                <DateField
                  value={sinceDate}
                  onChange={setSinceDate}
                  testid="wizard-since-date"
                  placeholder="Pick start date"
                  className="h-10"
                  max={new Date().toISOString().slice(0, 10)}
                />
                <div className="mt-2 text-[10px] font-mono text-slate-400 leading-relaxed">
                  {sinceDate ? (
                    <>Fetching every post from <span className="text-pink-600">{sinceDate}</span> → today. No cap — pagination stops when it hits the date.</>
                  ) : (
                    <>Leave blank to fall back to the "posts lookback" number on Step 5.</>
                  )}
                </div>
                <div className="mt-2 rounded-md bg-emerald-50 border border-emerald-200 p-2 text-[10px] text-emerald-600/90 leading-relaxed" data-testid="wizard-batching-note">
                  ⚙ Safe batching is on: pages are paginated with a 2s gap, all
                  RapidAPI calls share a global concurrency cap of 3, and 429s
                  auto-backoff for 15/30/45s. The date is the true stop — the
                  fetcher walks up to 5,000 pages (~60k posts) if needed. Even
                  3,000-5,000 posts run cleanly in the background.
                </div>
              </div>

              {/* ── What to track ── */}
              <div className="bg-white border border-slate-200 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Radio size={14} className="text-pink-600" />
                  <div>
                    <div className="text-sm text-slate-800 font-medium">
                      What should this report track?
                    </div>
                    <div className="text-[11px] text-slate-400 font-mono">
                      Pick one — you can always change it later.
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-2" role="radiogroup" aria-label="tracker-mode">
                  {[
                    {v: "posts",   label: "Posts only",   sub: "Reels · Carousels · Static", icon: <Radio size={13} />, testId: "tracker-mode-posts"},
                    {v: "stories", label: "Stories only", sub: "Highlights · Live 24h",     icon: <Zap size={13} />,   testId: "tracker-mode-stories"},
                    {v: "both",    label: "Both",         sub: "Posts + Stories",           icon: <Sparkles size={13} />, testId: "tracker-mode-both"},
                  ].map(opt => {
                    const isActive = trackerMode === opt.v;
                    return (
                      <button
                        key={opt.v}
                        type="button"
                        role="radio"
                        aria-checked={isActive}
                        onClick={() => setTrackerMode(opt.v)}
                        data-testid={opt.testId}
                        className={`text-left p-3 rounded-lg border transition-all ${
                          isActive
                            ? "bg-pink-50 border-pink-300 ring-1 ring-pink-300"
                            : "bg-[#F8FAFC] border-slate-200 hover:border-slate-300"
                        }`}
                      >
                        <div className={`flex items-center gap-1.5 text-[12px] font-medium ${isActive ? "text-pink-600" : "text-slate-700"}`}>
                          {opt.icon}
                          {opt.label}
                        </div>
                        <div className="text-[10px] text-slate-400 font-mono mt-0.5">{opt.sub}</div>
                      </button>
                    );
                  })}
                </div>
                {trackerMode !== "posts" && (
                  <div className="mt-3 border-t border-slate-200 pt-3 space-y-2">
                    <div className="grid grid-cols-2 gap-3 pb-2 border-b border-slate-200">
                      <div className="flex items-start justify-between gap-2 p-2 rounded-md bg-white border border-slate-200">
                        <div>
                          <div className="text-[11px] text-slate-800 font-medium flex items-center gap-1.5">
                            <Bookmark size={11} className="text-pink-600" />
                            Highlights
                          </div>
                          <div className="text-[9px] text-slate-400 font-mono leading-relaxed">Public · no cookie</div>
                        </div>
                        <Switch
                          checked={fetchHighlights}
                          onCheckedChange={setFetchHighlights}
                          data-testid="wizard-fetch-highlights"
                        />
                      </div>
                      <div className="flex items-start justify-between gap-2 p-2 rounded-md bg-white border border-slate-200">
                        <div>
                          <div className="text-[11px] text-slate-800 font-medium flex items-center gap-1.5">
                            <Radio size={11} className="text-pink-600" />
                            Live 24h
                          </div>
                          <div className="text-[9px] text-slate-400 font-mono leading-relaxed">Needs IG cookie</div>
                        </div>
                        <Switch
                          checked={fetchLive}
                          onCheckedChange={setFetchLive}
                          data-testid="wizard-fetch-live"
                        />
                      </div>
                    </div>
                    <div className="flex items-start justify-between gap-3 pb-2 border-b border-slate-200">
                      <div>
                        <div className="text-[12px] text-slate-800 font-medium">
                          Fetch ALL stories (ignore keyword filter)
                        </div>
                        <div className="text-[10px] text-slate-400 font-mono leading-relaxed">
                          Great for testing a profile — every highlight is kept, even if it doesn&apos;t mention your keywords. Posts still keyword-filter.
                        </div>
                      </div>
                      <Switch
                        checked={storiesMatchAny}
                        onCheckedChange={setStoriesMatchAny}
                        data-testid="wizard-stories-match-any"
                      />
                    </div>
                    <label className="text-[10px] uppercase font-mono tracking-widest text-slate-400 flex items-center gap-1.5">
                      <Info size={10} /> Instagram sessionid (required for LIVE 24h stories — any logged-in account works, ideally a burner)
                    </label>
                    <div className="relative">
                      <Input
                        type={showCookie ? "text" : "password"}
                        value={sessionCookie}
                        onChange={(e) => setSessionCookie(e.target.value)}
                        placeholder="Leave blank to only fetch highlights"
                        className="bg-white border-slate-200 font-mono text-xs pr-10"
                        data-testid="wizard-session-cookie"
                      />
                      <button
                        onClick={() => setShowCookie((v) => !v)}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
                        type="button"
                      >
                        {showCookie ? <EyeOff size={13} /> : <Eye size={13} />}
                      </button>
                    </div>
                    <p className="text-[10px] text-pink-600/80 leading-relaxed">
                      Highlights work without a cookie. <b>Live 24h stories always require a session cookie</b>,
                      even for public accounts — Instagram&apos;s stories API is auth-only. Use a
                      <b> burner IG account&apos;s cookie</b>: paste ANY logged-in account&apos;s
                      <code className="font-mono text-pink-600"> sessionid</code>, not the target
                      creator&apos;s.
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {step === 6 && mode === "handles" && (
            <div className="space-y-5 fade-in-up">
              {/* ── Schedule & Refresh — user picks BEFORE report creation ── */}
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 space-y-3" data-testid="wizard-schedule-section">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <CalendarClock size={14} className="text-emerald-600" />
                    <div>
                      <div className="text-sm text-slate-800 font-medium">Auto-refresh Schedule</div>
                      <div className="text-[11px] text-slate-400 font-mono">
                        Refreshes only scan the newest N posts per handle — the initial historical fetch is one-off.
                      </div>
                    </div>
                  </div>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={autoTrack}
                      onChange={(e) => setAutoTrack(e.target.checked)}
                      className="accent-emerald-500"
                      data-testid="wizard-auto-track"
                    />
                    <span className="text-[11px] text-slate-700">Auto-track</span>
                  </label>
                </div>

                {autoTrack ? (
                  <>
                    <div>
                      <label className="text-[10px] uppercase text-slate-400 tracking-wider">
                        Stories cadence (fast, small credit cost)
                      </label>
                      <select
                        className="mt-1 w-full bg-[#F8FAFC] border border-slate-200 rounded-md h-9 px-2 text-xs text-slate-900"
                        value={storiesCron}
                        onChange={(e) => setStoriesCron(e.target.value)}
                        data-testid="wizard-stories-cron"
                      >
                        <option value="">Off (manual only)</option>
                        <option value="0 10 * * *">Every day, 10:00 IST</option>
                        <option value="0 8 * * *">Every day, 08:00 IST</option>
                        <option value="0 */6 * * *">Every 6 hours</option>
                      </select>
                    </div>

                    <div>
                      <label className="text-[10px] uppercase text-slate-400 tracking-wider">
                        Posts cadence (heavier, bigger credit cost)
                      </label>
                      <select
                        className="mt-1 w-full bg-[#F8FAFC] border border-slate-200 rounded-md h-9 px-2 text-xs text-slate-900"
                        value={postsCron}
                        onChange={(e) => setPostsCron(e.target.value)}
                        data-testid="wizard-posts-cron"
                      >
                        <option value="">Off (manual only)</option>
                        <option value="0 10 * * *">Every day, 10:00 IST</option>
                        <option value="0 10 * * 4">Every Thursday, 10:00 IST</option>
                        <option value="0 10 * * 1">Every Monday, 10:00 IST</option>
                        <option value="0 10 * * 0">Every Sunday, 10:00 IST</option>
                        <option value="0 10 1 * *">1st of month, 10:00 IST</option>
                        <option value="0 10 15 * *">15th of month, 10:00 IST</option>
                      </select>
                    </div>

                    <div>
                      <label className="text-[10px] uppercase text-slate-400 tracking-wider">
                        Refresh lookback depth (last N posts per handle)
                      </label>
                      <input
                        type="number"
                        min="1"
                        max="500"
                        value={postsLookback}
                        onChange={(e) => setPostsLookback(e.target.value)}
                        placeholder="e.g. 24"
                        className="mt-1 w-32 bg-[#F8FAFC] border border-slate-200 rounded-md h-9 px-2 text-xs text-slate-900"
                        data-testid="wizard-posts-lookback"
                      />
                      <span className="ml-2 text-[10px] text-slate-400">
                        No default — set what suits this report.
                      </span>
                    </div>

                    <div className="text-[10px] text-slate-500 leading-relaxed">
                      💡 The shared handle-cache automatically dedupes RapidAPI calls
                      across all your reports touching the same handle — you'll save
                      credits without any extra config.
                    </div>
                  </>
                ) : (
                  <div className="text-[11px] text-slate-500 leading-relaxed">
                    Auto-track is <b>OFF</b> — nothing fires automatically. Use the
                    <b> Run Now</b> button on the report page for manual refreshes.
                    You can enable auto-track anytime from the report&apos;s
                    <b> Schedule</b> button.
                  </div>
                )}
              </div>

              <div className="bg-white border border-slate-200 rounded-lg p-3 text-[11px] text-slate-500 leading-relaxed">
                On <b>Create &amp; Run</b>, we&apos;ll immediately kick off the initial historical fetch{sinceDate ? <> from <span className="text-pink-600 font-mono">{sinceDate}</span></> : " (using the lookback number above)"} — subsequent runs auto-switch to refresh mode.
              </div>
            </div>
          )}
        </div>

        <div className="p-4 border-t border-slate-200 flex items-center justify-between bg-white">
          <div className="text-[10px] font-mono text-slate-400">
            {step} of {STEP_TITLES.length}
          </div>
          <div className="flex items-center gap-2">
            {step > 1 && (
              <Button
                variant="outline"
                onClick={() => setStep(step - 1)}
                className="bg-white border-slate-200 hover:bg-slate-50 text-slate-700"
                data-testid="wizard-back"
              >
                <ArrowLeft size={13} className="mr-1.5" />
                Back
              </Button>
            )}
            {step < STEP_TITLES.length ? (
              <Button
                onClick={() => canProceed() && setStep(step + 1)}
                disabled={!canProceed()}
                className="bg-slate-900 hover:bg-slate-800 text-white font-semibold disabled:opacity-40"
                data-testid="wizard-next"
              >
                Next
                <ArrowRight size={13} className="ml-1.5" />
              </Button>
            ) : (
              <Button
                onClick={submit}
                disabled={
                  submitting ||
                  !name.trim() ||
                  (mode === "urls"
                    ? (urls.length === 0 || !urlProvider)
                    : (!handles.length || !keywords.length))
                }
                className="bg-slate-900 hover:bg-slate-800 text-white font-semibold disabled:opacity-40 amber-glow"
                data-testid="wizard-submit"
              >
                {submitting ? (
                  <>
                    <Loader2 size={13} className="mr-1.5 animate-spin" />
                    Creating…
                  </>
                ) : mode === "urls" ? (
                  <>
                    <Download size={13} className="mr-1.5" />
                    Create &amp; Fetch
                  </>
                ) : (
                  <>
                    <Zap size={13} className="mr-1.5" />
                    Create &amp; Run
                  </>
                )}
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Renders the batch card for the report's latest job AND, if a different
// older batch is still resumable (cancelled/failed with unstored links),
// surfaces that card too so the Resume button never disappears.
function UrlBatchSection({ report }) {
  const [resumableId, setResumableId] = useState(null);
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/reports/${report.id}/url-batches/resumable`);
        if (alive) setResumableId(data.job_id || null);
      } catch { /* silent */ }
    })();
    return () => { alive = false; };
  }, [report.id, report.last_job_id]);
  const showResumable = resumableId && resumableId !== report.last_job_id;
  if (!report.last_job_id && !showResumable) return null;
  return (
    <div className="space-y-3" data-testid="url-batch-section">
      {report.last_job_id && (
        <UrlBatchProgress key={`prog-${report.last_job_id}`} jobId={report.last_job_id} />
      )}
      {showResumable && (
        <UrlBatchProgress key={`prog-${resumableId}`} jobId={resumableId} />
      )}
    </div>
  );
}


function UrlBatchProgress({ jobId }) {
  const [job, setJob] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [resumeJobId, setResumeJobId] = useState(null);
  const [resuming, setResuming] = useState(false);
  const activeJobId = resumeJobId || jobId;

  const resumeBatch = async () => {
    if (!job?.id) return;
    setResuming(true);
    try {
      const { data } = await axios.post(`${API}/scrape/job/${job.id}/resume`);
      if (data.job_id) {
        const n = data.url_count ?? data.remaining ?? 0;
        toast.success(
          `Resumed — ${n} link(s) to go` +
          (data.skipped_already_stored ? ` (${data.skipped_already_stored} already stored, skipped free)` : "")
        );
        setJob(null);
        setResumeJobId(data.job_id);
      } else {
        toast.info(data.message || "Nothing to resume");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Resume failed");
    } finally {
      setResuming(false);
    }
  };

  const [retrying, setRetrying] = useState(false);
  const retryFailed = async () => {
    if (!job?.id || !job?.report_id) return;
    setRetrying(true);
    try {
      const { data } = await axios.post(
        `${API}/reports/${job.report_id}/refetch-post-types`,
        { provider: job.provider || "rapidapi", retry_job_id: job.id },
      );
      if (data.job_id) {
        toast.success(`Re-fetching ${data.total} link(s) that failed last time`);
        setJob(null);
        setResumeJobId(data.job_id);
      } else {
        toast.info(data.message || "Nothing left to retry");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Retry failed");
    } finally {
      setRetrying(false);
    }
  };

  const [cancelling, setCancelling] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const cancelBatch = async () => {
    if (!job?.id) return;
    if (!window.confirm("Cancel this URL batch? You can Resume it afterwards — already-saved links won't be re-fetched.")) return;
    setCancelling(true);
    try {
      await axios.post(`${API}/scrape/job/${job.id}/cancel`);
      toast.success("Batch cancelled — Resume is now available.");
      // trigger an immediate re-poll by resetting local state
      setJob((j) => (j ? { ...j, status: "cancelled" } : j));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Cancel failed");
    } finally {
      setCancelling(false);
    }
  };

  useEffect(() => {
    if (!activeJobId) { setJob(null); return; }
    let alive = true;
    let timer;
    const poll = async () => {
      try {
        const { data } = await axios.get(`${API}/scrape/job/${activeJobId}`);
        if (!alive) return;
        setJob(data);
        // Stop polling as soon as the job settles
        if (data.status === "running" || data.status === "queued") {
          timer = setTimeout(poll, 2000);
        }
      } catch { /* silent */ }
    };
    poll();
    return () => { alive = false; if (timer) clearTimeout(timer); };
  }, [activeJobId]);

  useEffect(() => { setResumeJobId(null); }, [jobId]);

  if (!job) return null;
  // Only show for URL-mode jobs
  if (!job.url_progress && (job.run_kind !== "url-batch")) return null;

  const prog = job.url_progress || {};
  const requested = Math.max(1, Number(prog.requested || 0));
  const fetched = Number(prog.fetched || 0);
  const failed = Number(prog.failed || 0);
  const done = fetched + failed;
  const pct = Math.min(100, Math.round((done / requested) * 100));
  const isRunning = job.status === "queued" || job.status === "running";
  const ledger = job.url_report || [];
  // Completed batches go away on their own — the data is in the report.
  // Exception: a completed re-fetch batch with failures stays so the
  // "Retry failed" button remains reachable.
  const isRetype = job.triggered_by === "refetch-post-types";
  if (job.status === "completed" && (failed === 0 || !isRetype)) return null;
  if (dismissed) return null;
  const dupCount = ledger.filter((r) => r.status === "duplicate").length;
  const invalidCount = ledger.filter((r) => r.status === "invalid").length;
  const missCount = ledger.filter((r) => r.status === "not_found" || r.status === "error").length;

  const downloadReport = async () => {
    setDownloading(true);
    try {
      const res = await axios.get(`${API}/scrape/job/${job.id}/url-report`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `url-report_${job.id.slice(0, 8)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("URL report downloaded");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Report download failed");
    } finally { setDownloading(false); }
  };

  const phaseLabel = {
    queued: "Queued",
    fetching: "Fetching posts",
    "post-processing": "Post-processing",
    persisting: "Saving to report",
    completed: "Completed",
    failed: "Failed",
  }[prog.phase || (isRunning ? "fetching" : job.status)] || "";

  return (
    <div
      className={`rounded-xl border p-5 ${
        isRunning
          ? "border-emerald-200 bg-gradient-to-br from-emerald-500/5 to-transparent"
          : job.status === "failed"
          ? "border-red-200 bg-red-50"
          : "border-slate-200 bg-white"
      }`}
      data-testid="url-batch-progress"
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className={`p-1.5 rounded-md ${isRunning ? "bg-emerald-500/15" : "bg-white"}`}>
            {isRunning ? (
              <Loader2 size={13} className="text-emerald-600 animate-spin" />
            ) : (
              <Check size={13} className="text-emerald-600" />
            )}
          </div>
          <div>
            <div className="text-xs font-semibold text-slate-900">URL Batch Fetch</div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-slate-400">
              {phaseLabel} · {job.provider || "apify"}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {["queued", "running"].includes(job.status) && (
            <Button
              onClick={cancelBatch}
              disabled={cancelling}
              variant="outline"
              className="bg-white border-red-200 text-red-600 hover:bg-red-50 hover:border-red-300 hover:text-red-700 text-xs h-8"
              data-testid="url-batch-cancel-btn"
              title="Force-cancel this batch. If it's stuck (e.g. after a redeploy) this frees the Resume button."
            >
              {cancelling ? (
                <><Loader2 size={11} className="mr-1.5 animate-spin" /> Cancelling…</>
              ) : (
                <><XIcon size={11} className="mr-1.5" /> Cancel</>
              )}
            </Button>
          )}
          {["cancelled", "error", "failed"].includes(job.status) && ledger.length > 0 && (
            <Button
              onClick={resumeBatch}
              disabled={resuming}
              className="bg-indigo-600 hover:bg-indigo-700 text-white text-xs h-8"
              data-testid="url-batch-resume-btn"
              title="Re-run only the links that never made it into the report — already-saved links are skipped free"
            >
              {resuming ? (
                <><Loader2 size={11} className="mr-1.5 animate-spin" /> Resuming…</>
              ) : (
                <><RefreshCw size={11} className="mr-1.5" /> Resume fetch</>
              )}
            </Button>
          )}
          {job.triggered_by === "refetch-post-types" && !isRunning && failed > 0 && (
            <Button
              onClick={retryFailed}
              disabled={retrying}
              className="bg-indigo-600 hover:bg-indigo-700 text-white text-xs h-8"
              data-testid="url-batch-retry-failed-btn"
              title="Re-fetch ONLY the links that failed in this batch — repaired links are not re-billed"
            >
              {retrying ? (
                <><Loader2 size={11} className="mr-1.5 animate-spin" /> Retrying…</>
              ) : (
                <><RefreshCw size={11} className="mr-1.5" /> Retry failed ({failed})</>
              )}
            </Button>
          )}
          {!isRunning && (
            <button
              onClick={() => setDismissed(true)}
              className="text-slate-300 hover:text-slate-600 transition-colors p-1"
              data-testid="url-batch-dismiss-btn"
              title="Hide this banner"
            >
              <XIcon size={13} />
            </button>
          )}
          {ledger.length > 0 && (
            <Button
              onClick={downloadReport}
              disabled={downloading}
              variant="outline"
              className="bg-white border-slate-200 hover:bg-slate-100 hover:border-slate-300 hover:text-slate-900 text-slate-700 text-xs h-8"
              data-testid="url-batch-download-report"
              title="Download a CSV listing every URL with fetched/duplicate/failed reason"
            >
              {downloading ? (
                <><Loader2 size={11} className="mr-1.5 animate-spin" /> …</>
              ) : (
                <><Download size={11} className="mr-1.5" /> URL Report (CSV)</>
              )}
            </Button>
          )}
        </div>
      </div>
      <div className="flex items-center justify-between text-[11px] font-mono mb-1.5">
        <span className="text-slate-700">
          <span className="text-emerald-600 font-semibold">{done}</span>
          <span className="text-slate-400"> / </span>
          <span className="text-slate-800">{requested}</span>
          <span className="text-slate-400"> fetched</span>
        </span>
        <span className="text-[10px] text-slate-400">{pct}%</span>
      </div>
      <div className="h-1.5 rounded-full bg-white overflow-hidden">
        <div
          className={`h-full rounded-full transition-all duration-500 ${
            job.status === "failed" ? "bg-red-500" : "bg-emerald-500"
          }`}
          style={{ width: `${Math.max(2, pct)}%` }}
        />
      </div>
      <div className="mt-3 grid grid-cols-4 gap-2 text-[10px] font-mono">
        <div className="rounded-md border border-emerald-500/25 bg-emerald-50 p-2">
          <div className="text-emerald-600/80 uppercase tracking-widest">Fetched</div>
          <div className="text-emerald-600 text-sm font-semibold mt-0.5">{fetched}</div>
        </div>
        <div className={`rounded-md border p-2 ${dupCount > 0 ? "border-pink-200 bg-pink-50" : "border-slate-200 bg-white"}`}>
          <div className={`uppercase tracking-widest ${dupCount > 0 ? "text-pink-600/80" : "text-slate-400"}`}>Duplicates</div>
          <div className={`text-sm font-semibold mt-0.5 ${dupCount > 0 ? "text-pink-600" : "text-slate-900"}`}>{dupCount}</div>
        </div>
        <div className={`rounded-md border p-2 ${invalidCount > 0 ? "border-red-500/25 bg-red-50" : "border-slate-200 bg-white"}`}>
          <div className={`uppercase tracking-widest ${invalidCount > 0 ? "text-red-600/80" : "text-slate-400"}`}>Invalid</div>
          <div className={`text-sm font-semibold mt-0.5 ${invalidCount > 0 ? "text-red-600" : "text-slate-900"}`}>{invalidCount}</div>
        </div>
        <div className={`rounded-md border p-2 ${missCount > 0 ? "border-red-500/25 bg-red-50" : "border-slate-200 bg-white"}`}>
          <div className={`uppercase tracking-widest ${missCount > 0 ? "text-red-600/80" : "text-slate-400"}`}>Missing</div>
          <div className={`text-sm font-semibold mt-0.5 ${missCount > 0 ? "text-red-600" : "text-slate-900"}`}>{missCount}</div>
        </div>
      </div>
      {!isRunning && (dupCount + invalidCount + missCount > 0) && (
        <div className="mt-3 text-[11px] text-slate-500 leading-relaxed">
          {dupCount > 0 && <>· <b className="text-pink-600">{dupCount}</b> duplicate link{dupCount === 1 ? "" : "s"}<br /></>}
          {invalidCount > 0 && <>· <b className="text-red-600">{invalidCount}</b> not an Instagram post/reel URL<br /></>}
          {missCount > 0 && <>· <b className="text-red-600">{missCount}</b> couldn't be fetched — likely deleted, private, or region-blocked<br /></>}
          <span className="text-slate-400">Download the CSV to see every link with its exact reason.</span>
        </div>
      )}
    </div>
  );
}

function LatestRunSummary({ reportId, jobId, report }) {
  const [job, setJob] = useState(null);
  const [data, setData] = useState(null);
  const [filterPreset, setFilterPreset] = useState("all");
  const [collabFilter, setCollabFilter] = useState("all"); // all | owned | collab
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [subTab, setSubTab] = useState("stats");
  const [drawerCreator, setDrawerCreator] = useState(null);
  // Custom stories — manually-entered per-creator story counts. Persisted on
  // the report doc; folded into Total/Matched only when includeCustom is ON.
  const [customStories, setCustomStories] = useState(report?.custom_stories || {});
  const [includeCustom, setIncludeCustom] = useState(!!report?.include_custom_stories);
  const [historicTotal, setHistoricTotal] = useState(Number(report?.historic_stories_total || 0));
  // Keep local state in sync when the parent-refetched report doc arrives
  // (fixes: manually typed historic total resets on refresh / tab switch).
  useEffect(() => {
    setCustomStories(report?.custom_stories || {});
    setIncludeCustom(!!report?.include_custom_stories);
    setHistoricTotal(Number(report?.historic_stories_total || 0));
  }, [report?.custom_stories, report?.include_custom_stories, report?.historic_stories_total]);
  const [contentPage, setContentPage] = useState(0);
  const [creatorPage, setCreatorPage] = useState(0);
  const pollingRef = useRef(null);
  const fetchSeqRef = useRef(0);

  const range = useMemo(() => {
    const today = new Date();
    const iso = (d) => d.toISOString().slice(0, 10);
    if (filterPreset === "all") return { from: "", to: "" };
    if (filterPreset === "custom") return { from: fromDate, to: toDate };
    if (filterPreset === "thisMonth") {
      const start = new Date(today.getFullYear(), today.getMonth(), 1);
      return { from: iso(start), to: iso(today) };
    }
    if (filterPreset === "lastMonth") {
      const start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      const end = new Date(today.getFullYear(), today.getMonth(), 0);
      return { from: iso(start), to: iso(end) };
    }
    const days = filterPreset === "7d" ? 7 : filterPreset === "30d" ? 30 : 90;
    const start = new Date(today);
    start.setDate(start.getDate() - days);
    return { from: iso(start), to: iso(today) };
  }, [filterPreset, fromDate, toDate]);

  const [loadingData, setLoadingData] = useState(false);
  const [customizeOpen, setCustomizeOpen] = useState(false);
  const [statVis, setStatVis] = useState(() => {
    try { return JSON.parse(localStorage.getItem("cx-stat-vis") || "{}"); } catch { return {}; }
  });
  const toggleStat = (key, v) => {
    setStatVis((prev) => {
      const next = { ...prev, [key]: v };
      try { localStorage.setItem("cx-stat-vis", JSON.stringify(next)); } catch { /* ignore */ }
      return next;
    });
  };
  const fetchData = useCallback(async () => {
    if (!reportId) return;
    const seq = ++fetchSeqRef.current;
    setLoadingData(true);
    try {
      const params = new URLSearchParams();
      if (range.from) params.set("from_date", range.from);
      if (range.to) params.set("to_date", range.to);
      const { data } = await axios.get(
        `${API}/reports/${reportId}/data?${params.toString()}`
      );
      if (seq === fetchSeqRef.current) setData(data);
    } catch (e) {
      if (process.env.NODE_ENV !== "production") console.error("Failed to fetch report data:", e);
    } finally {
      if (seq === fetchSeqRef.current) setLoadingData(false);
    }
  }, [reportId, range.from, range.to]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Ref so the 4s job poller always calls the latest fetchData without
  // needing it as an effect dep (which restarted polling on every filter
  // change and caused extra fetches + flicker).
  const fetchDataRef = useRef(fetchData);
  useEffect(() => { fetchDataRef.current = fetchData; }, [fetchData]);

  // Persist custom-stories edits (per-creator count inputs + include toggle)
  // via the report update endpoint, then re-pull data so matched_total /
  // the Total card reflect the server-computed values.
  const saveCustomStories = useCallback(async (nextMap, nextInclude) => {
    try {
      await axios.put(`${API}/reports/${reportId}`, {
        custom_stories: nextMap,
        include_custom_stories: nextInclude,
      });
      setCustomStories(nextMap);
      setIncludeCustom(nextInclude);
      toast.success("Historic stories saved");
      // Refetch fresh report doc so local state doesn't reset on remount.
      try {
        const { data: fresh } = await axios.get(`${API}/reports/${reportId}`);
        if (fresh?.report) {
          setCustomStories(fresh.report.custom_stories || {});
          setIncludeCustom(!!fresh.report.include_custom_stories);
          setHistoricTotal(Number(fresh.report.historic_stories_total || 0));
        }
      } catch { /* non-fatal */ }
      fetchDataRef.current();
    } catch (e) {
      toast.error("Could not save historic stories");
    }
  }, [reportId]);

  // Report-level manual historic story total (the editable dashboard box).
  const saveHistoricTotal = useCallback(async (n) => {
    try {
      await axios.put(`${API}/reports/${reportId}`, { historic_stories_total: n });
      setHistoricTotal(n);
      toast.success("Historic stories total saved");
      // Refetch fresh report doc so subsequent remounts don't reset the value.
      try {
        const { data: fresh } = await axios.get(`${API}/reports/${reportId}`);
        if (fresh?.report) {
          setHistoricTotal(Number(fresh.report.historic_stories_total || 0));
          setIncludeCustom(!!fresh.report.include_custom_stories);
        }
      } catch { /* non-fatal */ }
    } catch (e) {
      toast.error("Could not save historic total");
    }
  }, [reportId]);

  useEffect(() => {
    if (!jobId) return;
    const fetchJob = async () => {
      try {
        const { data } = await axios.get(`${API}/scrape/job/${jobId}`);
        setJob(data);
        if (data.status === "completed" || data.status === "failed") {
          clearInterval(pollingRef.current);
          pollingRef.current = null;
          fetchDataRef.current();
        }
      } catch (e) {
        if (process.env.NODE_ENV !== "production") console.error("Failed to poll job status:", e);
      }
    };
    fetchJob();
    pollingRef.current = setInterval(fetchJob, 4000);
    return () => pollingRef.current && clearInterval(pollingRef.current);
  }, [jobId]);

  const _downloadBlob = async (url, filename, kind) => {
    const t = toast.loading(`Preparing ${kind}…`);
    try {
      const res = await axios.get(url, { responseType: "blob" });
      const blob = new Blob([res.data], {
        type: filename.endsWith(".csv")
          ? "text/csv"
          : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const objUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objUrl;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(objUrl);
      toast.success(`${kind} downloaded`, { id: t });
    } catch (e) {
      // Axios blob errors come back as a blob too — parse for a real message
      let detail = "Export failed";
      if (e.response?.data instanceof Blob) {
        try { detail = JSON.parse(await e.response.data.text()).detail || detail; } catch {}
      } else {
        detail = e.response?.data?.detail || e.message || detail;
      }
      toast.error(detail, { id: t });
    }
  };

  const exportFiltered = () => {
    const params = new URLSearchParams();
    if (range.from) params.set("from_date", range.from);
    if (range.to) params.set("to_date", range.to);
    return _downloadBlob(
      `${API}/reports/${reportId}/export?${params.toString()}`,
      `report_${reportId.slice(0, 8)}_full.xlsx`,
      "Full Excel"
    );
  };

  const exportCanonSummary = () => {
    const params = new URLSearchParams();
    if (range.from) params.set("from_date", range.from);
    if (range.to) params.set("to_date", range.to);
    return _downloadBlob(
      `${API}/reports/${reportId}/export/summary?${params.toString()}`,
      `report_${reportId.slice(0, 8)}_summary.xlsx`,
      "Summary Excel"
    );
  };

  const [backfilling, setBackfilling] = useState(false);
  const backfillMetrics = async () => {
    if (backfilling) return;
    setBackfilling(true);
    const t = toast.loading("Refreshing follower & view counts…");
    try {
      const r = await axios.post(`${API}/reports/${reportId}/backfill-metrics`);
      const d = r.data || {};
      toast.success(
        `Refreshed: ${d.profiles_updated || 0} profile(s) + ${
          d.posts_updated || 0
        } post view count(s)`,
        { id: t }
      );
      // Force reload the report data so the summary reflects the update
      const dr = await axios.get(`${API}/reports/${reportId}/data`, {
        params: {
          ...(range.from ? { from_date: range.from } : {}),
          ...(range.to ? { to_date: range.to } : {}),
        },
      });
      setData(dr.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Backfill failed", { id: t });
    } finally {
      setBackfilling(false);
    }
  };

  const [syncingHistory, setSyncingHistory] = useState(false);
  const syncStoryHistory = async () => {
    if (syncingHistory) return;
    setSyncingHistory(true);
    const t = toast.loading("Syncing story history from other workspace reports…");
    try {
      const r = await axios.post(`${API}/reports/${reportId}/sync-story-history`);
      const d = r.data || {};
      if (d.imported > 0) {
        toast.success(d.message, { id: t });
      } else {
        toast.info(d.message || "No new history rows found to import.", { id: t });
      }
      // Reload report data so the summary reflects imported history
      const dr = await axios.get(`${API}/reports/${reportId}/data`, {
        params: {
          ...(range.from ? { from_date: range.from } : {}),
          ...(range.to ? { to_date: range.to } : {}),
        },
      });
      setData(dr.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "History sync failed", { id: t });
    } finally {
      setSyncingHistory(false);
    }
  };

  const isRunning = job && (job.status === "running" || job.status === "queued");
  const [reclassifying, setReclassifying] = useState(false);
  const [fixTypesOpen, setFixTypesOpen] = useState(false);
  const [retypeProvider, setRetypeProvider] = useState("rapidapi");
  const [retypeBusy, setRetypeBusy] = useState(false);
  const [autoFixBusy, setAutoFixBusy] = useState(false);
  const [autoFixResult, setAutoFixResult] = useState(null);
  const [kwAuditBusy, setKwAuditBusy] = useState(false);
  const [kwAudit, setKwAudit] = useState(null);
  const runKeywordAudit = async () => {
    if (!reportId) return;
    setKwAuditBusy(true);
    setKwAudit(null);
    const t = toast.loading("Checking every stored post against current keywords…");
    try {
      const { data } = await axios.get(`${API}/reports/${reportId}/keyword-audit?limit=500`);
      setKwAudit(data);
      toast.success(
        data.applicable === false
          ? "No keywords on this report — audit not applicable"
          : `${data.non_matching} of ${data.unique_posts} posts don't match current keywords`,
        { id: t, duration: 6000 }
      );
    } catch (e) {
      toast.error(e.response?.data?.detail || "Keyword audit failed", { id: t });
    } finally {
      setKwAuditBusy(false);
    }
  };
  const autoFixAttribution = async () => {
    if (!reportId) return;
    setAutoFixBusy(true);
    setAutoFixResult(null);
    const t = toast.loading("Auto-fixing attribution (exact + fuzzy) + dedupe…");
    try {
      const { data } = await axios.post(
        `${API}/reports/${reportId}/auto-fix-attribution?enable_fuzzy=true`
      );
      setAutoFixResult(data);
      const r = data.repair || {};
      const d = data.dedupe || {};
      toast.success(
        `Reattributed ${r.reattributed || 0} · fuzzy ${r.fuzzy || 0} · dupes removed ${d.deleted || 0}`,
        { id: t, duration: 6000 }
      );
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Auto-fix failed", { id: t });
    } finally {
      setAutoFixBusy(false);
    }
  };
  const reclassifyPosts = async () => {
    if (!reportId) return;
    setReclassifying(true);
    const t = toast.loading("Re-evaluating post types & creator names…");
    try {
      const { data } = await axios.post(`${API}/reports/${reportId}/reclassify-posts`);
      toast.success(
        data.posts_updated
          ? `Fixed ${data.posts_updated} post(s) (Carousels & creator names updated)`
          : "All post types and names are already up to date",
        { id: t }
      );
      setFixTypesOpen(false);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Reclassification failed", { id: t });
    } finally {
      setReclassifying(false);
    }
  };
  const refetchPostTypes = async () => {
    if (!reportId) return;
    setRetypeBusy(true);
    const t = toast.loading("Queueing post-type re-fetch…");
    try {
      const { data } = await axios.post(
        `${API}/reports/${reportId}/refetch-post-types`,
        { provider: retypeProvider }
      );
      if (data.ok) {
        toast.success(
          `Re-fetching post types for ${data.total} stored link(s) via ${retypeProvider === "apify" ? "Apify" : "RapidAPI"} — progress appears under Latest Run`,
          { id: t }
        );
      } else {
        toast.info(data.message || "Nothing to re-fetch", { id: t });
      }
      setFixTypesOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start re-fetch", { id: t });
    } finally {
      setRetypeBusy(false);
    }
  };
  const posts = data?.posts || [];
  const summary = data?.summary || [];
  const storyRows = data?.story_rows || [];
  // Creators table sorting — click any column header. Default: Matched desc.
  const [creatorSort, setCreatorSort] = useState({ key: "matched", dir: -1 });
  const clickSort = (k) => setCreatorSort((p) => ({ key: k, dir: p.key === k ? -p.dir : -1 }));
  const sortArrow = (k) => (creatorSort.key === k ? (creatorSort.dir === 1 ? " ▲" : " ▼") : "");
  const sortedSummary = useMemo(() => {
    const val = (s) => {
      switch (creatorSort.key) {
        case "handle": return (s.handle || "").toLowerCase();
        case "followers": return Number(s.followers || 0);
        case "matched": return Number(s.matched_total ?? s.matched_posts ?? 0);
        case "collab": return Number(s.collab_posts || 0);
        case "reels": return Number(s.reels || 0);
        case "carousel": return Number(s.carousels || 0);
        case "static": return Number(s.static || 0);
        case "highlights": return Number(s.stories_highlights || 0);
        case "live": return Number(s.stories_live || 0);
        case "historic": return Number(s.custom_stories || 0);
        case "er_reels": return s.engagement_rate_reels == null ? -1 : Number(s.engagement_rate_reels);
        case "er_static": return s.engagement_rate_static == null ? -1 : Number(s.engagement_rate_static);
        case "likes": return Number(s.likes_sum ?? s.total_likes ?? 0);
        case "comments": return Number(s.comments_sum ?? s.total_comments ?? 0);
        case "engagement": return Number(s.total_likes ?? 0) + Number(s.total_comments ?? 0) + (s.shares == null ? 0 : Number(s.shares));
        case "reach": return Number(s.reach || 0);
        case "status": return creatorStatus(s).key;
        default: return 0;
      }
    };
    return [...summary].sort((a, b) => {
      const va = val(a), vb = val(b);
      if (typeof va === "string") return va.localeCompare(vb) * creatorSort.dir;
      return (va - vb) * creatorSort.dir;
    });
  }, [summary, creatorSort]);
  // Paginate large tables (100 rows/page) — thousands of DOM rows at once
  // was the main cause of slow dashboard repopulation. All rows stay
  // reachable via the pager; nothing is dropped.
  const CREATOR_PAGE_SIZE = 100;
  const creatorPageCount = Math.max(1, Math.ceil(sortedSummary.length / CREATOR_PAGE_SIZE));
  const safeCreatorPage = Math.min(creatorPage, creatorPageCount - 1);
  const pagedSummary = sortedSummary.slice(safeCreatorPage * CREATOR_PAGE_SIZE, (safeCreatorPage + 1) * CREATOR_PAGE_SIZE);
  // Reach — sum of per-creator estimates (16.4–18.9% of views each,
  // deterministic per handle, computed server-side in build_summary).
  const totalReach = useMemo(
    () => summary.reduce((n, x) => n + (Number(x.reach) || 0), 0),
    [summary]
  );
  const totalAccumulated = data?.total_accumulated_posts || 0;

  // Dashboard counts are the SUM of per-creator summary rows — the exact
  // same basis exports use (Full Excel Influencer Summary, Summary Excel,
  // Matched Posts sheet). Canonical rule from the user: if 10 creators × 20
  // posts = 200, dashboard = 200 and every export = 200. Collab is an
  // attribute (already inside its type count on the attributed creator);
  // it is NEVER added as a separate bucket to any total.
  const counts = useMemo(() => {
    let reels = 0, carousels = 0, staticN = 0, videos = 0, collabs = 0;
    let stories = 0, highlights = 0, live = 0, customStoriesTotal = 0;
    for (const s of summary) {
      reels += s.reels || 0;
      carousels += s.carousels || 0;
      staticN += s.static || 0;
      videos += s.videos || 0;
      collabs += s.collab_posts || 0;
      stories += s.stories || 0;
      highlights += s.stories_highlights || 0;
      live += s.stories_live || 0;
      customStoriesTotal += s.custom_stories || 0;
    }
    return { reels, carousels, static: staticN, videos, collabs, stories, highlights, live, customStoriesTotal };
  }, [summary]);
  // Live stories: accumulated total across ALL days + today's slice, so the
  // dashboard shows both "today" and "till now" at a glance.
  const todayIso = new Date().toISOString().slice(0, 10);
  const liveToday = useMemo(
    () => storyRows.filter((r) => (r.run_date || "") === todayIso)
      .reduce((a, b) => a + (b.live || 0), 0),
    [storyRows, todayIso]
  );
  // Story tables: zero-activity rows (0 live AND 0 highlights) are noise —
  // never rendered ("if it's zero, don't consider it").
  const nzStoryRows = useMemo(
    () => storyRows.filter((r) => (r.live || 0) > 0 || (r.highlights || 0) > 0),
    [storyRows]
  );
  const latestStoryDate = useMemo(
    () => nzStoryRows.reduce((m, r) => ((r.run_date || "") > m ? r.run_date : m), ""),
    [nzStoryRows]
  );
  const liveTodayRows = useMemo(
    () => nzStoryRows
      .filter((r) => r.run_date === latestStoryDate && (r.live || 0) > 0)
      .sort((a, b) => (b.live || 0) - (a.live || 0)),
    [nzStoryRows, latestStoryDate]
  );
  const storyTotals = useMemo(() => {
    const m = {};
    for (const r of nzStoryRows) {
      const e = m[r.handle] || (m[r.handle] = { handle: r.handle, live: 0, highlights: 0, days: 0 });
      e.live += r.live || 0;
      e.highlights += r.highlights || 0;
      e.days += 1;
    }
    return Object.values(m).sort((a, b) => b.live - a.live);
  }, [nzStoryRows]);
  // Overview engagement-rate cards — user's PER-CREATOR MEAN formulas
  // (2026-Feb, "N = creators who actually posted, not creators with a
  // populated ER"):
  //   ER Reels   = Σ per-creator ER Reels ÷ N creators who posted Reels
  //   ER Static  = Σ per-creator ER Static ÷ N creators who posted Static
  //   ER Overall = Σ (ER Reels + ER Static) for creators who posted BOTH
  //                ÷ N creators who posted BOTH
  //                (or mean-of-two per creator when report.er_overall_mean
  //                is ON — Nikon-only per user directive)
  // Creators without a computed ER (e.g. followers unknown) contribute 0
  // to the sum but ARE counted — matches "N creators who posted", which
  // is the caption on the tile.
  const useMeanOverall = !!report?.er_overall_mean;
  const erStats = useMemo(() => {
    const reelsPosters = summary.filter((s) => Number(s.video_posts_count || 0) > 0);
    const staticPosters = summary.filter((s) => Number(s.static_posts_count || 0) > 0);
    const bothPosters = summary.filter(
      (s) => Number(s.video_posts_count || 0) > 0
          && Number(s.static_posts_count || 0) > 0
    );
    const reelsSum = reelsPosters.reduce(
      (a, s) => a + Number(s.engagement_rate_reels || 0), 0);
    const staticSum = staticPosters.reduce(
      (a, s) => a + Number(s.engagement_rate_static || 0), 0);
    const bothSum = bothPosters.reduce(
      (a, s) => a + Number(s.engagement_rate_reels || 0)
                  + Number(s.engagement_rate_static || 0), 0);
    return {
      erReels: reelsPosters.length ? reelsSum / reelsPosters.length : null,
      erStatic: staticPosters.length ? staticSum / staticPosters.length : null,
      // Default: sum-of-two ÷ N. Nikon-only flag: mean-of-two ÷ N (Option A).
      erOverall: bothPosters.length
        ? bothSum / (useMeanOverall ? 2 : 1) / bothPosters.length
        : null,
      erOverallMode: useMeanOverall ? "mean" : "sum",
      nReels: reelsPosters.length,
      nStatic: staticPosters.length,
      nBoth: bothPosters.length,
    };
  }, [summary, useMeanOverall]);

  // Aggregate engagement totals across the filtered post set — surface as
  // toggleable stat tiles on the overview grid. Shares are summed only from
  // posts where the provider actually returned a value (Apify), so RapidAPI
  // batches don't lie with a bogus 0.
  const engTotals = useMemo(() => {
    let views = 0, likes = 0, comments = 0, shares = 0;
    let hasShares = false;
    for (const p of posts) {
      views += Number(p.video_views || 0);
      likes += Number(p.likes || 0);
      comments += Number(p.comments || 0);
      if (p.shares !== null && p.shares !== undefined) {
        shares += Number(p.shares || 0);
        hasShares = true;
      }
    }
    return { views, likes, comments, shares, hasShares };
  }, [posts]);
  // Total = posts (Reels + Posts + Carousels + Videos, collabs included)
  // PLUS accumulated live stories. Highlights stay count-only, never in Total.
  const postTotal = counts.reels + counts.carousels + counts.static + counts.videos;
  // Historic stories (per-creator column + the editable dashboard total box)
  // join the Total ONLY when the user flipped the switch ON.
  const historicSum = counts.customStoriesTotal + historicTotal;
  const grandTotal = postTotal + counts.live + (includeCustom ? historicSum : 0);

  if (!jobId && !data) {
    return (
      <div className="el-card p-12 text-center text-xs text-slate-400 font-mono" data-testid="report-data-loading">
        <div className="inline-block w-4 h-4 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin mb-2" />
        <div>Loading report analytics…</div>
      </div>
    );
  }

  return (
    <div className={`space-y-5 transition-opacity duration-150 ${loadingData ? "opacity-60 pointer-events-none" : "opacity-100"}`}>
      {/* Filter bar */}
      <div className="el-card p-4">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[10px] font-mono uppercase tracking-widest text-slate-400 flex items-center gap-1.5">
            <CalendarClock size={11} className="text-pink-600" />
            Filter view
            <span className="ml-1 normal-case tracking-normal text-slate-400">— by post &amp; highlight date</span>
          </span>
          <div className="flex gap-1 flex-wrap" data-testid="report-date-presets">
            {[
              { id: "all", label: "All time" },
              { id: "thisMonth", label: "This month" },
              { id: "lastMonth", label: "Last month" },
              { id: "7d", label: "7d" },
              { id: "30d", label: "30d" },
              { id: "90d", label: "90d" },
              { id: "custom", label: "Custom" },
            ].map((p) => (
              <button
                key={p.id}
                onClick={() => setFilterPreset(p.id)}
                className={`px-2.5 py-1 rounded-md text-[11px] font-medium transition-all ${
                  filterPreset === p.id
                    ? "bg-slate-900 text-white"
                    : "bg-white border border-slate-200 text-slate-500 hover:border-pink-300"
                }`}
                data-testid={`report-filter-${p.id}`}
              >
                {p.label}
              </button>
            ))}
          </div>
          {filterPreset === "custom" && (
            <div className="flex items-center gap-2 ml-auto">
              <DateField value={fromDate} onChange={setFromDate} testid="report-filter-from" placeholder="From" />
              <span className="text-slate-400">→</span>
              <DateField value={toDate} onChange={setToDate} testid="report-filter-to" placeholder="To" />
            </div>
          )}
          <div className="ml-auto text-[10px] font-mono text-slate-400">
            {range.from ? `${range.from} → ${range.to || "today"}` : "All time"}
            <span className="mx-2">·</span>
            <span className="text-pink-600 font-semibold">
              {posts.length}
            </span>
            <span> of {totalAccumulated} accumulated</span>
          </div>
        </div>
      </div>

      {/* Status + export */}
      <div className="flex items-center gap-2">
        {isRunning ? (
          <>
            <Loader2 size={14} className="animate-spin text-pink-600" />
            <span className="text-xs font-mono text-slate-500">Refreshing…</span>
          </>
        ) : job ? (
          <>
            <span
              className={`w-2 h-2 rounded-full ${
                job.status === "completed" ? "bg-emerald-400" : "bg-red-500"
              }`}
            />
            <span className="text-xs font-mono text-slate-500">
              Last run · {new Date(job.created_at).toLocaleString()}
            </span>
          </>
        ) : (
          <span className="text-xs font-mono text-slate-400">No runs yet</span>
        )}
        <Button
          onClick={() => setSubTab("performers")}
          variant="outline"
          className="bg-white border-slate-200 hover:bg-amber-50 hover:border-amber-300 hover:text-amber-700 text-slate-700 h-8 text-xs"
          data-testid="analyse-creator-split-btn"
          title="Analyse this report by creator split (Nano/Micro/Macro/Mega/Celebrity) and top performers — works on existing reports, no new fetches."
        >
          <Trophy size={12} className="mr-1.5" />
          Analyse: Creator Split & Top Performers
        </Button>
        <Button
          onClick={() => setFixTypesOpen(true)}
          variant="outline"
          className="bg-white border-slate-200 hover:bg-violet-50 hover:border-violet-300 hover:text-violet-700 text-slate-700 h-8 text-xs disabled:opacity-40"
          data-testid="report-reclassify-btn"
          title="Fix post types (Carousel vs Reel vs Static) & creator names — quick free scan, or an accurate re-fetch of stored links."
        >
          <Sparkles size={12} className="mr-1.5" />
          Fix Post Types
        </Button>
        <Dialog open={fixTypesOpen} onOpenChange={setFixTypesOpen}>
          <DialogContent className="bg-white border-slate-200 text-slate-900 max-w-md" data-testid="fix-post-types-dialog">
            <DialogHeader>
              <DialogTitle className="text-slate-900 text-base">Fix post types & creator names</DialogTitle>
              <DialogDescription className="text-slate-500 text-xs">
                Repair Carousels misread as Static, and backfill creator names, across every link already stored in this report.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3 mt-1">
              <button
                type="button"
                onClick={autoFixAttribution}
                disabled={autoFixBusy || reclassifying || retypeBusy}
                className="w-full text-left rounded-lg border-2 border-emerald-300 hover:border-emerald-400 hover:bg-emerald-50/60 p-3 transition-colors disabled:opacity-50 bg-emerald-50/30"
                data-testid="auto-fix-attribution-btn"
              >
                <div className="text-xs font-semibold text-slate-900 flex items-center gap-1.5">
                  <Sparkles size={12} className="text-emerald-600" />
                  Auto-fix everything — free, recommended
                </div>
                <div className="text-[11px] text-slate-500 mt-1 leading-snug">
                  Attributes orphan posts to tracked creators automatically: exact
                  owner/coauthor/mention match first, then closest-name fuzzy
                  match (letters-only, ≥4 chars). Guarantees zero duplicate rows
                  per (post, creator). Zero credits.
                </div>
                {autoFixBusy && (
                  <div className="text-[10px] text-emerald-700 mt-1.5 font-mono">Running…</div>
                )}
                {autoFixResult && !autoFixBusy && (
                  <div className="text-[10px] text-slate-600 mt-2 font-mono grid grid-cols-2 gap-x-2" data-testid="auto-fix-result">
                    <div>Orphans before: <b>{autoFixResult.before?.orphan ?? 0}</b></div>
                    <div>Orphans after: <b>{autoFixResult.after?.orphan ?? 0}</b></div>
                    <div>Reattributed: <b>{autoFixResult.repair?.reattributed ?? 0}</b></div>
                    <div>Via fuzzy: <b>{autoFixResult.repair?.fuzzy ?? 0}</b></div>
                    <div>First-of-ambiguous: <b>{autoFixResult.repair?.ambiguous_first_picked ?? 0}</b></div>
                    <div>Duplicates removed: <b>{autoFixResult.dedupe?.deleted ?? 0}</b></div>
                  </div>
                )}
              </button>
              <button
                type="button"
                onClick={runKeywordAudit}
                disabled={kwAuditBusy || autoFixBusy || reclassifying || retypeBusy}
                className="w-full text-left rounded-lg border border-slate-200 hover:border-amber-300 hover:bg-amber-50/40 p-3 transition-colors disabled:opacity-50"
                data-testid="keyword-audit-btn"
              >
                <div className="text-xs font-semibold text-slate-900 flex items-center gap-1.5">
                  <Search size={12} className="text-amber-600" /> Keyword audit — free, report only
                </div>
                <div className="text-[11px] text-slate-400 mt-1 leading-snug">
                  Re-checks every stored post against this report's CURRENT keywords and lists the
                  ones that no longer match. Nothing is deleted — you review and decide.
                </div>
                {kwAuditBusy && (
                  <div className="text-[10px] text-amber-700 mt-1.5 font-mono">Auditing…</div>
                )}
                {kwAudit && !kwAuditBusy && kwAudit.applicable !== false && (
                  <div className="mt-2" data-testid="keyword-audit-result">
                    <div className="text-[10px] text-slate-600 font-mono grid grid-cols-2 gap-x-2">
                      <div>Unique posts: <b>{kwAudit.unique_posts}</b></div>
                      <div>Match keywords: <b className="text-emerald-600">{kwAudit.matching}</b></div>
                      <div>Don't match: <b className="text-red-600">{kwAudit.non_matching}</b></div>
                      <div>Can't verify (no caption stored): <b>{kwAudit.unverifiable}</b></div>
                    </div>
                    {kwAudit.items?.length > 0 && (
                      <div className="max-h-40 overflow-y-auto mt-2 space-y-1 border-t border-amber-200/60 pt-2">
                        {kwAudit.items.map((it) => (
                          <a
                            key={it.shortcode}
                            href={it.post_url || `https://www.instagram.com/p/${it.shortcode}/`}
                            target="_blank" rel="noreferrer"
                            className="block text-[10px] font-mono text-slate-500 hover:text-amber-700 truncate"
                            data-testid={`kw-audit-item-${it.shortcode}`}
                          >
                            {it.post_type} · {it.shortcode} · {(it.handles || []).map((h) => "@" + h).join(", ")}
                            {it.caption_snippet ? ` — ${it.caption_snippet}` : ""}
                          </a>
                        ))}
                        {kwAudit.non_matching > kwAudit.returned && (
                          <div className="text-[10px] text-slate-400 font-mono pt-1">
                            …and {kwAudit.non_matching - kwAudit.returned} more
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
                {kwAudit && !kwAuditBusy && kwAudit.applicable === false && (
                  <div className="text-[10px] text-slate-500 mt-2 font-mono" data-testid="keyword-audit-result">
                    {kwAudit.reason}
                  </div>
                )}
              </button>
              <button
                type="button"
                onClick={reclassifyPosts}
                disabled={reclassifying || retypeBusy || autoFixBusy}
                className="w-full text-left rounded-lg border border-slate-200 hover:border-violet-300 hover:bg-violet-50/40 p-3 transition-colors disabled:opacity-50"
                data-testid="fix-types-quick-btn"
              >
                <div className="text-xs font-semibold text-slate-900 flex items-center gap-1.5">
                  <Sparkles size={12} className="text-violet-600" /> Quick scan — free, instant
                </div>
                <div className="text-[11px] text-slate-400 mt-1 leading-snug">
                  Re-checks stored data for carousel signals and backfills creator names. Zero credits — but can only use data we already stored.
                </div>
              </button>
              <div className="rounded-lg border border-slate-200 p-3">
                <div className="text-xs font-semibold text-slate-900 flex items-center gap-1.5">
                  <RefreshCw size={12} className="text-indigo-600" /> Re-fetch from Instagram — accurate
                </div>
                <div className="text-[11px] text-slate-400 mt-1 leading-snug">
                  Re-downloads each stored link's type, updates rows in place, and <b>regroups posts onto your tracked handles</b> (owner + collab attribution) — fixes Carousel counts and old links added before a handle existed. Costs 1 credit per stored link.
                </div>
                <div className="flex gap-2 mt-2.5" data-testid="retype-provider">
                  {[
                    { k: "rapidapi", label: "RapidAPI", sub: "~50/min plan cap" },
                    { k: "apify", label: "Apify", sub: "fast bulk · ~$1.50/1k" },
                  ].map((p) => (
                    <button
                      key={p.k}
                      type="button"
                      onClick={() => setRetypeProvider(p.k)}
                      className={`flex-1 px-2.5 py-1.5 rounded-lg border text-[11px] font-semibold transition-colors ${
                        retypeProvider === p.k
                          ? "bg-indigo-50 border-indigo-300 text-indigo-700"
                          : "bg-white border-slate-200 text-slate-500 hover:border-slate-300"
                      }`}
                      data-testid={`retype-provider-${p.k}`}
                    >
                      {p.label}
                      <span className="block text-[9px] font-normal opacity-70">{p.sub}</span>
                    </button>
                  ))}
                </div>
                <Button
                  onClick={refetchPostTypes}
                  disabled={retypeBusy || reclassifying || autoFixBusy}
                  className="w-full mt-2.5 bg-slate-900 hover:bg-slate-800 text-white h-8 text-xs disabled:opacity-50"
                  data-testid="retype-start-btn"
                >
                  {retypeBusy ? "Starting…" : "Start re-fetch"}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
        <Button
          onClick={backfillMetrics}
          disabled={backfilling}
          variant="outline"
          className="ml-auto bg-white border-slate-200 hover:bg-cyan-50 hover:border-cyan-500/40 hover:text-cyan-700 text-slate-700 h-8 text-xs disabled:opacity-40"
          data-testid="report-backfill-metrics"
          title="Re-fetch follower counts + Reel view counts WITHOUT re-scraping posts (saves quota). Refreshes engagement rates in place."
        >
          <RefreshCw
            size={12}
            className={`mr-1.5 ${backfilling ? "animate-spin" : ""}`}
          />
          {backfilling ? "Refreshing…" : "Refresh metrics"}
        </Button>
        <Button
          onClick={syncStoryHistory}
          disabled={syncingHistory}
          variant="outline"
          className="bg-white border-slate-200 hover:bg-slate-100 hover:border-slate-300 hover:text-slate-900 text-slate-700 h-8 text-xs disabled:opacity-40"
          data-testid="report-sync-story-history"
          title="Import historical daily story counts for these handles already collected by other reports in your workspace (no re-scraping, idempotent)."
        >
          <Clock
            size={12}
            className={`mr-1.5 ${syncingHistory ? "animate-spin" : ""}`}
          />
          {syncingHistory ? "Syncing…" : "Sync story history"}
        </Button>
        <Button
          onClick={exportCanonSummary}
          disabled={!data}
          variant="outline"
          className="bg-white border-slate-200 hover:bg-slate-100 hover:border-slate-300 hover:text-slate-900 text-slate-700 h-8 text-xs"
          data-testid="report-export-canon"
          title="Single-sheet creator summary in the Canon reporting format"
        >
          <FileText size={12} className="mr-1.5" />
          Summary Excel
        </Button>
        <Button
          onClick={exportFiltered}
          disabled={!data}
          variant="outline"
          className="bg-white border-slate-200 hover:bg-slate-50 hover:border-pink-300 text-slate-700 h-8 text-xs"
          data-testid="report-export-filtered"
        >
          <Download size={12} className="mr-1.5" />
          Full Excel
        </Button>
      </div>

      {isRunning && (
        <ScrollArea className="h-24 rounded-md bg-white border border-slate-200 p-3">
          <div className="text-[11px] font-mono text-slate-500 space-y-1">
            {(job?.log || []).slice(-10).map((l, i) => (
              <div key={i} className={l.includes("ERROR") ? "text-red-600" : ""}>
                <span className="text-slate-400">›</span> {l}
              </div>
            ))}
          </div>
        </ScrollArea>
      )}

      {/* Link review queue — added links awaiting creator confirmation.
          Self-fetches; hidden when empty. */}
      <PendingLinksCard reportId={reportId} onResolved={fetchData} />
      <OrphanReviewCard reportId={reportId} onResolved={fetchData} />

      {/* Sub-tabs — kills the endless scroll: Stats / Creators / Content / Stories */}
      <div
        className="flex items-center gap-1 p-1 bg-slate-100/80 border border-slate-200/80 rounded-xl w-fit"
        data-testid="report-subtabs-bar"
      >
        {[
          { id: "stats", label: "Stats & ER", icon: BarChart3, testId: "subtab-stats" },
          { id: "creators", label: "Creators", icon: Users, testId: "subtab-creators" },
          { id: "content", label: "Content", icon: LayoutGrid, testId: "subtab-content" },
          { id: "stories", label: "Stories & Live", icon: Zap, testId: "subtab-stories" },
          { id: "performers", label: "Top Performers", icon: Trophy, testId: "subtab-performers" },
        ].map((t) => {
          const Icon = t.icon;
          const active = subTab === t.id;
          return (
            <button
              key={t.id}
              onClick={() => setSubTab(t.id)}
              data-testid={t.testId}
              className={`flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg text-xs transition-all ${
                active
                  ? "bg-white text-slate-900 shadow-sm border border-slate-200/60 font-semibold"
                  : "text-slate-500 font-medium hover:text-slate-800 hover:bg-slate-200/50"
              }`}
            >
              <Icon size={13} className={active ? "text-pink-600" : "text-slate-400"} />
              {t.label}
            </button>
          );
        })}
      </div>

      {subTab === "stats" && (<>
      {/* Apify credit breakdown for the latest run */}
      {job && Array.isArray(job.apify_usage) && job.apify_usage.length > 0 && (() => {
        const usage = job.apify_usage;
        const byKind = { posts: {usd:0, cu:0, items:0}, stories: {usd:0, cu:0, items:0}, profile: {usd:0, cu:0, items:0} };
        for (const u of usage) {
          const k = u.kind || "posts";
          if (byKind[k]) {
            byKind[k].usd += Number(u.usd || 0);
            byKind[k].cu += Number(u.compute_units || 0);
            byKind[k].items += Number(u.items || 0);
          }
        }
        const total = usage.reduce((a,b)=>a+Number(b.usd||0),0);
        return (
          <div className="el-card p-4" data-testid="apify-usage-breakdown">
            <div className="flex items-center justify-between gap-3 mb-2">
              <span className="text-[10px] font-mono uppercase tracking-widest text-slate-400 flex items-center gap-1.5">
                <Zap size={11} className="text-emerald-600" />
                Scraper usage — this run
              </span>
              <span className="text-[11px] font-mono text-emerald-600">
                Apify total ${total.toFixed(4)}
              </span>
            </div>
            <div className="grid grid-cols-3 gap-2 text-[11px] font-mono">
              {[
                {k:"posts",   label:"Posts",   c:"text-pink-600"},
                {k:"stories", label:"Stories", c:"text-pink-600"},
                {k:"profile", label:"Profile", c:"text-violet-600"},
              ].map(({k,label,c}) => (
                <div key={k} className="p-2 rounded-md bg-white border border-slate-200">
                  <div className={`text-[9px] uppercase tracking-widest ${c}`}>{label}</div>
                  <div className="text-slate-800">${byKind[k].usd.toFixed(4)}</div>
                  <div className="text-[10px] text-slate-400">
                    {byKind[k].cu.toFixed(4)} CU · {byKind[k].items} item(s)
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })()}


      {/* Stats grid — Total = Posts + accumulated Live stories (today + older).
          Collabs are posts, already inside Total (bracketed). Highlights are
          count-only, never folded into Total. Customizable via the drawer. */}
      {(() => {
        const statTiles = [
          { key: "total", label: "Total", value: grandTotal, icon: Layers, accent: "amber", testId: "stat-total-posts",
            sub: `${postTotal} posts + ${counts.live} live${counts.collabs ? ` · ${counts.collabs} collabs` : ""}${historicSum ? (includeCustom ? ` · +${historicSum} historic stories` : ` · ${historicSum} historic not in total`) : ""}` },
          { key: "reels", label: "Reels", value: counts.reels, icon: Film, accent: "pink", testId: "stat-reels" },
          { key: "static", label: "Posts", value: counts.static, icon: ImageIcon, accent: "emerald", testId: "stat-static" },
          { key: "carousels", label: "Carousels", value: counts.carousels, icon: Layers, accent: "violet", testId: "stat-carousels" },
          { key: "collabs", label: "Collabs", value: counts.collabs, icon: Users, accent: "orange", testId: "stat-collabs",
            sub: "highlight — already inside type counts" },
          { key: "stories", label: "Stories", value: counts.stories, icon: Bookmark, accent: "fuchsia", testId: "stat-stories",
            sub: "live 24h only · highlights excluded" },
          { key: "highlights", label: "Highlights", value: counts.highlights, icon: Bookmark, accent: "cyan", testId: "stat-highlights",
            sub: "not in Total" },
          { key: "live", label: "Live 24h", value: counts.live, icon: Radio, accent: "pink", testId: "stat-live",
            sub: `${liveToday} today · ${counts.live} till now` },
          { key: "reach", label: "Reach", value: formatNum(totalReach), icon: Target, accent: "cyan", testId: "stat-reach" },
          { key: "er-reels", label: "ER Reels", testId: "stat-er-reels", icon: Film, accent: "violet",
            value: erStats.erReels === null ? "N/A" : `${erStats.erReels.toFixed(2)}%`,
            sub: `Σ per-creator ER Reels ÷ ${erStats.nReels} creator${erStats.nReels === 1 ? "" : "s"}` },
          { key: "er-static", label: "ER Static", testId: "stat-er-static", icon: ImageIcon, accent: "emerald",
            value: erStats.erStatic === null ? "N/A" : `${erStats.erStatic.toFixed(2)}%`,
            sub: `Σ per-creator ER Static ÷ ${erStats.nStatic} creator${erStats.nStatic === 1 ? "" : "s"}` },
          { key: "er-overall", label: "ER Overall", testId: "stat-er-overall", icon: Activity, accent: "pink",
            value: erStats.erOverall === null ? "N/A" : `${erStats.erOverall.toFixed(2)}%`,
            sub: useMeanOverall
              ? `mean of (ER Reels + ER Static)/2 ÷ ${erStats.nBoth} posted both`
              : `Σ (ER Reels + ER Static) for creators who posted BOTH ÷ ${erStats.nBoth}` },
          { key: "views", label: "Total Views", value: formatNum(engTotals.views), icon: Eye, accent: "cyan", testId: "stat-views",
            sub: "video views across all posts" },
          { key: "likes", label: "Total Likes", value: formatNum(engTotals.likes), icon: Heart, accent: "pink", testId: "stat-likes",
            sub: "across all posts" },
          { key: "comments", label: "Total Comments", value: formatNum(engTotals.comments), icon: MessageCircle, accent: "violet", testId: "stat-comments",
            sub: "across all posts" },
          { key: "shares", label: "Total Shares", value: engTotals.hasShares ? formatNum(engTotals.shares) : "—",
            icon: Radio, accent: "orange", testId: "stat-shares",
            sub: engTotals.hasShares ? "Apify-provided only" : "not returned by RapidAPI" },
          { key: "total-eng", label: "Total Engagement", value: formatNum(engTotals.likes + engTotals.comments + engTotals.shares), icon: Heart, accent: "rose", testId: "stat-total-engagement",
            sub: "all likes + all comments + all shares" },
          // ── Posted vs Not-Posted card set (5 tiles) — toggle-able individually ──
          { key: "posted", label: "Posted section · Total Posted", section: "bif", value: "—", testId: "stat-toggle-posted",
            sub: "creators who posted at least one Reel/Static/Carousel" },
          { key: "posted-stories", label: "Posted section · Posted + Stories", section: "bif", value: "—", testId: "stat-toggle-posted-stories",
            sub: "creators with posts AND stories" },
          { key: "posted-only", label: "Posted section · Posted only (no stories)", section: "bif", value: "—", testId: "stat-toggle-posted-only",
            sub: "creators with posts but no stories" },
          { key: "stories-only", label: "Posted section · Stories only", section: "bif", value: "—", testId: "stat-toggle-stories-only",
            sub: "creators with only stories, no posts" },
          { key: "nothing", label: "Posted section · Nothing at all", section: "bif", value: "—", testId: "stat-toggle-nothing",
            sub: "creators with no posts and no stories" },
          // ── ER strip cards (average per post) ──
          { key: "er-strip-reels", label: "ER strip · ER Reels", section: "bif", value: "—", testId: "stat-toggle-er-strip-reels",
            sub: "Σ per-creator Reels ERs ÷ creators who posted Reels" },
          { key: "er-strip-static", label: "ER strip · ER Static", section: "bif", value: "—", testId: "stat-toggle-er-strip-static",
            sub: "Σ per-creator Static ERs ÷ creators who posted Static/Carousels" },
          { key: "er-strip-overall", label: "ER strip · ER Overall", section: "bif", value: "—", testId: "stat-toggle-er-strip-overall",
            sub: "Σ all per-creator ERs ÷ creators who posted at least once" },
          { key: "er-detail-table", label: "ER strip · detail table", section: "bif", value: "—", testId: "stat-toggle-er-detail-table",
            sub: "creators / engagement / base / ER% per metric" },
          { key: "tier-split", label: "Creator tiers · Nano/Micro/Macro/Mega/Celebrity", section: "bif", value: "—", testId: "stat-toggle-tier-split",
            sub: "creators split by follower band" },
          { key: "historic-stories", label: "Historic Stories (editable total)", section: "bif", value: "—", testId: "stat-toggle-historic-stories",
            sub: "editable manual story total — counts in Total when the switch is ON" },
        ];
        return (
          <>
            <div className="flex items-center justify-end gap-4">
              <label
                className="inline-flex items-center gap-2 text-[11px] font-medium text-slate-500 cursor-pointer"
                title="Add the manually-entered Historic Stories column to the Total & Matched counts"
              >
                <Switch
                  checked={includeCustom}
                  onCheckedChange={(v) => saveCustomStories(customStories, v)}
                  data-testid="custom-stories-toggle"
                />
                Historic stories in Total
              </label>
              <label
                className="inline-flex items-center gap-1.5 text-[11px] font-medium text-slate-500 hover:text-indigo-600 border border-slate-200 hover:border-indigo-300 rounded-lg px-2.5 py-1.5 bg-white transition-colors cursor-pointer"
                title="Bulk-upload per-creator baseline Live 24h story counts from a CSV (columns: Profile, count). Adds to each creator's Live 24h — new tracked stories accumulate on top."
                data-testid="upload-baseline-stories-btn"
              >
                <Upload size={12} /> Upload baseline stories (CSV)
                <input
                  type="file"
                  accept=".csv,text/csv"
                  className="hidden"
                  data-testid="upload-baseline-stories-input"
                  onChange={async (e) => {
                    const f = e.target.files && e.target.files[0];
                    e.target.value = "";
                    if (!f) return;
                    const t = toast.loading("Uploading baseline stories…");
                    try {
                      const fd = new FormData();
                      fd.append("file", f);
                      const { data } = await axios.post(
                        `${API}/reports/${reportId}/baseline-live-stories/upload?mode=replace`,
                        fd,
                        { headers: { "Content-Type": "multipart/form-data" } }
                      );
                      toast.success(
                        `Baseline saved — ${data.creators_imported} creator(s), total ${data.total_baseline} live stories${data.unmatched_count ? ` · ${data.unmatched_count} handle(s) not tracked (still stored)` : ""}`,
                        { id: t, duration: 8000 }
                      );
                      fetchDataRef.current && fetchDataRef.current();
                    } catch (err) {
                      toast.error(err.response?.data?.detail || "Upload failed", { id: t });
                    }
                  }}
                />
              </label>
              <button
                type="button"
                onClick={async () => {
                  const t = toast.loading("Fetching fresh stories from Instagram…");
                  try {
                    const { data } = await axios.post(
                      `${API}/reports/${reportId}/refresh-stories`
                    );
                    toast.success(
                      `${data.message} (${data.handles} handle${data.handles === 1 ? "" : "s"})`,
                      { id: t, duration: 8000 }
                    );
                    // Give the background job ~30s then refresh the dashboard.
                    setTimeout(() => fetchDataRef.current && fetchDataRef.current(), 30000);
                  } catch (err) {
                    toast.error(err.response?.data?.detail || "Could not start refresh", { id: t });
                  }
                }}
                className="inline-flex items-center gap-1.5 text-[11px] font-medium text-slate-500 hover:text-pink-600 border border-slate-200 hover:border-pink-300 rounded-lg px-2.5 py-1.5 bg-white transition-colors"
                title="Fetch live 24h stories NOW — bypasses cache, uses RapidAPI credits. Fixes 'IG shows 2 stories but dashboard shows 1' when stories were posted after the daily cron run."
                data-testid="refresh-stories-now-btn"
              >
                <RefreshCcw size={12} /> Refresh stories now
              </button>
              <button
                onClick={() => setCustomizeOpen(true)}
                className="inline-flex items-center gap-1.5 text-[11px] font-medium text-slate-500 hover:text-indigo-600 border border-slate-200 hover:border-indigo-300 rounded-lg px-2.5 py-1.5 bg-white transition-colors"
                data-testid="customize-stats-btn"
              >
                <LayoutGrid size={12} /> Customize
              </button>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-3">
              {statTiles.filter((t) => t.section !== "bif" && statVis[t.key] !== false).map((t) => (
                <StatMini key={t.key} label={t.label} value={t.value} icon={t.icon} accent={t.accent} testId={t.testId} sub={t.sub} />
              ))}
              {statVis["historic-stories"] !== false && (
                <div
                  className="el-tile px-3.5 py-3"
                  data-testid="stat-historic-stories"
                  title="Manual historic story total for the whole report — counts toward Total when 'Historic stories in Total' is ON"
                >
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] font-semibold uppercase tracking-wider text-indigo-500">Historic Stories</span>
                    <Pencil size={11} className="text-indigo-300" />
                  </div>
                  <input
                    key={historicTotal}
                    type="number"
                    min="0"
                    defaultValue={historicTotal || ""}
                    placeholder="0"
                    data-testid="historic-stories-total-input"
                    className="mt-0.5 w-full text-lg font-semibold font-mono tabular-nums tracking-tight text-slate-900 bg-transparent border-b border-transparent hover:border-indigo-200 focus:border-indigo-400 focus:outline-none"
                    onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
                    onBlur={(e) => {
                      const n = Math.max(0, parseInt(e.target.value || "0", 10) || 0);
                      if (n !== historicTotal) saveHistoricTotal(n);
                    }}
                  />
                  <div className="text-[10px] font-mono text-slate-400 mt-0.5">
                    {includeCustom ? "included in Total" : "not in Total — flip the switch"}
                  </div>
                </div>
              )}
            </div>
            {customizeOpen && (
              <div className="fixed inset-0 z-50" data-testid="customize-stats-drawer">
                <div className="absolute inset-0 bg-slate-900/30" onClick={() => setCustomizeOpen(false)} />
                <div className="absolute right-0 top-0 h-full w-80 max-w-[85vw] bg-white shadow-2xl border-l border-slate-200 flex flex-col">
                  <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
                    <div>
                      <h3 className="text-sm font-semibold text-slate-900">Customize stats</h3>
                      <p className="text-[11px] text-slate-400 mt-0.5">Choose which cards show in Stats & ER.</p>
                    </div>
                    <button
                      onClick={() => setCustomizeOpen(false)}
                      className="text-slate-400 hover:text-slate-700 transition-colors"
                      data-testid="customize-stats-close"
                    >
                      <XIcon size={16} />
                    </button>
                  </div>
                  <div className="flex-1 overflow-y-auto px-5 py-4 space-y-1.5">
                    {statTiles.map((t) => (
                      <EditToggleRow
                        key={t.key}
                        label={t.label}
                        desc={t.sub || ""}
                        checked={statVis[t.key] !== false}
                        onChange={(v) => toggleStat(t.key, v)}
                        testid={`stat-toggle-${t.key}`}
                      />
                    ))}
                  </div>
                </div>
              </div>
            )}
          </>
        );
      })()}

      {/* Deliverable Type summary — per user's spec (per-post average):
          Reels ER = avg per Reel of (likes+comments+shares) / views · 100
          Static / Carousel ER = avg per post of
            (likes+comments+shares) / followers · 100,
            using shares only if the provider returned them (Apify);
            followers = per-owner followersCount from summary rows. */}
      <DeliverableTypeTable posts={posts} summary={summary} />

      {/* Posted vs Not-Posted bifurcation — same block as Excel Report Overview */}
      <PostedBifurcation summary={summary} posts={posts} testIdPrefix="bif" visible={statVis}
        erOverallMode={report?.er_overall_mean ? "mean" : "sum"} />

      {/* Creator-tier split (Nano/Micro/Macro/Mega/Celebrity) — toggleable
          from the Customize drawer, computed from stored followers. */}
      <CreatorTiers summary={summary} visible={statVis} />

      {/* Campaign timeline + interactive graphs (frequency + views) */}
      <CampaignTimeline posts={posts} />
      </>)}

      {/* Stories — three focused tables instead of one endless list:
          Live right now (latest capture) · Totals till now · compact history.
          Zero-activity rows are filtered out (nzStoryRows). */}
      {subTab === "stories" && nzStoryRows.length === 0 && (
        <div className="el-card p-10 text-center text-xs font-mono text-slate-400" data-testid="story-timeline-empty">
          No active or highlight stories recorded during this window.
        </div>
      )}
      {subTab === "stories" && nzStoryRows.length > 0 && (
        <div className="space-y-4">
          {/* 1. Live right now — latest capture day only */}
          <div className="el-card p-4" data-testid="story-live-today">
            <div className="text-[10px] font-mono uppercase tracking-widest text-slate-400 mb-2 flex items-center gap-1.5">
              <Radio size={10} className="text-pink-600" /> Live right now
              <span className="text-slate-300 normal-case tracking-normal">
                — captured {latestStoryDate} · {liveTodayRows.reduce((n, r) => n + (r.live || 0), 0)} live across {liveTodayRows.length} creator(s)
              </span>
            </div>
            {liveTodayRows.length === 0 ? (
              <div className="text-xs font-mono text-slate-400 py-3 text-center" data-testid="story-live-today-empty">
                No live stories on the latest capture day.
              </div>
            ) : (
              <div className="overflow-x-auto rounded-lg border border-slate-200/70">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-[10px] uppercase tracking-wider font-mono text-slate-400 bg-slate-50/70 border-b border-slate-200">
                      <th className="text-left px-3 py-2.5 font-medium">Creator</th>
                      <th className="text-right px-3 py-2.5 font-medium">Live stories</th>
                    </tr>
                  </thead>
                  <tbody>
                    {liveTodayRows.map((r) => (
                      <tr key={r.handle} className="border-b border-slate-100 last:border-0 data-row">
                        <td className="px-3 py-2 text-slate-700 font-medium">@{r.handle}</td>
                        <td className="px-3 py-2 text-right font-mono font-semibold text-slate-900 tabular-nums">{r.live}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* 2. Totals till now — one row per creator */}
          <div className="el-card p-4" data-testid="story-totals">
            <div className="text-[10px] font-mono uppercase tracking-widest text-slate-400 mb-2 flex items-center gap-1.5">
              <Bookmark size={10} className="text-pink-600" /> Totals till now
              <span className="text-slate-300 normal-case tracking-normal">— accumulated live 24h stories; highlights shown separately, never in totals</span>
            </div>
            <div className="overflow-x-auto rounded-lg border border-slate-200/70">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-[10px] uppercase tracking-wider font-mono text-slate-400 bg-slate-50/70 border-b border-slate-200">
                    <th className="text-left px-3 py-2.5 font-medium">Creator</th>
                    <th className="text-right px-3 py-2.5 font-medium">Live (till now)</th>
                    <th className="text-right px-3 py-2.5 font-medium">Highlights</th>
                    <th className="text-right px-3 py-2.5 font-medium">Days active</th>
                  </tr>
                </thead>
                <tbody>
                  {storyTotals.map((r) => (
                    <tr key={r.handle} className="border-b border-slate-100 last:border-0 data-row">
                      <td className="px-3 py-2 text-slate-700 font-medium">@{r.handle}</td>
                      <td className="px-3 py-2 text-right font-mono font-semibold text-slate-900 tabular-nums">{r.live}</td>
                      <td className="px-3 py-2 text-right font-mono text-slate-500 tabular-nums">{r.highlights}</td>
                      <td className="px-3 py-2 text-right font-mono text-slate-500 tabular-nums">{r.days}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* 3. Day-by-day history — only days with activity, newest first, compact scroll */}
          <div className="el-card p-4" data-testid="story-history">
            <div className="text-[10px] font-mono uppercase tracking-widest text-slate-400 mb-2 flex items-center gap-1.5">
              <CalendarClock size={10} className="text-pink-600" /> Day-by-day history
              <span className="text-slate-300 normal-case tracking-normal">— only days with activity are listed</span>
            </div>
            <div className="overflow-auto rounded-lg border border-slate-200/70 max-h-72">
              <table className="w-full text-xs">
                <thead className="sticky top-0 bg-slate-50 z-10">
                  <tr className="text-[10px] uppercase tracking-wider font-mono text-slate-400 border-b border-slate-200">
                    <th className="text-left px-3 py-2.5 font-medium">Date</th>
                    <th className="text-left px-3 py-2.5 font-medium">Creator</th>
                    <th className="text-right px-3 py-2.5 font-medium">Live</th>
                    <th className="text-right px-3 py-2.5 font-medium">Highlights</th>
                  </tr>
                </thead>
                <tbody>
                  {[...nzStoryRows].sort((a, b) => ((a.run_date || "") < (b.run_date || "") ? 1 : -1)).map((r) => (
                    <tr key={`${r.run_date}-${r.handle}`} className="border-b border-slate-100 last:border-0 data-row">
                      <td className="px-3 py-2 font-mono text-slate-500 tabular-nums">{r.run_date}</td>
                      <td className="px-3 py-2 text-slate-700 font-medium">@{r.handle}</td>
                      <td className="px-3 py-2 text-right font-mono font-semibold text-slate-900 tabular-nums">{r.live}</td>
                      <td className="px-3 py-2 text-right font-mono text-slate-500 tabular-nums">{r.highlights}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Influencer summary */}
      {subTab === "creators" && summary.length === 0 && (
        <div className="el-card p-10 text-center text-xs font-mono text-slate-400" data-testid="creators-empty">
          No creators mapped to this report yet. Add handles in settings to start tracking.
        </div>
      )}
      {subTab === "creators" && summary.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-[10px] uppercase tracking-widest font-mono text-slate-400 border-b border-slate-200">
                <th className="text-left px-3 py-2 cursor-pointer select-none hover:text-indigo-600" onClick={() => clickSort("handle")} data-testid="sort-handle">Handle{sortArrow("handle")}</th>
                <th className="text-right px-2 py-2 cursor-pointer select-none hover:text-indigo-600" onClick={() => clickSort("followers")} data-testid="sort-followers">Followers{sortArrow("followers")}</th>
                <th className="text-right px-2 py-2 cursor-pointer select-none hover:text-indigo-600" onClick={() => clickSort("matched")} data-testid="sort-matched" title="Posts (Reels + Carousel + Static + Videos) + Live 24h stories matched for this creator. Highlights are pinned profile fixtures and are NOT included. Collabs are already inside their type counts.">Matched{sortArrow("matched")}</th>
                <th className="text-right px-2 py-2 text-slate-400 cursor-pointer select-none hover:text-indigo-600" onClick={() => clickSort("collab")} data-testid="sort-collab" title="Posts where the handle is a collaborator (not the owner) — highlight only, already counted in their type">Collab{sortArrow("collab")}</th>
                <th className="text-right px-2 py-2 text-slate-400 cursor-pointer select-none hover:text-indigo-600" onClick={() => clickSort("reels")} data-testid="sort-reels">Reels{sortArrow("reels")}</th>
                <th className="text-right px-2 py-2 text-slate-400 cursor-pointer select-none hover:text-indigo-600" onClick={() => clickSort("carousel")} data-testid="sort-carousel">Carousel{sortArrow("carousel")}</th>
                <th className="text-right px-2 py-2 text-slate-400 cursor-pointer select-none hover:text-indigo-600" onClick={() => clickSort("static")} data-testid="sort-static">Static{sortArrow("static")}</th>
                <th className="text-right px-2 py-2 text-slate-400 cursor-pointer select-none hover:text-indigo-600" onClick={() => clickSort("highlights")} data-testid="sort-highlights" title="Saved Highlights (permanent stories on the profile)">Highlights{sortArrow("highlights")}</th>
                <th className="text-right px-2 py-2 text-slate-400 cursor-pointer select-none hover:text-indigo-600" onClick={() => clickSort("live")} data-testid="sort-live" title="Live 24h stories captured on the latest daily run">Live 24h{sortArrow("live")}</th>
                <th className="text-right px-2 py-2 text-indigo-500" title="Manually-entered historic story count for this creator (stories posted before tracking started / tracked elsewhere). Counted in Matched/Total only when 'Historic stories in Total' is ON.">Historic Stories</th>
                <th className="text-right px-2 py-2 text-slate-400 cursor-pointer select-none hover:text-indigo-600" onClick={() => clickSort("er_reels")} data-testid="sort-er-reels" title="(avg likes per reel + avg comments per reel) / avg views per reel × 100">ER Reels{sortArrow("er_reels")}</th>
                <th className="text-right px-2 py-2 text-slate-400 cursor-pointer select-none hover:text-indigo-600" onClick={() => clickSort("er_static")} data-testid="sort-er-static" title="(avg likes per static post + avg comments per static post) / followers × 100">ER Static{sortArrow("er_static")}</th>
                <th className="text-right px-2 py-2 cursor-pointer select-none hover:text-indigo-600" onClick={() => clickSort("likes")} data-testid="sort-likes">Likes{sortArrow("likes")}</th>
                <th className="text-right px-2 py-2 cursor-pointer select-none hover:text-indigo-600" onClick={() => clickSort("comments")} data-testid="sort-comments">Comments{sortArrow("comments")}</th>
                <th className="text-right px-2 py-2 cursor-pointer select-none hover:text-indigo-600" onClick={() => clickSort("engagement")} data-testid="sort-engagement" title="Total engagement = all likes + all comments + all shares">Total Eng.{sortArrow("engagement")}</th>
                <th className="text-right px-2 py-2 text-slate-400 cursor-pointer select-none hover:text-indigo-600" onClick={() => clickSort("reach")} data-testid="sort-reach" title="Estimated reach — 16.4–18.9% of the creator's total views, deterministic per handle. Real reach needs creator Insights access.">Reach{sortArrow("reach")}</th>
                <th className="text-center px-3 py-2 text-slate-500 cursor-pointer select-none hover:text-indigo-600" onClick={() => clickSort("status")} data-testid="sort-status" title="Posted / Posted+Stories / Stories only / Nothing — same 4-way split as the bifurcation tiles above">Status{sortArrow("status")}</th>
              </tr>
            </thead>
            <tbody>
              {pagedSummary.map((s) => (
                <tr
                  key={s.handle}
                  className="border-b border-slate-200/40 data-row cursor-pointer hover:bg-slate-50"
                  onClick={() => setDrawerCreator(s)}
                  data-testid={`creator-row-${s.handle}`}
                >
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-2">
                      {s.profile_pic_url ? (
                        <img
                          src={s.profile_pic_url}
                          alt=""
                          referrerPolicy="no-referrer"
                          className="w-6 h-6 rounded-full object-cover border border-slate-200"
                        />
                      ) : (
                        <div className="w-6 h-6 rounded-full bg-gradient-to-br from-pink-500 to-pink-500 flex items-center justify-center text-[9px] font-bold text-black">
                          {s.handle?.[0]?.toUpperCase()}
                        </div>
                      )}
                      <div>
                        <div className="flex items-center gap-1.5">
                          <a
                            href={s.profile_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="text-slate-800 hover:text-indigo-600 font-medium"
                          >
                            @{s.handle}
                          </a>
                          {s.verified && <span className="text-blue-500 text-[10px]">✓</span>}
                        </div>
                        {s.owner_full_name && s.owner_full_name !== s.handle && (
                          <div className="text-[11px] text-slate-400 font-normal leading-tight">
                            {s.owner_full_name}
                          </div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="text-right font-mono text-slate-800">{formatNum(s.followers)}</td>
                  <td className="text-right font-mono text-slate-900 font-medium" title="Posts + Live 24h stories matched for this creator (highlights excluded)">
                    {(s.matched_total !== undefined ? s.matched_total : (((s.collab_posts || 0) + (s.reels || 0) + (s.carousels || 0) + (s.static || 0) + (s.videos || 0) + (s.stories_live || 0))))}
                  </td>
                  <td className="text-right font-mono text-orange-600">
                    <CollabHover handle={s.handle} count={s.collab_posts || 0} posts={posts} />
                  </td>
                  <td className="text-right font-mono text-pink-600">{s.reels}</td>
                  <td className="text-right font-mono text-violet-600">{s.carousels}</td>
                  <td className="text-right font-mono text-emerald-600">{s.static}</td>
                  <td
                    className="text-right font-mono text-fuchsia-600"
                    data-testid={`row-highlights-${s.handle}`}
                    title={`${s.stories_highlights || 0} saved Highlights on @${s.handle}`}
                  >{s.stories_highlights || 0}</td>
                  <td
                    className="text-right font-mono text-red-600"
                    data-testid={`row-live-${s.handle}`}
                    title={`${s.stories_live || 0} Live 24h stories on @${s.handle} (last run)`}
                  >{s.stories_live || 0}</td>
                  <td
                    className="text-right font-mono text-slate-700"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <input
                      type="number"
                      min="0"
                      defaultValue={s.custom_stories || ""}
                      placeholder="0"
                      data-testid={`custom-stories-input-${s.handle}`}
                      className="w-16 text-right border border-slate-200 rounded-md px-1.5 py-0.5 text-xs focus-brand"
                      title="Historic story count for this creator — saves on blur/Enter"
                      onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
                      onBlur={(e) => {
                        const n = Math.max(0, parseInt(e.target.value || "0", 10) || 0);
                        if (n !== (s.custom_stories || 0)) {
                          saveCustomStories({ ...customStories, [s.handle]: n }, includeCustom);
                        }
                      }}
                    />
                  </td>
                  <td className="text-right font-mono text-pink-600">
                    {s.engagement_rate_reels != null ? `${s.engagement_rate_reels}%` : "—"}
                  </td>
                  <td className="text-right font-mono text-emerald-600">
                    {s.engagement_rate_static != null ? `${s.engagement_rate_static}%` : "—"}
                  </td>
                  <td className="text-right font-mono text-slate-700">{formatNum(s.total_likes)}</td>
                  <td className="text-right font-mono text-slate-700">{formatNum(s.total_comments)}</td>
                  <td className="text-right font-mono text-slate-800 font-medium" data-testid={`row-engagement-${s.handle}`}>{formatNum(Number(s.total_likes ?? 0) + Number(s.total_comments ?? 0) + (s.shares == null ? 0 : Number(s.shares)))}</td>
                  <td className="text-right font-mono text-slate-500 tabular-nums" title={s.reach_pct ? `est. ${s.reach_pct}% of views` : "no video data"}>
                    {formatNum(s.reach || 0)}
                  </td>
                  <td className="px-3 py-1 text-center">
                    <CreatorStatusBadge creator={s} testId={`row-status-${s.handle}`} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {creatorPageCount > 1 && (
            <div className="flex items-center justify-between px-3 py-2 border-t border-slate-200 text-[11px] font-mono text-slate-500" data-testid="creators-pager">
              <button
                disabled={safeCreatorPage === 0}
                onClick={() => setCreatorPage(safeCreatorPage - 1)}
                className="px-2.5 py-1 rounded-md border border-slate-200 disabled:opacity-40 hover:border-indigo-300 hover:text-indigo-600"
                data-testid="creators-prev-btn"
              >← Prev</button>
              <span data-testid="creators-page-info">
                Page {safeCreatorPage + 1} / {creatorPageCount} · showing {pagedSummary.length} of {summary.length} creators
              </span>
              <button
                disabled={safeCreatorPage >= creatorPageCount - 1}
                onClick={() => setCreatorPage(safeCreatorPage + 1)}
                className="px-2.5 py-1 rounded-md border border-slate-200 disabled:opacity-40 hover:border-indigo-300 hover:text-indigo-600"
                data-testid="creators-next-btn"
              >Next →</button>
            </div>
          )}
          <div className="p-2.5 border-t border-slate-200 text-[10px] font-mono text-slate-400 flex items-center gap-4 flex-wrap">
            <span><span className="text-pink-600">ER Reels</span> = (avg likes/Reel + avg comments/Reel) / avg views/Reel × 100</span>
            <span><span className="text-emerald-600">ER Static</span> = (avg likes/Static + avg comments/Static) / followers × 100</span>
            <span className="text-orange-600">Collab</span>
            <span>= posts owned by another account but co-authored by this handle (still counted here, not the owner)</span>
          </div>
        </div>
      )}

      {/* Posts list preview */}
      {subTab === "content" && posts.length === 0 && (
        <div className="el-card p-10 text-center text-xs font-mono text-slate-400" data-testid="content-empty">
          No posts found matching the date range and keyword criteria.
        </div>
      )}
      {subTab === "content" && posts.length > 0 && (() => {
        const visiblePosts = posts.filter((p) => {
          if (collabFilter === "owned") return !p.is_collab;
          if (collabFilter === "collab") return p.is_collab;
          return true;
        });
        // 100 rows per page — all rows reachable via the pager, nothing
        // dropped; initial render stays under the 3s budget on big reports.
        const CONTENT_PAGE_SIZE = 100;
        const contentPageCount = Math.max(1, Math.ceil(visiblePosts.length / CONTENT_PAGE_SIZE));
        const safeContentPage = Math.min(contentPage, contentPageCount - 1);
        const pagedPosts = visiblePosts.slice(safeContentPage * CONTENT_PAGE_SIZE, (safeContentPage + 1) * CONTENT_PAGE_SIZE);
        return (
        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid="filtered-posts-preview">
          <div className="px-3 py-2 border-b border-slate-200 flex items-center gap-3 flex-wrap">
            <span className="text-[10px] font-mono uppercase tracking-widest text-slate-400 flex items-center gap-1.5">
              <ImageIcon size={10} /> Matched posts · {visiblePosts.length}
              {collabFilter !== "all" && (
                <span className="text-slate-400">/ {posts.length}</span>
              )}
            </span>
            <div className="ml-auto flex gap-1 bg-white border border-slate-200 rounded-md p-0.5" data-testid="collab-filter">
              {[
                { id: "all", label: "All" },
                { id: "owned", label: "Owned" },
                { id: "collab", label: "Collab", accent: "text-orange-600" },
              ].map((f) => (
                <button
                  key={f.id}
                  onClick={() => { setCollabFilter(f.id); setContentPage(0); }}
                  className={`px-2.5 py-1 rounded text-[11px] font-medium transition-all ${
                    collabFilter === f.id
                      ? f.id === "collab"
                        ? "bg-orange-500 text-black"
                        : "bg-slate-900 text-white"
                      : `bg-transparent ${f.accent || "text-slate-500"} hover:text-pink-600`
                  }`}
                  data-testid={`collab-filter-${f.id}`}
                >
                  {f.label}
                  {f.id === "collab" && posts.filter((p) => p.is_collab).length > 0 && (
                    <span className={`ml-1 text-[9px] ${
                      collabFilter === f.id ? "text-black/70" : "text-orange-600"
                    }`}>
                      {posts.filter((p) => p.is_collab).length}
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>
          <div className="max-h-[36rem] overflow-y-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-[10px] uppercase tracking-widest font-mono text-slate-400 border-b border-slate-200 sticky top-0 bg-white">
                  <th className="text-left px-3 py-2">Handle</th>
                  <th className="text-left px-2 py-2">Type</th>
                  <th className="text-left px-2 py-2">Caption</th>
                  <th className="text-right px-2 py-2">Likes</th>
                  <th className="text-right px-2 py-2">Comments</th>
                  <th className="text-right px-2 py-2">Views</th>
                  <th className="text-right px-2 py-2">Shares</th>
                  <th className="text-left px-2 py-2">Date</th>
                  <th className="text-right px-3 py-2">Link</th>
                </tr>
              </thead>
              <tbody>
                {pagedPosts.map((p, i) => (
                  <tr
                    key={`${p.shortcode || i}-${p.handle || ""}-${p.is_collab ? "c" : "o"}`}
                    className={`border-b border-slate-200/40 data-row ${
                      p.is_collab ? "bg-orange-500/[0.04]" : ""
                    }`}
                    data-testid={`filtered-post-row-${i}`}
                  >
                    <td className="px-3 py-2 text-slate-800">
                      <div>
                        <div className="flex items-center gap-1.5">
                          <span className="font-medium">@{p.handle}</span>
                          {p.is_collab && (
                            <span
                              className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[9px] font-mono font-semibold uppercase tracking-wider bg-orange-500/15 text-orange-600 border border-orange-500/40"
                              title={`Collab — actually owned by @${p.original_owner}. Matched via: ${(p.matched_keywords || []).join(", ") || "n/a"}`}
                              data-testid={`collab-tag-${i}`}
                            >
                              <Users size={9} /> Collab
                            </span>
                          )}
                        </div>
                        {p.owner_full_name && p.owner_full_name !== p.handle && (
                          <div className="text-[11px] text-slate-400 font-normal leading-tight">
                            {p.owner_full_name}
                          </div>
                        )}
                      </div>
                      {p.is_collab && p.original_owner && (
                        <a
                          href={`https://www.instagram.com/${p.original_owner}/`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-[10px] font-mono text-orange-600/70 hover:text-orange-600"
                        >
                          ← owned by @{p.original_owner}
                        </a>
                      )}
                    </td>
                    <td className="px-2 py-2 text-slate-700 font-mono">{p.post_type}</td>
                    <td className="px-2 py-2 text-slate-500 max-w-[300px] truncate">
                      {p.caption || <span className="text-slate-400 italic">no caption</span>}
                    </td>
                    <td className="text-right px-2 font-mono text-pink-600">{formatNum(p.likes)}</td>
                    <td className="text-right px-2 font-mono text-violet-600">{formatNum(p.comments)}</td>
                    <td className="text-right px-2 font-mono text-slate-600 tabular-nums">
                      {p.video_views || p.video_plays ? formatNum(p.video_views || p.video_plays) : <span className="text-slate-300">—</span>}
                    </td>
                    <td className="text-right px-2 font-mono text-slate-600 tabular-nums">
                      {p.shares != null ? formatNum(p.shares) : <span className="text-slate-300">—</span>}
                    </td>
                    <td className="px-2 py-2 text-slate-500 font-mono tabular-nums">
                      {p.post_date ? new Date(p.post_date).toLocaleDateString() : "—"}
                    </td>
                    <td className="px-3 text-right">
                      <a href={p.post_url} target="_blank" rel="noopener noreferrer" className="text-pink-600 hover:text-pink-600">
                        <ExternalLink size={12} />
                      </a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {visiblePosts.length === 0 && collabFilter !== "all" && (
            <div className="p-6 text-center text-xs text-slate-400 font-mono">
              No {collabFilter === "collab" ? "collab" : "owned"} posts in this range.
            </div>
          )}
          {contentPageCount > 1 && (
            <div className="flex items-center justify-between px-3 py-2 border-t border-slate-200 text-[11px] font-mono text-slate-500" data-testid="content-pager">
              <button
                disabled={safeContentPage === 0}
                onClick={() => setContentPage(safeContentPage - 1)}
                className="px-2.5 py-1 rounded-md border border-slate-200 disabled:opacity-40 hover:border-indigo-300 hover:text-indigo-600"
                data-testid="content-prev-btn"
              >← Prev</button>
              <span data-testid="content-page-info">
                Page {safeContentPage + 1} / {contentPageCount} · showing {pagedPosts.length} of {visiblePosts.length}
              </span>
              <button
                disabled={safeContentPage >= contentPageCount - 1}
                onClick={() => setContentPage(safeContentPage + 1)}
                className="px-2.5 py-1 rounded-md border border-slate-200 disabled:opacity-40 hover:border-indigo-300 hover:text-indigo-600"
                data-testid="content-next-btn"
              >Next →</button>
            </div>
          )}
        </div>
        );
      })()}

      {subTab === "performers" && <TopPerformers summary={summary} posts={posts} reportId={reportId} />}

      {job?.status === "failed" && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-xs text-red-600">
          {job.error || "Unknown error"}
        </div>
      )}

      {/* Right-side drawer: click any creator row on the Creators sub-tab to
          open a full breakdown (per-day chart + post list with links). */}
      <CreatorDrawer
        open={!!drawerCreator}
        onClose={() => setDrawerCreator(null)}
        creator={drawerCreator}
        posts={posts}
        storyRows={storyRows}
      />
    </div>
  );
}


function StatMini({ label, value, icon: Icon, accent, testId, sub }) {
  const color =
    accent === "amber"
      ? "text-pink-600"
      : accent === "pink"
        ? "text-pink-600"
        : accent === "emerald"
          ? "text-emerald-600"
          : accent === "fuchsia"
            ? "text-fuchsia-600"
            : accent === "orange"
              ? "text-orange-600"
              : accent === "cyan"
                ? "text-cyan-700"
                : "text-violet-600";
  const softBg =
    accent === "emerald" ? "bg-emerald-50"
      : accent === "fuchsia" ? "bg-fuchsia-50"
        : accent === "orange" ? "bg-orange-50"
          : accent === "cyan" ? "bg-cyan-50"
            : accent === "violet" ? "bg-violet-50"
              : "bg-pink-50";
  return (
    <div
      className="el-tile px-3.5 py-3 flex items-center justify-between"
      data-testid={testId || `stat-${String(label).toLowerCase()}`}
    >
      <div>
        <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">
          {label}
        </div>
        <div
          className="text-xl font-medium font-mono tracking-tight text-slate-900 tabular-nums mt-1"
          data-testid={`stat-${String(label).toLowerCase()}-value`}
        >{value}</div>
        {sub && (
          <div className="text-[10px] text-slate-400 mt-1 leading-tight">{sub}</div>
        )}
      </div>
      <div className={`p-2 rounded-lg ${softBg}`}>
        <Icon size={14} className={color} />
      </div>
    </div>
  );
}

function formatNum(n) {
  if (n == null) return "0";
  const num = Number(n);
  if (num >= 1e6) return (num / 1e6).toFixed(1) + "M";
  if (num >= 1e3) return (num / 1e3).toFixed(1) + "K";
  return String(num);
}

// ---------------------------------------------------------------------------
// RapidApiQuotaBadge — polls /api/quota/rapidapi every 60s and shows a small
// pill "RapidAPI · X of Y used" with a colored dot (green→amber→red as usage
// climbs). Silently renders nothing until the first RapidAPI call has been
// made, so Apify-only users never see this widget.
// ---------------------------------------------------------------------------
function RapidApiQuotaBadge() {
  const [quota, setQuota] = useState(null);

  useEffect(() => {
    let live = true;
    const load = async () => {
      try {
        const { data } = await axios.get(`${API}/quota/rapidapi`);
        if (live) setQuota(data?.tracked ? data : null);
      } catch (e) {
        if (process.env.NODE_ENV !== "production") console.error("RapidAPI quota poll failed:", e);
      }
    };
    load();
    const id = setInterval(load, 60_000);
    return () => { live = false; clearInterval(id); };
  }, []);

  if (!quota || quota.limit == null || quota.remaining == null) return null;

  const used = Number(quota.used ?? Math.max(0, quota.limit - quota.remaining));
  const limit = Number(quota.limit);
  const pct = limit > 0 ? Math.min(100, (used / limit) * 100) : 0;
  const dot =
    pct >= 90 ? "bg-red-400"
    : pct >= 70 ? "bg-slate-900"
    : "bg-emerald-400";
  const barColor =
    pct >= 90 ? "bg-red-500/70"
    : pct >= 70 ? "bg-slate-900"
    : "bg-emerald-500/70";

  // resets_in — human friendly
  let resetsIn = "";
  if (quota.reset_at) {
    const dt = new Date(quota.reset_at);
    const diffMs = dt.getTime() - Date.now();
    if (diffMs > 0) {
      const days = Math.floor(diffMs / 86_400_000);
      const hrs = Math.floor((diffMs % 86_400_000) / 3_600_000);
      resetsIn = days >= 1 ? `resets in ${days}d ${hrs}h` : `resets in ${hrs}h`;
    }
  }

  return (
    <div
      className="mt-3 mb-6 flex items-center gap-3 rounded-lg border border-slate-200 bg-white px-3 py-2"
      data-testid="rapidapi-quota-badge"
    >
      <span className={`inline-block w-2 h-2 rounded-full ${dot}`} />
      <span className="text-[10px] font-mono uppercase tracking-widest text-slate-500">
        RapidAPI · plan quota
      </span>
      <div className="flex-1 max-w-[240px] h-1.5 rounded-full bg-slate-50 overflow-hidden">
        <div
          className={`h-full ${barColor} transition-all`}
          style={{ width: `${pct}%` }}
        />
      </div>
      <span className="text-[11px] font-mono text-slate-800" data-testid="rapidapi-quota-usage">
        {used.toLocaleString()} <span className="text-slate-400">of</span>{" "}
        {limit.toLocaleString()}
      </span>
      {resetsIn && (
        <span className="text-[10px] font-mono text-slate-400 hidden sm:inline">· {resetsIn}</span>
      )}
    </div>
  );
}

export default function Reports() {
  const { isAdmin } = useAuth();
  const [reports, setReports] = useState([]);
  const [groups, setGroups] = useState([]);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cookieOpen, setCookieOpen] = useState(false);
  const [compareOpen, setCompareOpen] = useState(false);
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [urlFetchOpen, setUrlFetchOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [detailTick, setDetailTick] = useState(0);
  const [detailTab, setDetailTab] = useState("overview");
  // Always land on the Overview tab when a different report is opened.
  useEffect(() => { setDetailTab("overview"); }, [selectedId]);

  useEffect(() => {
    loadReports();
    loadGroups();
    const interval = setInterval(loadReports, 8000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadReports = async () => {
    try {
      const { data } = await axios.get(`${API}/reports`);
      const list = data.reports || [];
      // Cheap fingerprint (id + updated_at/last_run_at/last_job_id) so the
      // 8s poll skips setState when nothing meaningful changed — a full
      // JSON.stringify of the whole payload was the biggest source of
      // main-thread lag with 50+ reports.
      const fp = list.map((r) =>
        `${r.id}|${r.last_run_at || ""}|${r.last_job_id || ""}|${r.updated_at || ""}|${r.enabled ? 1 : 0}`
      ).join(",");
      setReports((prev) => {
        const prevFp = prev.map((r) =>
          `${r.id}|${r.last_run_at || ""}|${r.last_job_id || ""}|${r.updated_at || ""}|${r.enabled ? 1 : 0}`
        ).join(",");
        return prevFp === fp ? prev : list;
      });
    } catch (e) {
      if (process.env.NODE_ENV !== "production") console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const loadGroups = async () => {
    try {
      const { data } = await axios.get(`${API}/groups`);
      setGroups(data.groups || []);
    } catch (e) {
      if (process.env.NODE_ENV !== "production") console.error("Failed to load groups:", e);
    }
  };

  const runNow = async (id, forceRefresh = false, mode = null) => {
    try {
      const qs = new URLSearchParams();
      if (forceRefresh) qs.set("force_refresh", "true");
      if (mode) qs.set("mode", mode);
      const suffix = qs.toString() ? `?${qs.toString()}` : "";
      await axios.post(`${API}/reports/${id}/run${suffix}`);
      const modeLabel = mode ? ` (${mode} only)` : "";
      toast.success(
        forceRefresh
          ? `Running now (cache bypassed)${modeLabel}…`
          : `Running now${modeLabel}…`
      );
      setTimeout(loadReports, 1200);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not run report");
    }
  };

  const clearStuck = async (id) => {
    try {
      const { data } = await axios.post(`${API}/reports/${id}/cancel-queued`);
      toast.success(data.cancelled ? `Cleared ${data.cancelled} stuck refresh(es)` : "No stuck refreshes on this report");
      setTimeout(loadReports, 500);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not clear stuck refreshes");
    }
  };

  const toggleEnabled = async (r) => {
    try {
      await axios.put(`${API}/reports/${r.id}`, { enabled: !r.enabled });
      toast.success(r.enabled ? "Auto-refresh paused" : "Auto-refresh resumed");
      loadReports();
    } catch (e) {
      toast.error("Could not update");
    }
  };

  const deleteReport = async (r) => {
    if (!window.confirm(`Delete report "${r.name}"? This can't be undone.`)) return;
    try {
      await axios.delete(`${API}/reports/${r.id}`);
      toast.success(`Deleted "${r.name}"`);
      if (selectedId === r.id) setSelectedId(null);
      loadReports();
    } catch (e) {
      toast.error("Could not delete");
    }
  };

  const selected = reports.find((r) => r.id === selectedId);
  const activeCount = reports.filter((r) => r.enabled).length;
  const totalHandles = reports.reduce((n, r) => n + (r.handles?.length || 0), 0);

  return (
    <div className="min-h-screen bg-[#F8FAFC] relative">
      <div className="relative z-10 max-w-[1400px] mx-auto p-4 sm:p-6 lg:p-8">
        {!selected && (
          <div className="fade-in-up">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h1 className="text-2xl font-semibold tracking-tight text-slate-900">
                  Reports
                </h1>
                <p className="text-xs font-mono text-slate-400 uppercase tracking-widest mt-1">
                  Saved keyword tracking · Auto-refresh daily at 10:00 IST
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  onClick={async () => {
                    if (!window.confirm(
                      "Reconcile all reports so Dashboard, Summary, and Excel/CSV exports show the SAME counts on every report? Zero cost — read-only recompute."
                    )) return;
                    const t = toast.loading("Reconciling all reports…");
                    try {
                      const { data } = await axios.post(`${API}/org/reconcile-report-counts`);
                      const inconsistent = (data.reports || []).filter((r) => r.consistent === false).length;
                      const g = data.grand_total || {};
                      toast.success(
                        `Reconciled ${data.reports_scanned} report(s) — Reels ${g.reels || 0} · Carousels ${g.carousels || 0} · Static ${g.static || 0} · Videos ${g.videos || 0} · Live ${g.live || 0}${inconsistent ? ` · ${inconsistent} still off (open each to inspect)` : " · all consistent"}`,
                        { id: t, duration: 8000 }
                      );
                      loadReports();
                    } catch (e) {
                      toast.error(e.response?.data?.detail || "Reconcile failed", { id: t });
                    }
                  }}
                  variant="outline"
                  className="bg-white border-slate-200 hover:bg-slate-100 hover:border-slate-300 hover:text-slate-900 text-slate-700 font-semibold rounded-xl"
                  title="Fix ALL reports: dashboard counts = summary totals = Excel/CSV exports. Collab is an attribute, not a separate count."
                  data-testid="reconcile-counts-btn"
                >
                  <RefreshCw size={13} className="mr-1.5" />
                  Fix Report Counts
                </Button>
                <Button
                  onClick={async () => {
                    if (!window.confirm(
                      "Switch ALL reports to count only stories that mention your track terms (keywords/hashtags/tags)? Existing numbers stay exactly as they are — only future runs change."
                    )) return;
                    try {
                      const { data } = await axios.post(`${API}/org/story-settings/matched-only`);
                      toast.success(
                        data.reports_fixed
                          ? `Done — ${data.reports_fixed} report(s) now count matched stories only`
                          : "All reports already count matched stories only"
                      );
                    } catch (e) {
                      toast.error(e.response?.data?.detail || "Story setting fix failed");
                    }
                  }}
                  variant="outline"
                  className="bg-white border-slate-200 hover:bg-slate-100 hover:border-slate-300 hover:text-slate-900 text-slate-700 font-semibold rounded-xl"
                  title="Count only keyword-matched stories on every report (keeps existing numbers)"
                  data-testid="fix-story-counts-btn"
                >
                  <Radio size={13} className="mr-1.5" />
                  Fix Story Counts
                </Button>
                <Button
                  onClick={async () => {
                    if (!window.confirm(
                      "Scan ALL reports and delete every stored post outside each report's date window? This cannot be undone."
                    )) return;
                    try {
                      const { data } = await axios.post(`${API}/org/purge-out-of-window-all`);
                      const affected = (data.reports || []).filter((r) => r.removed > 0).length;
                      toast.success(
                        data.removed
                          ? `Cleanup done — scanned ${data.posts_checked} post(s) across ${data.reports_checked} report(s), removed ${data.removed} out-of-range post(s) from ${affected} report(s)`
                          : `Scanned ${data.posts_checked} post(s) across ${data.reports_checked} report(s) — everything is already inside its date window`
                      );
                    } catch (e) {
                      toast.error(e.response?.data?.detail || "Cleanup failed");
                    }
                  }}
                  variant="outline"
                  className="bg-white border-slate-200 hover:bg-slate-100 hover:border-slate-300 hover:text-slate-900 text-slate-700 font-semibold rounded-xl"
                  title="Bulk-remove posts outside every report's date window"
                  data-testid="purge-all-btn"
                >
                  <Trash2 size={13} className="mr-1.5" />
                  Cleanup Old Posts
                </Button>
                <Button
                  onClick={() => setWizardOpen(true)}
                  className="bg-slate-900 hover:bg-slate-800 text-white font-semibold rounded-xl el-btn-primary"
                  data-testid="new-report-button"
                >
                  <Plus size={15} className="mr-1.5" />
                  New Report
                </Button>
              </div>
            </div>

            {/* RapidAPI live credit counter — SUPER-ADMIN ONLY (shared
                cross-tenant resource; belongs on the admin console). */}
            {isAdmin && <RapidApiQuotaBadge />}
            <RapidApiConnectionBanner />

            {/* Overview strip */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6" data-testid="reports-overview">
              {[
                { label: "Total Reports", value: reports.length, testid: "overview-total" },
                { label: "Auto-refreshing", value: activeCount, testid: "overview-active" },
                { label: "Paused", value: reports.length - activeCount, testid: "overview-paused" },
                { label: "Handles Tracked", value: totalHandles, testid: "overview-handles" },
              ].map((t) => (
                <div
                  key={t.testid}
                  className="el-tile px-4 py-3"
                  data-testid={t.testid}
                >
                  <div className="text-[10px] font-bold uppercase tracking-widest text-slate-400 font-mono">
                    {t.label}
                  </div>
                  <div className="text-2xl font-semibold tracking-tight text-slate-900 mt-1">
                    {t.value}
                  </div>
                </div>
              ))}
            </div>

            {/* Reports table */}
            <div
              className="bg-white border border-slate-200/80 rounded-2xl overflow-hidden shadow-[0_1px_3px_rgba(0,0,0,0.02)]"
              data-testid="report-list"
            >
              <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
                <h2 className="text-sm font-semibold text-slate-900">Reports List</h2>
                <span className="text-[10px] font-mono uppercase tracking-widest text-slate-400">
                  {reports.length} total
                </span>
              </div>
            {loading && (
              <div className="text-xs text-slate-400 font-mono p-4">Loading…</div>
            )}
            {!loading && reports.length === 0 && (
              <div className="bg-white border border-dashed border-slate-200 rounded-xl p-8 text-center">
                <FileText size={26} className="mx-auto text-pink-600 mb-3" />
                <h3 className="text-slate-800 font-semibold mb-1">No reports yet</h3>
                <p className="text-xs text-slate-400 mb-4">
                  Create your first report to start tracking keywords across handles.
                </p>
                <Button
                  onClick={() => setWizardOpen(true)}
                  className="bg-slate-900 hover:bg-slate-800 text-white font-semibold"
                  data-testid="empty-new-report"
                >
                  <Plus size={13} className="mr-1.5" />
                  Create Report
                </Button>
              </div>
            )}
            {!loading && reports.length > 0 && (
              <div className="overflow-x-auto">
                <table className="w-full text-left text-xs">
                  <thead>
                    <tr className="border-b border-slate-200 bg-slate-50/50 text-slate-400 font-mono uppercase text-[10px] tracking-wider">
                      <th className="px-5 py-3 font-medium">Name</th>
                      <th className="px-3 py-3 font-medium">Status</th>
                      <th className="px-3 py-3 font-medium">Scope</th>
                      <th className="px-3 py-3 font-medium">Timeline</th>
                      <th className="px-3 py-3 font-medium">Last Run</th>
                      <th className="px-3 py-3 font-medium">Created</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reports.map((r, i) => (
                      <tr
                        key={r.id}
                        onClick={() => setSelectedId(r.id)}
                        className="border-b border-slate-100 hover:bg-slate-50/60 transition-colors cursor-pointer h-14"
                        data-testid={`report-item-${r.id.slice(0, 8)}`}
                      >
                        <td className="px-5 py-3">
                          <div className="flex items-center gap-3">
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${AVATAR_COLORS[i % AVATAR_COLORS.length]}`}>
                              {(r.name || "?")[0].toUpperCase()}
                            </div>
                            <div className="min-w-0">
                              <div className="text-sm font-semibold text-slate-900 truncate max-w-[240px]">
                                {r.name}
                              </div>
                              <div className="text-[10px] font-mono text-slate-400 mt-0.5">
                                {r.report_type === "urls"
                                  ? "URL batch"
                                  : `${r.handles.length} handles · ${r.keywords.length} keywords`}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-3 py-3">
                          {r.enabled ? (
                            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-medium bg-emerald-50 text-emerald-700 border border-emerald-200">
                              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                              Active
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-medium bg-slate-100 text-slate-500 border border-slate-200">
                              <span className="w-1.5 h-1.5 rounded-full bg-slate-400" />
                              Paused
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-3 font-mono text-[11px] text-slate-500">
                          {r.report_type === "urls"
                            ? "URLs"
                            : r.include_stories
                            ? "Posts + Stories"
                            : "Posts"}
                        </td>
                        <td className="px-3 py-3 font-mono text-[11px] text-slate-500">
                          {r.since_date || r.from_date
                            ? `${r.since_date || r.from_date}${r.to_date ? ` → ${r.to_date}` : " →"}`
                            : "All time"}
                        </td>
                        <td className="px-3 py-3 font-mono text-[11px] text-slate-500">
                          {r.last_run_at
                            ? new Date(r.last_run_at).toLocaleDateString()
                            : "Never"}
                        </td>
                        <td className="px-3 py-3 font-mono text-[11px] text-slate-500">
                          {new Date(r.created_at).toLocaleDateString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            </div>
          </div>
        )}

        {selected && (
          <div className="fade-in-up">
            {/* Top bar — back, title + badges, actions */}
            <div className="flex items-start justify-between gap-4 flex-wrap mb-5">
              <div className="flex items-center gap-3 min-w-0">
                <button
                  onClick={() => setSelectedId(null)}
                  className="p-2 rounded-xl bg-white border border-slate-200 text-slate-500 hover:text-slate-900 hover:bg-slate-50 transition-colors shrink-0"
                  data-testid="report-back-btn"
                  title="Back to all reports"
                >
                  <ArrowLeft size={15} />
                </button>
                <div className="min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h2 className="text-xl font-bold text-slate-900">{selected.name}</h2>
                        {selected.enabled ? (
                          <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 text-[11px] font-semibold">
                            Auto-refresh
                          </Badge>
                        ) : (
                          <Badge className="bg-slate-100 text-slate-500 border-slate-200 text-[11px] font-semibold">
                            Paused
                          </Badge>
                        )}
                        {(selected.provider === "rapidapi") ? (
                          <Badge className="bg-slate-100 text-slate-600 border-slate-200 text-[11px] font-semibold" data-testid="badge-provider-rapidapi">
                            <Zap size={9} className="mr-1" /> RapidAPI
                          </Badge>
                        ) : (
                          <Badge className="bg-slate-100 text-slate-600 border-slate-200 text-[11px] font-semibold" data-testid="badge-provider-apify">
                            Apify
                          </Badge>
                        )}
                        {selected.include_stories && (
                          <Badge className="bg-slate-100 text-slate-600 border-slate-200 text-[11px] font-semibold">
                            <Radio size={9} className="mr-1" /> Stories · auto-tracked daily
                          </Badge>
                        )}
                        {selected.include_stories && selected.stories_only && (
                          <Badge className="bg-slate-100 text-slate-600 border-slate-200 text-[11px] font-semibold" data-testid="badge-stories-only">
                            <Zap size={9} className="mr-1" /> Stories-only
                          </Badge>
                        )}
                        {selected.include_stories && !selected.stories_only && selected.fetch_highlights !== false && (
                          <Badge className="bg-slate-100 text-slate-600 border-slate-200 text-[11px] font-semibold" data-testid="badge-fetch-highlights">
                            <Bookmark size={9} className="mr-1" /> Highlights
                          </Badge>
                        )}
                        {selected.include_stories && selected.fetch_live !== false && (
                          <Badge className="bg-slate-100 text-slate-600 border-slate-200 text-[11px] font-semibold" data-testid="badge-fetch-live">
                            <Radio size={9} className="mr-1" /> Live 24h
                          </Badge>
                        )}
                        {selected.include_stories && selected.fetch_live !== false && !selected.has_session_cookie && (
                          <Badge
                            className="bg-red-50 text-red-600 border-red-200 text-[11px] font-semibold cursor-pointer hover:bg-red-100"
                            data-testid="badge-live-cookie-missing"
                            onClick={() => setCookieOpen(true)}
                            title="Live 24h stories can't be fetched without a session cookie (Instagram requirement). Click to add one."
                          >
                            <ShieldAlert size={9} className="mr-1" /> Live SKIPPED · Add cookie
                          </Badge>
                        )}
                        {selected.include_stories && selected.stories_match_any && (
                          <Badge
                            className="bg-slate-100 text-slate-600 border-slate-200 text-[11px] font-semibold"
                            data-testid="badge-stories-match-any"
                          >
                            All stories
                          </Badge>
                        )}
                      </div>
                      <div className="text-[11px] font-mono text-slate-400">
                        Created {new Date(selected.created_at).toLocaleString()} · Daily 10:00 IST
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                      {/* Segmented refresh control — one-shot mode override.
                          Shown for handle reports AND for URL-mode reports
                          once handles have been added (hybrid mode). */}
                      {(selected.report_type !== "urls" || (selected.handles || []).length > 0) && (
                      <>
                      <div className="flex items-stretch rounded-md overflow-hidden border border-slate-200" data-testid="report-refresh-modes">
                        <Button
                          onClick={() => runNow(selected.id, false, "posts")}
                          variant="outline"
                          className="bg-white border-0 border-r border-slate-200 hover:bg-slate-100 hover:text-slate-900 text-slate-800 h-9 rounded-none px-3"
                          data-testid="report-run-posts"
                          title="Refresh POSTS only (Reels + Carousels + Static + Videos). Skips stories → fewer credits."
                        >
                          <Film size={12} className="mr-1.5" />
                          Posts
                        </Button>
                        <Button
                          onClick={() => runNow(selected.id, false, "stories")}
                          variant="outline"
                          className="bg-white border-0 border-r border-slate-200 hover:bg-slate-100 hover:text-slate-900 text-slate-800 h-9 rounded-none px-3"
                          data-testid="report-run-stories"
                          title="Refresh STORIES only (Highlights + Live 24h). Skips posts → fastest option."
                        >
                          <Radio size={12} className="mr-1.5" />
                          Stories
                        </Button>
                        <Button
                          onClick={() => runNow(selected.id, false, "both")}
                          variant="outline"
                          className="bg-white border-0 hover:bg-slate-100 hover:text-slate-900 text-slate-800 h-9 rounded-none px-3"
                          data-testid="report-run-both"
                          title="Refresh BOTH posts and stories in one job."
                        >
                          <Sparkles size={12} className="mr-1.5" />
                          Both
                        </Button>
                      </div>
                      <Button
                        onClick={() => {
                          if (window.confirm("Force refresh will bypass the handle-cache and re-hit RapidAPI for every fetch. This burns extra credits. Continue?")) {
                            runNow(selected.id, true, "both");
                          }
                        }}
                        variant="outline"
                        title="Force refresh (bypass cache) — runs BOTH posts and stories"
                        className="bg-white border-slate-200 hover:bg-red-50 hover:border-red-200 hover:text-red-600 text-slate-800 h-9 px-2"
                        data-testid="report-run-now-force"
                      >
                        <RefreshCcw size={13} />
                      </Button>
                      <Button
                        onClick={() => clearStuck(selected.id)}
                        variant="outline"
                        title="Clear stuck refreshes — cancels any queued/running refresh on this report so a new one can start"
                        className="bg-white border-slate-200 hover:bg-amber-50 hover:border-amber-200 hover:text-slate-900 text-slate-800 h-9 px-2 text-xs"
                        data-testid="report-clear-stuck-btn"
                      >
                        Clear stuck
                      </Button>
                      </>
                      )}
                      {/* Schedule is available on EVERY report — URL reports
                          included — so URL-fetched reports with handles added
                          later can still be put on a daily schedule. */}
                      <Button
                        onClick={() => setScheduleOpen(true)}
                        variant="outline"
                        className="bg-white border-slate-200 hover:bg-slate-100 hover:border-slate-300 hover:text-slate-900 text-slate-800 h-9"
                        data-testid="report-schedule-btn"
                        title="Configure per-report schedule + cache TTL (works for URL reports too — add handles in Edit to enable tracking)"
                      >
                        <Clock size={13} className="mr-1.5" />
                        Schedule
                      </Button>
                      <Button
                        onClick={() => setUrlFetchOpen(true)}
                        variant="outline"
                        className="bg-white border-slate-200 hover:bg-slate-100 hover:border-slate-300 hover:text-slate-900 text-slate-800 h-9"
                        data-testid="report-fetch-urls-btn"
                        title="Fetch specific posts by URL (RapidAPI or Apify — Apify adds Shares + Reposts)"
                      >
                        <ExternalLink size={13} className="mr-1.5" />
                        Fetch URLs
                      </Button>
                      <Button
                        onClick={() => setCompareOpen(true)}
                        variant="outline"
                        className="bg-white border-slate-200 hover:bg-slate-100 hover:border-slate-300 hover:text-slate-900 text-slate-800 h-9"
                        data-testid="report-compare"
                      >
                        <GitCompareArrows size={13} className="mr-1.5" />
                        Compare
                      </Button>
                      <Button
                        onClick={() => setCookieOpen(true)}
                        variant="outline"
                        className={`h-9 border-slate-200 ${
                          selected.has_session_cookie
                            ? (selected.cookie_age_days >= 25
                              ? "bg-red-50 border-red-200 text-red-600 hover:bg-red-100"
                              : "bg-pink-50 border-pink-200 text-pink-600 hover:bg-pink-100")
                            : "bg-white hover:bg-slate-100 hover:border-slate-300 hover:text-pink-600 text-slate-800"
                        }`}
                        data-testid="report-cookie-btn"
                      >
                        <KeyRound size={13} className="mr-1.5" />
                        {selected.has_session_cookie
                          ? (selected.cookie_age_days >= 25
                            ? "Cookie expiring"
                            : `Live cookie · ${selected.cookie_age_days ?? 0}d`)
                          : "Add Live cookie"}
                      </Button>
                      <Button
                        onClick={() => toggleEnabled(selected)}
                        variant="outline"
                        className={`h-9 border-slate-200 ${
                          selected.enabled
                            ? "bg-white hover:bg-slate-50 text-slate-700"
                            : "bg-emerald-50 hover:bg-emerald-100 border-emerald-200 text-emerald-600"
                        }`}
                        data-testid="report-toggle-enabled"
                      >
                        {selected.enabled ? "Pause" : "Resume"}
                      </Button>
                      <Button
                        onClick={() => setEditOpen(true)}
                        variant="outline"
                        className="bg-white border-slate-200 hover:bg-slate-100 hover:border-slate-300 hover:text-slate-900 text-slate-700 h-9"
                        title="Rename / edit this report"
                        data-testid="report-edit-btn"
                      >
                        <Pencil size={13} className="mr-1" /> Edit
                      </Button>
                      <Button
                        onClick={() => deleteReport(selected)}
                        variant="outline"
                        className="bg-white border-slate-200 hover:bg-red-50 hover:border-red-200 hover:text-red-600 text-slate-500 h-9"
                        data-testid="report-delete"
                      >
                        <Trash2 size={13} />
                      </Button>
                    </div>
                  </div>

                  {/* Tabs */}
                  <div className="flex items-center border-b border-slate-200 gap-6 mb-6" data-testid="report-tabs">
                    {[
                      { id: "overview", label: "Overview" },
                      { id: "results", label: "Insights" },
                    ].map((t) => (
                      <button
                        key={t.id}
                        onClick={() => setDetailTab(t.id)}
                        className={`pb-3 text-sm font-semibold border-b-2 transition-colors -mb-px ${
                          detailTab === t.id
                            ? "text-pink-600 border-pink-500"
                            : "text-slate-400 border-transparent hover:text-slate-700"
                        }`}
                        data-testid={`report-tab-${t.id}`}
                      >
                        {t.label}
                      </button>
                    ))}
                  </div>

                  {/* URL-batch progress + Resume — visible on BOTH tabs so a
                      crashed batch is always recoverable without hunting.
                      UrlBatchSection also revives an older cancelled batch
                      card when last_job_id has moved on to another job. */}
                  <UrlBatchSection report={selected} />

                  {detailTab === "overview" && (
                  <div className="space-y-6">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <ConfigCard label="Handles" value={selected.handles.length} icon={Users} />
                    <ConfigCard label="Track terms" value={selected.keywords.length} icon={Hash} />
                    <ConfigCard label="Posts / handle" value={selected.posts_limit} icon={Bookmark} />
                    <ConfigCard
                      label="Timeline"
                      value={
                        selected.from_date
                          ? `${selected.from_date}${selected.to_date ? ` → ${selected.to_date}` : "…"}`
                          : "All time"
                      }
                      icon={CalendarClock}
                    />
                  </div>

                  <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div className="el-card p-4">
                      <div className="text-[10px] uppercase font-mono tracking-widest text-slate-400 mb-1.5 flex items-center gap-1">
                        <Users size={10} /> Handles
                      </div>
                      <div className="flex flex-wrap gap-1">
                        {selected.handles.map((h) => (
                          <span key={h} className="kw-chip">@{h}</span>
                        ))}
                      </div>
                    </div>
                    <div className="el-card p-4">
                      <div className="text-[10px] uppercase font-mono tracking-widest text-slate-400 mb-1.5 flex items-center gap-1">
                        <Hash size={10} /> Track terms · {selected.match_mode} match
                      </div>
                      <div className="flex flex-wrap gap-1">
                        {selected.keywords.map((k) => (
                          <span key={k} className="kw-chip">{k}</span>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Date-window self-check + bulk cleanup */}
                  <DataHealth reportId={selected.id} />
                  </div>
                  )}

                  {detailTab === "results" && (
                <div className="el-card p-6">
                  <div className="flex items-center gap-2 mb-4">
                    <Sparkles size={14} className="text-pink-600" />
                    <h3 className="text-sm font-semibold tracking-tight text-slate-900">
                      Latest Run
                    </h3>
                  </div>
                  <LatestRunSummary key={`${selected.id}-${detailTick}`} reportId={selected.id} jobId={selected.last_job_id} report={selected} />
                </div>
                  )}
              </div>
            )}
      </div>

      <Wizard
        open={wizardOpen}
        onClose={() => setWizardOpen(false)}
        onCreated={(r) => {
          loadReports();
          setSelectedId(r.id);
        }}
        groups={groups}
      />

      <CookieDialog
        open={cookieOpen}
        onClose={() => setCookieOpen(false)}
        report={selected}
        onSaved={() => {
          loadReports();
        }}
      />

      <CompareDialog
        open={compareOpen}
        onClose={() => setCompareOpen(false)}
        report={selected}
      />

      <UrlFetchDialog
        open={urlFetchOpen}
        onClose={() => setUrlFetchOpen(false)}
        report={selected}
        onStarted={() => {
          setTimeout(loadReports, 1500);
        }}
      />

      <EditReportDialog
        open={editOpen}
        onClose={() => setEditOpen(false)}
        report={selected}
        onSaved={() => { loadReports(); setDetailTick((t) => t + 1); }}
      />

      <ScheduleModal
        open={scheduleOpen}
        onClose={() => setScheduleOpen(false)}
        report={selected}
        onSaved={() => {
          loadReports();
        }}
      />
    </div>
  );
}

function DeliverableTypeTable({ posts, summary }) {
  const rows = useMemo(() => {
    // followers lookup by handle (from summary rows built server-side).
    const followers = {};
    for (const s of summary) followers[s.handle] = Number(s.followers || 0);

    // Bucket every post into one of the three deliverable types.
    const buckets = {
      Reels: { key: "Reels", basis: "views", posts: [] },
      "Static Posts": { key: "Static Posts", basis: "followers", posts: [] },
      Carousels: { key: "Carousels", basis: "followers", posts: [] },
    };
    for (const p of posts) {
      const t = p.post_type || "";
      let b;
      if (t === "Reel" || t === "Video") b = buckets.Reels;
      else if (t === "Static") b = buckets["Static Posts"];
      else if (t.startsWith("Carousel")) b = buckets.Carousels;
      else continue;
      b.posts.push(p);
    }

    let anyShare = false;
    const out = [];
    for (const b of Object.values(buckets)) {
      if (b.posts.length === 0) {
        out.push({ type: b.key, published: 0, views: 0, likes: 0, comments: 0,
                   shares: 0, er: null, hasShares: false });
        continue;
      }
      let likes = 0, comments = 0, views = 0, shares = 0, hasShares = false;
      for (const p of b.posts) {
        const lk = Number(p.likes || 0);
        const cm = Number(p.comments || 0);
        const vw = Number(p.video_views || 0);
        const hasS = p.shares !== null && p.shares !== undefined;
        const sh = hasS ? Number(p.shares || 0) : 0;
        likes += lk;
        comments += cm;
        views += vw;
        if (hasS) { shares += sh; hasShares = true; }
      }
      // ER — AGGREGATE per bucket (user's formula, 2026-09-22), deduped by
      // shortcode so collab rows count a post's engagement once:
      //   Reels  = Σ(likes+comments+shares) ÷ Σ views · 100
      //   Static = Σ(likes+comments+shares) ÷ Σ owner followers · 100
      const seenD = new Map();
      const rowScoreD = (x) => (((followers[x.handle] || 0) > 0 ? 2 : 0) + (!x.is_collab ? 1 : 0));
      for (const p of b.posts) {
        const k = p.shortcode || p.post_url || p.id;
        if (!k) continue;
        const prev = seenD.get(k);
        if (!prev || rowScoreD(p) > rowScoreD(prev)) seenD.set(k, p);
      }
      let erEng = 0, erBase = 0;
      for (const p of seenD.values()) {
        const lk = Number(p.likes || 0);
        const cm = Number(p.comments || 0);
        const sh = p.shares == null ? 0 : Number(p.shares || 0);
        if (b.basis === "followers") {
          const denom = followers[p.handle] || 0;
          if (denom > 0) { erEng += lk + cm + sh; erBase += denom; }
        } else {
          const vw = Number(p.video_views || 0);
          if (vw > 0) { erEng += lk + cm + sh; erBase += vw; }
        }
      }
      if (hasShares) anyShare = true;
      const er = erBase > 0 ? (erEng / erBase) * 100 : null;
      out.push({ type: b.key, published: b.posts.length, views, likes, comments,
                 shares, er, hasShares, basis: b.basis });
    }
    return { rows: out, anyShare };
  }, [posts, summary]);

  return (
    <div className="el-card p-4" data-testid="deliverable-type-table">
      <div className="text-[10px] font-mono uppercase tracking-widest text-slate-400 mb-3 flex items-center gap-1.5">
        <LayoutGrid size={10} className="text-pink-600" /> Deliverable type
        <span className="text-slate-300 normal-case tracking-normal">
          — Reels ER = Σ (likes + comments{rows.anyShare ? " + shares" : ""}) ÷ Σ views · Static/Carousel ER = Σ engagement ÷ Σ followers
        </span>
      </div>
      <div className="overflow-x-auto rounded-lg border border-slate-200/70">
        <table className="w-full text-xs">
          <thead>
            <tr className="text-[10px] uppercase tracking-wider font-mono text-slate-400 bg-slate-50/70 border-b border-slate-200">
              <th className="text-left px-3 py-2.5 font-medium">Deliverable Type</th>
              <th className="text-right px-3 py-2.5 font-medium">Published</th>
              <th className="text-right px-3 py-2.5 font-medium">Views</th>
              <th className="text-right px-3 py-2.5 font-medium">Likes</th>
              <th className="text-right px-3 py-2.5 font-medium">Comments</th>
              <th className="text-right px-3 py-2.5 font-medium">Shares</th>
              <th className="text-right px-3 py-2.5 font-medium">Avg. Engagement Rate</th>
            </tr>
          </thead>
          <tbody>
            {rows.rows.map((r) => (
              <tr
                key={r.type}
                className="border-b border-slate-100 last:border-0 data-row"
                data-testid={`deliverable-row-${r.type.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <td className="px-3 py-2 text-slate-700 font-medium">{r.type}</td>
                <td className="px-3 py-2 text-right font-mono text-slate-900 tabular-nums">{r.published}</td>
                <td className="px-3 py-2 text-right font-mono text-slate-500 tabular-nums">{formatNum(r.views)}</td>
                <td className="px-3 py-2 text-right font-mono text-slate-500 tabular-nums">{formatNum(r.likes)}</td>
                <td className="px-3 py-2 text-right font-mono text-slate-500 tabular-nums">{formatNum(r.comments)}</td>
                <td className="px-3 py-2 text-right font-mono text-slate-500 tabular-nums">
                  {r.hasShares ? formatNum(r.shares) : <span className="text-slate-300">—</span>}
                </td>
                <td className="px-3 py-2 text-right font-mono font-semibold text-slate-900 tabular-nums">
                  {r.er === null ? <span className="text-slate-300">N/A</span> : `${r.er.toFixed(2)}%`}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function PendingLinksCard({ reportId, onResolved }) {
  const [items, setItems] = useState([]);
  const [handles, setHandles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [choice, setChoice] = useState({});
  const [busy, setBusy] = useState({});
  const [checked, setChecked] = useState({});

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/reports/${reportId}/pending-links`);
      setItems(data.items || []);
      setHandles(data.tracked_handles || []);
    } catch { /* non-fatal */ } finally {
      setLoading(false);
    }
  }, [reportId]);

  useEffect(() => { load(); }, [load]);

  const resolve = async (item, action, quiet = false) => {
    const handle = (choice[item.id] || item.candidates?.[0] || "").toLowerCase();
    if (action === "attribute" && !handle) {
      toast.error("Pick a creator to attribute this link to");
      return;
    }
    setBusy((b) => ({ ...b, [item.id]: true }));
    try {
      const { data } = await axios.post(
        `${API}/reports/${reportId}/pending-links/${item.id}/resolve`,
        action === "discard" ? { action } : { action, handle }
      );
      if (!quiet) toast.success(
        action === "discard"
          ? "Link discarded"
          : `Attributed to @${data.attributed_to} — totals & exports updated`
      );
      await load();
      onResolved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not resolve link");
    } finally {
      setBusy((b) => ({ ...b, [item.id]: false }));
    }
  };

  const checkedIds = items.filter((it) => checked[it.id]).map((it) => it.id);
  const allChecked = items.length > 0 && checkedIds.length === items.length;
  const resolveMany = async (action) => {
    const chosen = items.filter((x) => checked[x.id]);
    for (const it of chosen) {
      await resolve(it, action, true);
    }
    toast.success(action === "discard" ? `Discarded ${chosen.length} link(s)` : `Attributed ${chosen.length} link(s)`);
    setChecked({});
  };

  if (loading || items.length === 0) return null;
  return (
    <div className="el-card p-4 border-amber-300/70 bg-amber-50/50" data-testid="pending-links-card">
      <div className="text-[10px] font-mono uppercase tracking-widest text-amber-700 mb-1 flex items-center gap-1.5">
        <ShieldAlert size={11} /> Needs your confirmation · {items.length} link(s)
      </div>
      <p className="text-[11px] text-slate-500 mb-3">
        These added links couldn't be auto-matched to a single tracked creator. Pick who each
        belongs to — it will be added to their totals (collab rules apply) — or discard it.
      </p>
      <div className="flex items-center gap-2 mb-2 text-[11px]" data-testid="pending-bulk-bar">
        <label className="inline-flex items-center gap-1.5 text-slate-500 cursor-pointer">
          <input
            type="checkbox"
            checked={allChecked}
            onChange={(e) => {
              const on = e.target.checked;
              setChecked(on ? Object.fromEntries(items.map((it) => [it.id, true])) : {});
            }}
            data-testid="pending-select-all"
          />
          Select all ({checkedIds.length}/{items.length})
        </label>
        {checkedIds.length > 0 && (
          <>
            <button
              onClick={() => resolveMany("attribute")}
              className="px-2.5 py-1 rounded-lg bg-slate-900 text-white text-[11px] font-semibold hover:bg-slate-800 transition-colors"
              data-testid="pending-attribute-all"
            >
              Attribute all ({checkedIds.length})
            </button>
            <button
              onClick={() => resolveMany("discard")}
              className="px-2.5 py-1 rounded-lg border border-slate-200 text-slate-500 text-[11px] font-medium hover:bg-slate-50 transition-colors"
              data-testid="pending-discard-all"
            >
              Discard all ({checkedIds.length})
            </button>
          </>
        )}
      </div>
      <div className="space-y-2">
        {items.map((item) => (
          <div
            key={item.id}
            className="flex items-center gap-3 bg-white rounded-lg border border-slate-200/80 px-3 py-2.5"
            data-testid={`pending-link-item-${item.shortcode}`}
          >
            <input
              type="checkbox"
              checked={!!checked[item.id]}
              onChange={(e) => setChecked((c) => ({ ...c, [item.id]: e.target.checked }))}
              className="shrink-0 accent-slate-900 cursor-pointer"
              data-testid={`pending-check-${item.shortcode}`}
            />
            {item.thumbnail ? (
              <img src={item.thumbnail} alt="" className="w-9 h-9 rounded-md object-cover border border-slate-200 shrink-0" />
            ) : (
              <div className="w-9 h-9 rounded-md bg-slate-100 border border-slate-200 shrink-0" />
            )}
            <div className="min-w-0 flex-1">
              <div className="text-xs font-medium text-slate-800 truncate">
                @{item.owner || "unknown"}
                {item.owner_full_name ? <span className="text-slate-400 font-normal"> · {item.owner_full_name}</span> : null}
              </div>
              <div className="text-[10px] font-mono text-slate-400">
                {item.reason === "multiple_matches"
                  ? "Collab matches multiple tracked creators"
                  : "Owner isn't a tracked creator"}
                {item.post_date ? ` · ${String(item.post_date).slice(0, 10)}` : ""}
              </div>
            </div>
            <a
              href={item.post_url}
              target="_blank"
              rel="noreferrer"
              className="text-slate-400 hover:text-pink-600 transition-colors shrink-0"
              data-testid={`pending-link-open-${item.shortcode}`}
            >
              <ExternalLink size={13} />
            </a>
            <select
              value={choice[item.id] || item.candidates?.[0] || ""}
              onChange={(e) => setChoice((c) => ({ ...c, [item.id]: e.target.value }))}
              className="text-xs border border-slate-200 rounded-lg px-2 py-1.5 bg-white text-slate-700 max-w-[160px]"
              data-testid={`pending-link-select-${item.shortcode}`}
            >
              {(item.candidates?.length ? item.candidates : handles).map((h) => (
                <option key={h} value={h}>{`@${h}`}</option>
              ))}
              {item.candidates?.length > 0 &&
                handles.filter((h) => !item.candidates.includes(h)).map((h) => (
                  <option key={h} value={h}>{`@${h}`}</option>
                ))}
            </select>
            <button
              onClick={() => resolve(item, "attribute")}
              disabled={busy[item.id]}
              className="px-2.5 py-1.5 rounded-lg bg-slate-900 text-white text-[11px] font-semibold hover:bg-slate-800 transition-colors disabled:opacity-50 shrink-0"
              data-testid={`pending-link-attribute-${item.shortcode}`}
            >
              {busy[item.id] ? "…" : "Attribute"}
            </button>
            <button
              onClick={() => resolve(item, "discard")}
              disabled={busy[item.id]}
              className="px-2.5 py-1.5 rounded-lg border border-slate-200 text-slate-500 text-[11px] font-medium hover:bg-slate-50 transition-colors disabled:opacity-50 shrink-0"
              data-testid={`pending-link-discard-${item.shortcode}`}
            >
              Discard
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

function OrphanReviewCard({ reportId, onResolved }) {
  const [items, setItems] = useState([]);
  const [handles, setHandles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [choice, setChoice] = useState({});
  const [busy, setBusy] = useState({});
  const [checked, setChecked] = useState({});
  const [collapsed, setCollapsed] = useState(true);

  const load = useCallback(async () => {
    try {
      const [orphResp, rptResp] = await Promise.all([
        axios.get(`${API}/reports/${reportId}/orphan-posts?limit=2000`),
        axios.get(`${API}/reports/${reportId}`),
      ]);
      const rows = orphResp.data.items || [];
      const trackedList = (rptResp.data.report?.handles || []).map((h) => h.toLowerCase());
      const tracked = new Set(trackedList);

      // Fuzzy suggestion: find the tracked handle most similar to a given
      // orphan/owner/mention string (name-part only, ignoring digits &
      // punctuation). E.g. orphan "sidhu_sharma" vs tracked "sidhu17" →
      // strong match on the "sidhu" root. Returns null unless the shared
      // prefix or subsequence is long enough to be a confident guess.
      const normKey = (s) => (s || "").toLowerCase().replace(/[^a-z]/g, "");
      const fuzzyMatch = (target) => {
        const t = normKey(target);
        if (t.length < 4) return null;
        let best = null, bestScore = 0;
        for (const h of trackedList) {
          const n = normKey(h);
          if (!n) continue;
          // longest common prefix
          let p = 0;
          while (p < t.length && p < n.length && t[p] === n[p]) p++;
          // contains either way
          const contains = t.includes(n) || n.includes(t);
          const score = Math.max(p, contains ? Math.min(t.length, n.length) : 0);
          if (score > bestScore && score >= 4) {
            bestScore = score; best = h;
          }
        }
        return best;
      };

      // Dedupe by shortcode and precompute best signal-suggested target.
      const bySc = new Map();
      for (const r of rows) {
        const sc = r.shortcode || r.post_url;
        if (!sc) continue;
        if (!bySc.has(sc)) bySc.set(sc, { ...r, orphan_handles: new Set([r.handle]) });
        else bySc.get(sc).orphan_handles.add(r.handle);
      }
      const arr = Array.from(bySc.values()).map((x) => {
        const cands = [];
        const push = (u) => {
          const k = (u || "").replace(/^@/, "").trim().toLowerCase();
          if (k && tracked.has(k) && !cands.includes(k)) cands.push(k);
        };
        push(x.original_owner);
        (x.coauthors || []).forEach(push);
        (x.mentions || []).slice(0, 6).forEach(push);
        // If exact-match signals yielded nothing, try fuzzy matching against
        // the untracked owner and each orphan handle.
        let fuzzy = null;
        if (!cands.length) {
          for (const src of [x.original_owner, ...(x.orphan_handles || []), ...(x.mentions || []).slice(0, 6)]) {
            const g = fuzzyMatch(src);
            if (g) { fuzzy = g; break; }
          }
          if (fuzzy && !cands.includes(fuzzy)) cands.push(fuzzy);
        }
        return {
          ...x,
          orphan_handles: Array.from(x.orphan_handles),
          candidates: cands,
          fuzzy_source: fuzzy,
        };
      });
      arr.sort((a, b) => (b.post_date || "").localeCompare(a.post_date || ""));
      setItems(arr);
      setHandles(rptResp.data.report?.handles || []);
      // Pre-fill the per-row target so bulk "Attribute selected" works
      // without the user having to touch each dropdown first.
      setChoice((prev) => {
        const next = { ...prev };
        for (const x of arr) {
          if (!next[x.shortcode] && x.candidates[0]) next[x.shortcode] = x.candidates[0];
        }
        return next;
      });
    } catch { /* non-fatal */ } finally {
      setLoading(false);
    }
  }, [reportId]);

  useEffect(() => { load(); }, [load]);

  const resolveOne = async (item, action, target, quiet = false) => {
    setBusy((b) => ({ ...b, [item.shortcode]: true }));
    try {
      for (const oh of item.orphan_handles) {
        if (action === "attribute") {
          await axios.post(`${API}/reports/${reportId}/orphan-posts/attribute`,
            { shortcode: item.shortcode, orphan_handle: oh, target_handle: target });
        } else {
          await axios.post(`${API}/reports/${reportId}/orphan-posts/discard`,
            { shortcode: item.shortcode, orphan_handle: oh });
        }
      }
      if (!quiet) toast.success(action === "attribute" ? `Attributed to @${target}` : "Post discarded");
      setItems((xs) => xs.filter((x) => x.shortcode !== item.shortcode));
      onResolved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || `Could not ${action}`);
    } finally {
      setBusy((b) => ({ ...b, [item.shortcode]: false }));
    }
  };

  const acceptSuggested = async () => {
    const withSuggestion = items.filter((x) => x.candidates.length > 0);
    if (!withSuggestion.length) return toast.info("No auto-suggested attributions");
    if (!window.confirm(`Attribute ${withSuggestion.length} post(s) to their suggested tracked handles?`)) return;
    const t = toast.loading("Attributing…");
    // Fetch full orphan rows so each orphan_handle can carry ITS OWN
    // original_owner-derived target (avoids the earlier collapse where
    // 3 orphans of the same shortcode all landed on candidates[0], erasing
    // the other two tracked collab attributions).
    const tracked = new Set((handles || []).map((h) => h.toLowerCase()));
    const perOrphanTarget = (row) => {
      const cands = [];
      const push = (u) => {
        const k = (u || "").replace(/^@/, "").trim().toLowerCase();
        if (k && tracked.has(k) && !cands.includes(k)) cands.push(k);
      };
      push(row.original_owner);
      (row.coauthors || []).forEach(push);
      (row.mentions || []).slice(0, 6).forEach(push);
      return cands[0] || null;
    };
    try {
      const orphResp = await axios.get(`${API}/reports/${reportId}/orphan-posts?limit=5000`);
      const allRows = orphResp.data.items || [];
      const payload = { items: [] };
      const sizeMap = new Map();
      for (const it of withSuggestion) sizeMap.set(it.shortcode, true);
      for (const row of allRows) {
        if (!sizeMap.has(row.shortcode)) continue;
        const target = perOrphanTarget(row);
        if (!target) continue;
        payload.items.push({ shortcode: row.shortcode, orphan_handle: row.handle, target_handle: target });
      }
      const { data } = await axios.post(`${API}/reports/${reportId}/orphan-posts/attribute-many`, payload);
      toast.success(`Attributed ${data.attributed} row(s)`, { id: t });
      await load();
      onResolved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Bulk attribute failed", { id: t });
    }
  };

  const checkedIds = items.filter((it) => checked[it.shortcode]).map((it) => it.shortcode);
  const bulkAction = async (action) => {
    const chosen = items.filter((x) => checked[x.shortcode]);
    if (!chosen.length) return;
    if (action === "attribute") {
      const payload = { items: [] };
      for (const x of chosen) {
        const target = choice[x.shortcode] || x.candidates[0];
        if (!target) continue;
        for (const oh of x.orphan_handles) payload.items.push({ shortcode: x.shortcode, orphan_handle: oh, target_handle: target });
      }
      if (!payload.items.length) return toast.error("Pick a target creator for each");
      const t = toast.loading("Attributing selected…");
      try {
        const { data } = await axios.post(`${API}/reports/${reportId}/orphan-posts/attribute-many`, payload);
        toast.success(`Attributed ${data.attributed}`, { id: t });
        setChecked({}); await load(); onResolved?.();
      } catch (e) { toast.error(e.response?.data?.detail || "Bulk attribute failed", { id: t }); }
    } else {
      const t = toast.loading("Discarding selected…");
      const payload = { items: [] };
      for (const x of chosen) for (const oh of x.orphan_handles) payload.items.push({ shortcode: x.shortcode, orphan_handle: oh });
      try {
        const { data } = await axios.post(`${API}/reports/${reportId}/orphan-posts/discard-many`, payload);
        toast.success(`Discarded ${data.discarded}`, { id: t });
        setChecked({}); await load(); onResolved?.();
      } catch (e) { toast.error(e.response?.data?.detail || "Bulk discard failed", { id: t }); }
    }
  };

  if (loading || items.length === 0) return null;
  const suggested = items.filter((x) => x.candidates.length > 0).length;

  return (
    <div className="el-card p-4 border-indigo-300/70 bg-indigo-50/40" data-testid="orphan-review-card">
      <div className="flex items-center justify-between mb-1">
        <div className="text-[10px] font-mono uppercase tracking-widest text-indigo-700 flex items-center gap-1.5">
          <ShieldAlert size={11} /> Orphan posts · {items.length} unique post(s), {suggested} with a suggested match
        </div>
        <button
          onClick={() => setCollapsed((c) => !c)}
          className="text-[10px] font-mono uppercase tracking-widest text-indigo-700 hover:underline"
          data-testid="orphan-toggle"
        >
          {collapsed ? "Show" : "Hide"}
        </button>
      </div>
      <p className="text-[11px] text-slate-500 mb-3">
        These posts are stored under a handle that isn't tracked on this report and are{" "}
        <strong>not counted yet</strong>. Attribute each to one of your tracked creators (usually
        the original owner or a coauthor) — it immediately adds to that creator's totals, the
        dashboard cards, and every export — or discard it if it doesn't belong. Zero credit cost.
      </p>
      {!collapsed && (
        <>
          <div className="flex items-center gap-2 mb-2 text-[11px]" data-testid="orphan-bulk-bar">
            {suggested > 0 && (
              <button
                onClick={acceptSuggested}
                className="px-2.5 py-1 rounded-lg bg-indigo-600 text-white text-[11px] font-semibold hover:bg-indigo-500 transition-colors"
                data-testid="orphan-accept-suggested"
              >
                Accept all {suggested} suggested
              </button>
            )}
            <label className="inline-flex items-center gap-1.5 text-slate-500 cursor-pointer">
              <input
                type="checkbox"
                checked={items.length > 0 && checkedIds.length === items.length}
                onChange={(e) => {
                  const on = e.target.checked;
                  setChecked(on ? Object.fromEntries(items.map((it) => [it.shortcode, true])) : {});
                }}
                data-testid="orphan-select-all"
              />
              Select all ({checkedIds.length}/{items.length})
            </label>
            {checkedIds.length > 0 && (
              <>
                <button
                  onClick={() => bulkAction("attribute")}
                  className="px-2.5 py-1 rounded-lg bg-slate-900 text-white text-[11px] font-semibold hover:bg-slate-800"
                  data-testid="orphan-attribute-selected"
                >
                  Attribute selected ({checkedIds.length})
                </button>
                <button
                  onClick={() => bulkAction("discard")}
                  className="px-2.5 py-1 rounded-lg border border-slate-200 text-slate-500 text-[11px] font-medium hover:bg-slate-50"
                  data-testid="orphan-discard-selected"
                >
                  Discard selected ({checkedIds.length})
                </button>
              </>
            )}
          </div>
          <div className="space-y-2 max-h-[560px] overflow-y-auto">
            {items.map((item) => (
              <div
                key={item.shortcode}
                className="flex items-center gap-3 bg-white rounded-lg border border-slate-200/80 px-3 py-2.5"
                data-testid={`orphan-item-${item.shortcode}`}
              >
                <input
                  type="checkbox"
                  checked={!!checked[item.shortcode]}
                  onChange={(e) => setChecked((c) => ({ ...c, [item.shortcode]: e.target.checked }))}
                  className="shrink-0 accent-slate-900 cursor-pointer"
                />
                {item.thumbnail ? (
                  <img src={item.thumbnail} alt="" className="w-9 h-9 rounded-md object-cover border border-slate-200 shrink-0" />
                ) : (
                  <div className="w-9 h-9 rounded-md bg-slate-100 border border-slate-200 shrink-0" />
                )}
                <div className="min-w-0 flex-1">
                  <div className="text-xs font-medium text-slate-800 truncate">
                    @{item.orphan_handles.join(" · @")}
                    {item.original_owner ? <span className="text-slate-400 font-normal"> · owner: @{item.original_owner}</span> : null}
                  </div>
                  <div className="text-[10px] font-mono text-slate-400">
                    {item.post_type}
                    {item.post_date ? ` · ${String(item.post_date).slice(0, 10)}` : ""}
                    {` · ❤ ${item.likes || 0} · 💬 ${item.comments || 0}`}
                    {item.candidates.length > 0 ? ` · suggested: @${item.candidates[0]}` : " · no suggestion"}
                  </div>
                </div>
                <a
                  href={item.post_url}
                  target="_blank"
                  rel="noreferrer"
                  className="text-slate-400 hover:text-pink-600 transition-colors shrink-0"
                  data-testid={`orphan-open-${item.shortcode}`}
                >
                  <ExternalLink size={13} />
                </a>
                <select
                  value={choice[item.shortcode] || item.candidates[0] || ""}
                  onChange={(e) => setChoice((c) => ({ ...c, [item.shortcode]: e.target.value }))}
                  className="text-xs border border-slate-200 rounded-lg px-2 py-1.5 bg-white text-slate-700 max-w-[160px]"
                  data-testid={`orphan-select-${item.shortcode}`}
                >
                  <option value="">— pick target —</option>
                  {item.candidates.map((h) => (
                    <option key={h} value={h}>{`@${h} (suggested)`}</option>
                  ))}
                  {handles.filter((h) => !item.candidates.includes(h.toLowerCase())).map((h) => (
                    <option key={h} value={h.toLowerCase()}>{`@${h}`}</option>
                  ))}
                </select>
                <button
                  onClick={() => {
                    const t = choice[item.shortcode] || item.candidates[0];
                    if (!t) return toast.error("Pick a target creator");
                    resolveOne(item, "attribute", t);
                  }}
                  disabled={busy[item.shortcode]}
                  className="px-2.5 py-1.5 rounded-lg bg-slate-900 text-white text-[11px] font-semibold hover:bg-slate-800 disabled:opacity-50 shrink-0"
                  data-testid={`orphan-attribute-${item.shortcode}`}
                >
                  {busy[item.shortcode] ? "…" : "Attribute"}
                </button>
                <button
                  onClick={() => resolveOne(item, "discard")}
                  disabled={busy[item.shortcode]}
                  className="px-2.5 py-1.5 rounded-lg border border-slate-200 text-slate-500 text-[11px] font-medium hover:bg-slate-50 disabled:opacity-50 shrink-0"
                  data-testid={`orphan-discard-${item.shortcode}`}
                >
                  Discard
                </button>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}



function DataHealth({ reportId }) {
  const [health, setHealth] = useState(null);
  const [checking, setChecking] = useState(false);
  const [purging, setPurging] = useState(false);

  const check = useCallback(async (silent = false) => {
    if (!silent) setChecking(true);
    try {
      const { data } = await axios.get(`${API}/reports/${reportId}/data-health`);
      setHealth(data);
    } catch {
      if (!silent) toast.error("Data-health check failed");
    } finally {
      setChecking(false);
    }
  }, [reportId]);

  useEffect(() => { check(true); }, [check]);

  const purge = async () => {
    if (!window.confirm(
      "Delete EVERY stored post outside this report's date window? This cannot be undone."
    )) return;
    setPurging(true);
    try {
      const { data } = await axios.post(`${API}/reports/${reportId}/purge-out-of-window`);
      toast.success(
        data.removed
          ? `Scanned ${data.posts_checked} post(s) — removed ${data.removed} out-of-range`
          : `Scanned ${data.posts_checked} post(s) — nothing out of range`
      );
      await check(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Cleanup failed");
    } finally {
      setPurging(false);
    }
  };

  if (!health || (!health.since_date && !health.to_date)) return null;
  return (
    <div
      className={`flex items-center gap-3 px-4 py-3 rounded-xl border text-[11px] font-mono ${
        health.healthy
          ? "bg-emerald-50 border-emerald-200 text-emerald-700"
          : "bg-red-50 border-red-200 text-red-700"
      }`}
      data-testid="report-data-health"
    >
      {health.healthy ? <Check size={13} /> : <ShieldAlert size={13} />}
      <span>
        Date window {health.since_date || "…"} → {health.to_date || "…"} ·{" "}
        {health.out_of_window === 0
          ? `all ${health.total_posts} posts inside the window`
          : `${health.out_of_window} post(s) out of range${
              health.oldest_out_of_window
                ? ` (oldest ${String(health.oldest_out_of_window).slice(0, 10)})`
                : ""
            }`}
      </span>
      <div className="ml-auto flex items-center gap-2">
        <button
          onClick={() => check()}
          disabled={checking}
          className="px-2.5 py-1 rounded-lg border border-slate-300/60 bg-white/60 hover:bg-white transition-colors disabled:opacity-50"
          data-testid="data-health-check-btn"
        >
          {checking ? "Checking…" : "Re-check"}
        </button>
        {health.out_of_window > 0 && (
          <button
            onClick={purge}
            disabled={purging}
            className="px-2.5 py-1 rounded-lg bg-red-600 text-white hover:bg-red-700 transition-colors disabled:opacity-50"
            data-testid="data-health-purge-btn"
          >
            {purging ? "Removing…" : "Remove out-of-range posts"}
          </button>
        )}
      </div>
    </div>
  );
}

const EditField = ({ label, children }) => (
  <div>
    <label className="text-[10px] uppercase font-mono tracking-widest text-slate-400 mb-1.5 block">
      {label}
    </label>
    {children}
  </div>
);

const EditToggleRow = ({ label, desc, checked, onChange, testid }) => (
  <div className="flex items-center justify-between rounded-lg border border-slate-200 bg-slate-50/60 px-3 py-2.5">
    <div>
      <div className="text-xs font-semibold text-slate-800">{label}</div>
      <div className="text-[10px] text-slate-400">{desc}</div>
    </div>
    <Switch checked={checked} onCheckedChange={onChange} data-testid={testid} />
  </div>
);

function EditReportDialog({ open, onClose, report, onSaved }) {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  // Init the form exactly once per dialog opening. The 8s reports poll
  // replaces the `report` object identity — depending on it here would
  // wipe whatever the user is typing mid-edit (the rename-reset bug).
  const initRef = useRef(false);

  useEffect(() => {
    if (!open) { initRef.current = false; return; }
    if (open && report && !initRef.current) {
      initRef.current = true;
      setForm({
        name: report.name || "",
        handles: (report.handles || []).join("\n"),
        keywords: (report.keywords || []).join("\n"),
        since_date: report.since_date || "",
        to_date: report.to_date || "",
        posts_lookback_limit: report.posts_lookback_limit || report.posts_limit || 30,
        include_stories: report.include_stories ?? true,
        fetch_highlights: report.fetch_highlights ?? false,
        fetch_live: report.fetch_live ?? false,
        stories_match_any: report.stories_match_any ?? false,
        auto_track: report.auto_track ?? false,
        provider: report.provider || "rapidapi",
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!form) return null;
  const isUrls = report?.report_type === "urls";

  const save = async () => {
    if (!form.name.trim()) {
      toast.error("Report name is required");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        since_date: form.since_date || null,
        to_date: form.to_date || null,
      };
      // Allow every report — including URL-only ones — to add handles /
      // keywords / auto-tracking on the fly, so a URL-fetched report can be
      // upgraded to daily handle tracking without recreating it.
      payload.handles = form.handles
        .split("\n").map((x) => x.trim().replace(/^@/, "")).filter(Boolean);
      payload.keywords = form.keywords
        .split("\n").map((x) => x.trim()).filter(Boolean);
      payload.posts_lookback_limit = parseInt(form.posts_lookback_limit, 10) || 30;
      payload.include_stories = form.include_stories;
      payload.fetch_highlights = form.fetch_highlights;
      payload.fetch_live = form.fetch_live;
      payload.stories_match_any = form.stories_match_any;
      payload.auto_track = form.auto_track;
      payload.provider = form.provider;
      const { data: putData } = await axios.put(`${API}/reports/${report.id}`, payload);
      toast.success(`Report "${form.name.trim()}" updated`);
      if (putData?.regroup_hint) {
        toast.info(
          `${putData.regroup_hint.stored_links} stored link(s) will now group under your new handle(s) automatically. For collab attributions on older links, use Fix Post Types → Re-fetch from Instagram.`,
          { duration: 9000 }
        );
      }
      onSaved();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not update report");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className="bg-white border-slate-200 text-slate-900 max-w-lg max-h-[90vh] overflow-y-auto"
        data-testid="report-edit-dialog"
      >
        <DialogHeader>
          <DialogTitle>Edit Report</DialogTitle>
          <DialogDescription className="text-slate-400 text-xs">
            Rename the report or adjust what it tracks. Changes apply from the next run onward.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <EditField label="Report name">
            <Input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="bg-slate-50 border-slate-200 text-sm text-slate-900"
              data-testid="edit-name"
            />
          </EditField>

          {!isUrls ? null : (
            <div className="rounded-lg bg-indigo-50/60 border border-indigo-200 px-3 py-2.5 text-[11px] text-indigo-700 leading-snug">
              <b>URL-fetch report</b> — you can add handles + keywords below and enable auto-tracking to also collect handle-based content daily on top of the URLs already imported.
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <EditField label={isUrls ? "Handles (optional, one per line — add to enable daily tracking)" : "Handles (one per line)"}>
              <textarea
                value={form.handles}
                onChange={(e) => setForm({ ...form, handles: e.target.value })}
                rows={5}
                className="w-full bg-slate-50 border border-slate-200 rounded-md p-2.5 font-mono text-xs text-slate-900 focus:outline-none focus:border-pink-400"
                data-testid="edit-handles"
              />
            </EditField>
            <EditField label={isUrls ? "Track terms (optional, one per line)" : "Track terms (one per line)"}>
              <textarea
                value={form.keywords}
                onChange={(e) => setForm({ ...form, keywords: e.target.value })}
                rows={5}
                className="w-full bg-slate-50 border border-slate-200 rounded-md p-2.5 font-mono text-xs text-slate-900 focus:outline-none focus:border-pink-400"
                data-testid="edit-keywords"
              />
            </EditField>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <EditField label="Posts per refresh (lookback)">
              <Input
                type="number"
                min={1}
                max={500}
                value={form.posts_lookback_limit}
                onChange={(e) => setForm({ ...form, posts_lookback_limit: e.target.value })}
                className="bg-slate-50 border-slate-200 font-mono text-xs text-slate-900"
                data-testid="edit-lookback"
              />
            </EditField>
            <EditField label="Posts provider">
              <div className="flex gap-2" data-testid="edit-provider">
                {["rapidapi", "apify"].map((p) => (
                  <button
                    key={p}
                    type="button"
                    onClick={() => setForm({ ...form, provider: p })}
                    className={`flex-1 px-3 py-2 rounded-lg border text-xs font-semibold transition-colors ${
                      form.provider === p
                        ? "bg-pink-50 border-pink-300 text-pink-600"
                        : "bg-white border-slate-200 text-slate-500 hover:border-slate-300"
                    }`}
                    data-testid={`edit-provider-${p}`}
                  >
                    {p === "rapidapi" ? "RapidAPI" : "Apify"}
                  </button>
                ))}
              </div>
            </EditField>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <EditField label="Start date (hard floor)">
              <DateField value={form.since_date} onChange={(v) => setForm({ ...form, since_date: v })} testid="edit-since-date" placeholder="Start date" />
            </EditField>
            <EditField label="End date (optional)">
              <DateField value={form.to_date} onChange={(v) => setForm({ ...form, to_date: v })} testid="edit-to-date" placeholder="End date (optional)" />
            </EditField>
          </div>

          {!isUrls && (
            <div className="space-y-2">
              <EditToggleRow
                label="Auto-track daily"
                desc="Run a daily fetch for the tracked handles above (10:00 IST)"
                checked={form.auto_track}
                onChange={(v) => setForm({ ...form, auto_track: v })}
                testid="edit-auto-track"
              />
              <EditToggleRow
                label="Track stories"
                desc="Count daily stories for tracked handles"
                checked={form.include_stories}
                onChange={(v) => setForm({ ...form, include_stories: v })}
                testid="edit-include-stories"
              />
              <EditToggleRow
                label="Fetch highlights"
                desc="Include story highlights (counted separately, never in totals)"
                checked={form.fetch_highlights}
                onChange={(v) => setForm({ ...form, fetch_highlights: v })}
                testid="edit-fetch-highlights"
              />
              <EditToggleRow
                label="Fetch live stories"
                desc="Capture stories live in the last 24h (needs cookie)"
                checked={form.fetch_live}
                onChange={(v) => setForm({ ...form, fetch_live: v })}
                testid="edit-fetch-live"
              />
              <EditToggleRow
                label="Count ALL stories (ignore keywords)"
                desc="OFF = only stories mentioning your track terms are counted. Turn ON only for testing."
                checked={form.stories_match_any}
                onChange={(v) => setForm({ ...form, stories_match_any: v })}
                testid="edit-stories-match-any"
              />
            </div>
          )}

          {isUrls && (
            <div className="space-y-2">
              <EditToggleRow
                label="Auto-track daily (when handles added)"
                desc="Fetches posts (and stories if enabled) daily for the handles above. Off until you add at least one handle."
                checked={form.auto_track}
                onChange={(v) => setForm({ ...form, auto_track: v })}
                testid="edit-auto-track"
              />
              <EditToggleRow
                label="Track stories daily"
                desc="Count daily stories for the handles you added above"
                checked={form.include_stories}
                onChange={(v) => setForm({ ...form, include_stories: v })}
                testid="edit-include-stories"
              />
              <EditToggleRow
                label="Fetch highlights"
                desc="Include story highlights (counted separately, never in totals)"
                checked={form.fetch_highlights}
                onChange={(v) => setForm({ ...form, fetch_highlights: v })}
                testid="edit-fetch-highlights"
              />
              <EditToggleRow
                label="Fetch live stories"
                desc="Capture stories live in the last 24h (needs cookie)"
                checked={form.fetch_live}
                onChange={(v) => setForm({ ...form, fetch_live: v })}
                testid="edit-fetch-live"
              />
            </div>
          )}

          <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-100">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="bg-white border-slate-200 hover:bg-slate-50 text-slate-700"
              data-testid="edit-cancel"
            >
              Cancel
            </Button>
            <Button
              onClick={save}
              disabled={saving || !form.name.trim()}
              className="bg-slate-900 hover:bg-slate-800 text-white font-semibold disabled:opacity-40"
              data-testid="edit-save"
            >
              {saving ? (
                <><Loader2 size={12} className="mr-1.5 animate-spin" /> Saving…</>
              ) : "Save changes"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ConfigCard({ label, value, icon: Icon }) {
  return (
    <div className="el-card p-4">
      <div className="text-[10px] uppercase font-mono tracking-widest text-slate-400 flex items-center gap-1">
        <Icon size={10} /> {label}
      </div>
      <div className="text-sm font-mono text-slate-900 mt-1 truncate">{value}</div>
    </div>
  );
}

function CookieDialog({ open, onClose, report, onSaved }) {
  const [cookie, setCookie] = useState("");
  const [show, setShow] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) setTimeout(() => { setCookie(""); setShow(false); }, 200);
  }, [open]);

  if (!report) return null;
  const hasCookie = report.has_session_cookie;
  const age = report.cookie_age_days;
  const expiring = hasCookie && age != null && age >= 25;

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/reports/${report.id}/cookie`, {
        session_cookie: cookie.trim() || null,
      });
      toast.success(cookie.trim() ? "Live cookie saved" : "Live cookie cleared");
      onSaved && onSaved();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save cookie");
    } finally {
      setSaving(false);
    }
  };

  const clear = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/reports/${report.id}/cookie`, { session_cookie: "" });
      toast.success("Cleared. Only Highlights will be captured.");
      onSaved && onSaved();
      onClose();
    } catch (e) {
      toast.error("Could not clear cookie");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className="bg-white border-slate-200 text-slate-900 max-w-xl"
        data-testid="cookie-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <KeyRound size={16} className="text-pink-600" />
            Live-Story Cookie
          </DialogTitle>
          <DialogDescription className="text-slate-400 text-xs">
            Paste your Instagram <code className="font-mono text-pink-600">sessionid</code> cookie
            to also capture <b>live 24h stories</b> (in addition to Highlights).
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          {hasCookie && (
            <div className={`rounded-lg p-3 border ${
              expiring
                ? "bg-red-50 border-red-200 text-red-700"
                : "bg-emerald-50 border-emerald-200 text-emerald-700"
            }`}>
              <div className="flex items-center gap-2 text-sm">
                {expiring ? <ShieldAlert size={14} /> : <Check size={14} />}
                <span className="font-semibold">
                  {expiring ? "Cookie expiring soon" : "Cookie active"}
                </span>
                <span className="ml-auto text-xs font-mono">
                  set {age ?? 0}d ago
                </span>
              </div>
              <div className="text-[11px] mt-1 opacity-90">
                {expiring
                  ? "IG session cookies typically expire after ~30 days. Refresh below to keep Live-story capture working."
                  : "Live 24h stories will be included on every scheduled run."}
              </div>
            </div>
          )}

          <div>
            <label className="text-[10px] uppercase font-mono tracking-widest text-slate-400 mb-1.5 block">
              {hasCookie ? "Replace cookie value" : "sessionid value"}
            </label>
            <div className="relative">
              <Input
                type={show ? "text" : "password"}
                value={cookie}
                onChange={(e) => setCookie(e.target.value)}
                placeholder="e.g. 61234567890%3AXXXXX%3A25%3AXXX..."
                className="bg-white border-slate-200 font-mono text-xs pr-10"
                data-testid="cookie-input"
              />
              <button
                type="button"
                onClick={() => setShow((v) => !v)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
              >
                {show ? <EyeOff size={13} /> : <Eye size={13} />}
              </button>
            </div>
          </div>

          <div className="bg-white border border-pink-200 rounded-lg p-3 text-[11px] text-pink-600/90 leading-relaxed">
            <div className="font-semibold flex items-center gap-1.5 mb-1">
              <ShieldAlert size={12} /> How to get it & risks
            </div>
            <ol className="list-decimal ml-4 space-y-0.5 text-pink-600/80">
              <li>Log into Instagram in a browser using a <b>burner account</b> — never your main.</li>
              <li>Open DevTools → Application → Cookies → https://www.instagram.com.</li>
              <li>Copy the value of the <code className="font-mono">sessionid</code> row.</li>
            </ol>
            <div className="mt-2 text-pink-600/90">
              ⚠️ Instagram may shadow-ban / suspend the account whose cookie is used.
              Cookies expire in ~30 days. Rate-limits are strict (~50 handles/hr).
            </div>
          </div>

          <div className="flex items-center justify-end gap-2 pt-2">
            {hasCookie && (
              <Button
                variant="outline"
                onClick={clear}
                disabled={saving}
                className="bg-white border-slate-200 hover:bg-red-50 hover:border-red-200 hover:text-red-600 text-slate-500"
                data-testid="cookie-clear"
              >
                <Trash2 size={12} className="mr-1.5" />
                Clear cookie
              </Button>
            )}
            <Button
              variant="outline"
              onClick={onClose}
              className="bg-white border-slate-200 hover:bg-slate-50 text-slate-700"
            >
              Cancel
            </Button>
            <Button
              onClick={save}
              disabled={saving || !cookie.trim()}
              className="bg-pink-600 hover:bg-pink-700 text-white font-semibold disabled:opacity-40"
              data-testid="cookie-save"
            >
              {saving ? (
                <><Loader2 size={12} className="mr-1.5 animate-spin" /> Saving…</>
              ) : (
                <><Check size={12} className="mr-1.5" /> Save Cookie</>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function UrlFetchDialog({ open, onClose, report, onStarted }) {
  const [urlsText, setUrlsText] = useState("");
  const [provider, setProvider] = useState("rapidapi");
  const [keywordsText, setKeywordsText] = useState("");
  const [running, setRunning] = useState(false);

  useEffect(() => {
    if (!open) setTimeout(() => { setUrlsText(""); setKeywordsText(""); setProvider("rapidapi"); }, 200);
  }, [open]);

  const urlAnalysis = useMemo(() => {
    const lines = (urlsText || "").split(/[\n,]+/).map((s) => s.trim()).filter(Boolean);
    const codeRe = /instagram\.com\/(?:p|reel|reels|tv)\/([A-Za-z0-9_-]+)/i;
    const uniq = new Set();
    let invalid = 0;
    let valid = 0;
    for (const l of lines) {
      const m = l.match(codeRe);
      if (!m) { invalid += 1; continue; }
      valid += 1;
      uniq.add(m[1]);
    }
    return {
      totalPasted: lines.length,
      validCount: valid,
      invalidCount: invalid,
      uniqueCount: uniq.size,
      duplicateCount: Math.max(0, valid - uniq.size),
    };
  }, [urlsText]);

  const urls = useMemo(() => {
    const codeRe = /instagram\.com\/(?:p|reel|reels|tv)\/([A-Za-z0-9_-]+)/i;
    const seen = new Set();
    const out = [];
    for (const line of (urlsText || "").split(/[\n,]+/).map((s) => s.trim())) {
      const m = line.match(codeRe);
      if (!m || seen.has(m[1])) continue;
      seen.add(m[1]);
      out.push(line);
    }
    return out;
  }, [urlsText]);

  if (!report) return null;

  const submit = async () => {
    setRunning(true);
    try {
      const keywords = parseList(keywordsText);
      const { data } = await axios.post(`${API}/reports/${report.id}/fetch-urls`, {
        urls,
        provider,
        keywords,          // [] = no filter — keep every URL
      });
      if (!data.job_id) {
        toast.info(data.message || "All links are already in this report — nothing new to fetch.");
      } else {
        toast.success(
          `Fetching ${data.url_count || urls.length} new post(s) via ${provider === "apify" ? "Apify" : "RapidAPI"}…` +
          (data.already_in_report ? ` (${data.already_in_report} already stored — skipped free)` : "")
        );
      }
      onStarted && onStarted();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "URL fetch failed to start");
    } finally {
      setRunning(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className="bg-white border-slate-200 text-slate-900 max-w-xl"
        data-testid="url-fetch-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ExternalLink size={16} className="text-pink-600" />
            Fetch Posts by URL
          </DialogTitle>
          <DialogDescription className="text-slate-400 text-xs">
            Paste Instagram post / reel / carousel links (up to thousands at once) — data is
            downloaded straight into this report. Stories are skipped. Links auto-attribute to
            this report's tracked creators (collab rules apply — never a duplicate creator);
            anything ambiguous lands in the review queue above the report tabs for your confirmation.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <label className="text-[10px] uppercase font-mono tracking-widest text-slate-400 mb-1.5 block">
              Content URLs (one per line)
            </label>
            <Textarea
              value={urlsText}
              onChange={(e) => setUrlsText(e.target.value)}
              placeholder={"https://www.instagram.com/p/…\nhttps://www.instagram.com/reel/…"}
              className="bg-white border-slate-200 font-mono text-xs min-h-[120px]"
              data-testid="url-fetch-urls-input"
            />
            <div className="grid grid-cols-4 gap-1.5 mt-2 text-[10px] font-mono" data-testid="url-fetch-count">
              <div className="rounded border border-slate-200 bg-white p-2">
                <div className="text-slate-400 uppercase tracking-widest">Pasted</div>
                <div className="text-slate-900 text-sm font-semibold mt-0.5">{urlAnalysis.totalPasted}</div>
              </div>
              <div className="rounded border border-emerald-500/25 bg-emerald-50 p-2">
                <div className="text-emerald-600/80 uppercase tracking-widest">Will fetch</div>
                <div className="text-emerald-600 text-sm font-semibold mt-0.5">{urlAnalysis.uniqueCount}</div>
              </div>
              <div className={`rounded border p-2 ${urlAnalysis.duplicateCount > 0 ? "border-pink-200 bg-pink-50" : "border-slate-200 bg-white"}`}>
                <div className={`uppercase tracking-widest ${urlAnalysis.duplicateCount > 0 ? "text-pink-600/80" : "text-slate-400"}`}>Duplicates</div>
                <div className={`text-sm font-semibold mt-0.5 ${urlAnalysis.duplicateCount > 0 ? "text-pink-600" : "text-slate-900"}`}>{urlAnalysis.duplicateCount}</div>
              </div>
              <div className={`rounded border p-2 ${urlAnalysis.invalidCount > 0 ? "border-red-200 bg-red-50" : "border-slate-200 bg-white"}`}>
                <div className={`uppercase tracking-widest ${urlAnalysis.invalidCount > 0 ? "text-red-600/80" : "text-slate-400"}`}>Invalid</div>
                <div className={`text-sm font-semibold mt-0.5 ${urlAnalysis.invalidCount > 0 ? "text-red-600" : "text-slate-900"}`}>{urlAnalysis.invalidCount}</div>
              </div>
            </div>
          </div>

          <div>
            <label className="text-[10px] uppercase font-mono tracking-widest text-slate-400 mb-1.5 block">
              Provider
            </label>
            <div className="grid grid-cols-2 gap-2" data-testid="url-fetch-provider">
              <button
                type="button"
                onClick={() => setProvider("rapidapi")}
                className={`rounded-lg border p-3 text-left transition-colors ${
                  provider === "rapidapi"
                    ? "border-pink-300 bg-pink-50"
                    : "border-slate-200 bg-white hover:border-slate-300"
                }`}
                data-testid="url-fetch-provider-rapidapi"
              >
                <div className="text-xs font-semibold text-slate-900">RapidAPI</div>
                <div className="text-[10px] text-slate-400 mt-0.5 leading-snug">
                  Likes, comments, views, plays. No share/repost counts.
                </div>
              </button>
              <button
                type="button"
                onClick={() => setProvider("apify")}
                className={`rounded-lg border p-3 text-left transition-colors ${
                  provider === "apify"
                    ? "border-emerald-500/50 bg-emerald-50"
                    : "border-slate-200 bg-white hover:border-slate-300"
                }`}
                data-testid="url-fetch-provider-apify"
              >
                <div className="text-xs font-semibold text-slate-900">Apify · Data Slayer</div>
                <div className="text-[10px] text-slate-400 mt-0.5 leading-snug">
                  Adds <b className="text-emerald-600">Shares + Reposts</b> (reels: both;
                  static/carousel: reposts — IG hides shares on feed posts).
                </div>
              </button>
            </div>
            {provider === "rapidapi" && urls.length > 100 && (
              <div
                className="mt-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[11px] text-amber-800 leading-snug"
                data-testid="url-fetch-large-batch-hint"
              >
                <b>{urls.length.toLocaleString()} links via RapidAPI will be slow</b> (~200-300/min;
                a 1,000+ batch can take 5+ minutes). <b>Apify is the better choice for big
                batches</b> — one parallel run, ~$1.50 per 1,000 links, usually done in a
                few minutes, and it adds Shares/Reposts.
              </div>
            )}
          </div>

          <div>
            <label className="text-[10px] uppercase font-mono tracking-widest text-slate-400 mb-1.5 block">
              Keyword filter (optional — blank keeps every post)
            </label>
            <Input
              value={keywordsText}
              onChange={(e) => setKeywordsText(e.target.value)}
              placeholder="e.g. canon, #eosr5, @canonusa"
              className="bg-white border-slate-200 font-mono text-xs"
              data-testid="url-fetch-keywords-input"
            />
          </div>

          <div className="flex items-center justify-end gap-2 pt-1">
            <Button
              variant="outline"
              onClick={onClose}
              className="bg-white border-slate-200 hover:bg-slate-50 text-slate-700"
            >
              Cancel
            </Button>
            <Button
              onClick={submit}
              disabled={running || urls.length === 0}
              className="bg-slate-900 hover:bg-slate-800 text-white font-semibold disabled:opacity-40"
              data-testid="url-fetch-submit"
            >
              {running ? (
                <><Loader2 size={12} className="mr-1.5 animate-spin" /> Starting…</>
              ) : (
                <><Download size={12} className="mr-1.5" /> Fetch {urls.length || ""} Post(s)</>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}


function DeltaCell({ value, invert }) {
  const good = invert ? value < 0 : value > 0;
  const bad = invert ? value > 0 : value < 0;
  const Icon = value > 0 ? TrendingUp : value < 0 ? TrendingDown : Minus;
  const color = good ? "text-emerald-600" : bad ? "text-red-600" : "text-slate-400";
  const sign = value > 0 ? "+" : "";
  return (
    <span className={`inline-flex items-center gap-0.5 font-mono ${color}`}>
      <Icon size={10} />
      {sign}{value}
    </span>
  );
}

function CompareDialog({ open, onClose, report }) {
  const today = new Date();
  const iso = (d) => d.toISOString().slice(0, 10);
  const thisMonthStart = new Date(today.getFullYear(), today.getMonth(), 1);
  const lastMonthStart = new Date(today.getFullYear(), today.getMonth() - 1, 1);
  const lastMonthEnd = new Date(today.getFullYear(), today.getMonth(), 0);

  const [aFrom, setAFrom] = useState(iso(thisMonthStart));
  const [aTo, setATo] = useState(iso(today));
  const [bFrom, setBFrom] = useState(iso(lastMonthStart));
  const [bTo, setBTo] = useState(iso(lastMonthEnd));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const run = async () => {
    if (!report) return;
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        a_from: aFrom, a_to: aTo, b_from: bFrom, b_to: bTo,
      });
      const { data } = await axios.get(
        `${API}/reports/${report.id}/compare?${params.toString()}`
      );
      setData(data);
    } catch (e) {
      setError(e.response?.data?.detail || "Compare failed");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && report) run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, report?.id]);

  if (!report) return null;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className="bg-white border-slate-200 text-slate-900 max-w-5xl p-0"
        data-testid="compare-dialog"
      >
        <div className="p-5 border-b border-slate-200">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <GitCompareArrows size={16} className="text-violet-600" />
              Compare two windows — {report.name}
            </DialogTitle>
            <DialogDescription className="text-slate-400 text-xs">
              Window A vs Window B — new posts, dropped posts, and per-handle deltas.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            <div className="bg-white border border-pink-200 rounded-lg p-3">
              <div className="text-[10px] uppercase font-mono tracking-widest text-pink-600 mb-2">
                Window A (Current)
              </div>
              <div className="flex items-center gap-2">
                <DateField value={aFrom} onChange={setAFrom} testid="compare-a-from" placeholder="From" />
                <span className="text-slate-400">→</span>
                <DateField value={aTo} onChange={setATo} testid="compare-a-to" placeholder="To" />
              </div>
            </div>
            <div className="bg-white border border-violet-500/30 rounded-lg p-3">
              <div className="text-[10px] uppercase font-mono tracking-widest text-violet-600 mb-2">
                Window B (Previous)
              </div>
              <div className="flex items-center gap-2">
                <DateField value={bFrom} onChange={setBFrom} testid="compare-b-from" placeholder="From" />
                <span className="text-slate-400">→</span>
                <DateField value={bTo} onChange={setBTo} testid="compare-b-to" placeholder="To" />
              </div>
            </div>
          </div>
          <div className="flex items-center justify-end gap-2 mt-3">
            <Button
              onClick={run}
              disabled={loading}
              className="bg-violet-500 hover:bg-violet-600 text-white font-semibold h-9"
              data-testid="compare-run"
            >
              {loading ? (
                <><Loader2 size={12} className="mr-1.5 animate-spin" /> Comparing…</>
              ) : (
                <><GitCompareArrows size={12} className="mr-1.5" /> Compare</>
              )}
            </Button>
          </div>
        </div>

        <ScrollArea className="max-h-[60vh]">
          <div className="p-5 space-y-4">
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-xs text-red-600">
                {error}
              </div>
            )}
            {data && !error && (
              <>
                <div className="grid grid-cols-3 gap-3">
                  <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
                    <div className="text-[10px] uppercase font-mono tracking-widest text-emerald-600 flex items-center gap-1">
                      <TrendingUp size={11} /> New in A
                    </div>
                    <div className="text-3xl font-mono font-bold text-emerald-600 mt-1">
                      {data.counts.new_posts}
                    </div>
                  </div>
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <div className="text-[10px] uppercase font-mono tracking-widest text-red-600 flex items-center gap-1">
                      <TrendingDown size={11} /> Dropped from B
                    </div>
                    <div className="text-3xl font-mono font-bold text-red-600 mt-1">
                      {data.counts.dropped_posts}
                    </div>
                  </div>
                  <div className="bg-gray-500/10 border border-gray-500/30 rounded-lg p-4">
                    <div className="text-[10px] uppercase font-mono tracking-widest text-slate-500 flex items-center gap-1">
                      <Minus size={11} /> Kept in both
                    </div>
                    <div className="text-3xl font-mono font-bold text-slate-700 mt-1">
                      {data.counts.kept_posts}
                    </div>
                  </div>
                </div>

                <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto">
                  <div className="px-3 py-2 border-b border-slate-200 text-[10px] font-mono uppercase tracking-widest text-slate-400">
                    Per-handle deltas · A vs B
                  </div>
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="text-[10px] uppercase tracking-widest font-mono text-slate-400 border-b border-slate-200">
                        <th className="text-left px-3 py-2">Handle</th>
                        <th className="text-right px-2 py-2">A</th>
                        <th className="text-right px-2 py-2">B</th>
                        <th className="text-right px-2 py-2">Matched Δ</th>
                        <th className="text-right px-2 py-2 text-slate-400">Reels Δ</th>
                        <th className="text-right px-2 py-2 text-slate-400">Carousel Δ</th>
                        <th className="text-right px-2 py-2 text-slate-400">Static Δ</th>
                        <th className="text-right px-2 py-2 text-slate-400">Stories Δ</th>
                        <th className="text-right px-3 py-2">Engagement Δ</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.handles.map((h) => (
                        <tr key={h.handle} className="border-b border-slate-200/40 data-row">
                          <td className="px-3 py-2 text-slate-800">
                            @{h.handle}
                            {h.only_in === "A" && (
                              <span className="ml-1.5 text-[9px] font-mono uppercase text-emerald-600">new</span>
                            )}
                            {h.only_in === "B" && (
                              <span className="ml-1.5 text-[9px] font-mono uppercase text-red-600">gone</span>
                            )}
                          </td>
                          <td className="text-right font-mono text-pink-600">{h.matched_a}</td>
                          <td className="text-right font-mono text-slate-500">{h.matched_b}</td>
                          <td className="text-right"><DeltaCell value={h.matched_delta} /></td>
                          <td className="text-right"><DeltaCell value={h.reels_delta} /></td>
                          <td className="text-right"><DeltaCell value={h.carousels_delta} /></td>
                          <td className="text-right"><DeltaCell value={h.static_delta} /></td>
                          <td className="text-right"><DeltaCell value={h.stories_delta} /></td>
                          <td className="px-3 text-right">
                            <DeltaCell value={h.engagement_delta} />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {data.new_posts.length > 0 && (
                  <div className="bg-white border border-emerald-200 rounded-lg overflow-hidden">
                    <div className="px-3 py-2 border-b border-emerald-200 text-[10px] font-mono uppercase tracking-widest text-emerald-600 flex items-center gap-1">
                      <TrendingUp size={10} /> New posts in A · {data.new_posts.length}
                    </div>
                    <ComparePostList posts={data.new_posts} />
                  </div>
                )}
                {data.dropped_posts.length > 0 && (
                  <div className="bg-white border border-red-200 rounded-lg overflow-hidden">
                    <div className="px-3 py-2 border-b border-red-200 text-[10px] font-mono uppercase tracking-widest text-red-600 flex items-center gap-1">
                      <TrendingDown size={10} /> Dropped posts (only in B) · {data.dropped_posts.length}
                    </div>
                    <ComparePostList posts={data.dropped_posts} />
                  </div>
                )}
                {data.kept_posts.length > 0 && (
                  <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
                    <div className="px-3 py-2 border-b border-slate-200 text-[10px] font-mono uppercase tracking-widest text-slate-500 flex items-center gap-1">
                      <Minus size={10} /> Kept in both, biggest engagement swings
                    </div>
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="text-[10px] uppercase tracking-widest font-mono text-slate-400 border-b border-slate-200">
                          <th className="text-left px-3 py-2">Handle</th>
                          <th className="text-left px-2 py-2">Type</th>
                          <th className="text-left px-2 py-2">Caption</th>
                          <th className="text-right px-2 py-2">A</th>
                          <th className="text-right px-2 py-2">B</th>
                          <th className="text-right px-3 py-2">Δ Engagement</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.kept_posts.slice(0, 20).map((p) => (
                          <tr key={`${p.shortcode || p.id || p.post_date}-${p.handle || ""}-${p.is_collab ? "c" : "o"}`} className="border-b border-slate-200/40">
                            <td className="px-3 py-2 text-slate-800">@{p.handle}</td>
                            <td className="px-2 py-2 text-slate-700 font-mono">{p.post_type}</td>
                            <td className="px-2 py-2 text-slate-500 max-w-[280px] truncate">
                              {p.caption || <span className="text-slate-400 italic">no caption</span>}
                            </td>
                            <td className="text-right px-2 font-mono text-pink-600">{formatNum(p.engagement_a)}</td>
                            <td className="text-right px-2 font-mono text-slate-400">{formatNum(p.engagement_b)}</td>
                            <td className="px-3 text-right"><DeltaCell value={p.engagement_delta} /></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            )}
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}

function CollabHover({ handle, count, posts }) {
  const collabs = useMemo(
    () => (posts || []).filter((p) => p.is_collab && p.handle === handle),
    [posts, handle]
  );

  if (!count) return <span>0</span>;

  return (
    <HoverCard openDelay={150} closeDelay={80}>
      <HoverCardTrigger asChild>
        <button
          className="underline decoration-orange-500/40 decoration-dotted underline-offset-2 hover:text-orange-600 cursor-help"
          data-testid={`collab-hover-${handle}`}
        >
          {count}
        </button>
      </HoverCardTrigger>
      <HoverCardContent
        side="left"
        align="start"
        className="bg-white border-orange-200 text-slate-900 w-96 p-0 overflow-hidden"
      >
        <div className="p-3 border-b border-orange-500/20 bg-orange-50 flex items-center gap-2">
          <Users size={13} className="text-orange-600" />
          <div>
            <div className="text-xs font-semibold text-orange-200">
              {count} collab post{count === 1 ? "" : "s"} for @{handle}
            </div>
            <div className="text-[10px] font-mono text-orange-600/70">
              Owned by other accounts · attributed here by co-authorship
            </div>
          </div>
        </div>
        <div className="max-h-64 overflow-y-auto divide-y divide-slate-100">
          {collabs.length === 0 ? (
            <div className="p-3 text-xs text-slate-400 text-center">
              Collab detail rows not in current filter window.
            </div>
          ) : (
            collabs.map((p, i) => (
              <a
                key={`${p.shortcode || i}-${p.handle || ""}`}
                href={p.post_url || "#"}
                target="_blank"
                rel="noopener noreferrer"
                className="block p-3 hover:bg-orange-500/5"
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="text-[10px] font-mono uppercase tracking-widest text-slate-500">
                    {p.post_type}
                  </span>
                  <span className="text-[10px] font-mono text-slate-400">
                    {p.post_date ? new Date(p.post_date).toLocaleDateString() : "—"}
                  </span>
                </div>
                <div className="mt-1 flex items-center gap-1.5 text-[11px]">
                  <span className="text-slate-400">Owner:</span>
                  <span className="font-mono text-orange-600">@{p.original_owner || "—"}</span>
                </div>
                <div className="mt-1.5 flex flex-wrap gap-1">
                  {(p.matched_keywords || []).map((k) => (
                    <span
                      key={k}
                      className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-pink-50 text-pink-600 border border-pink-200"
                    >
                      {k}
                    </span>
                  ))}
                </div>
                <div className="mt-1 text-[11px] text-slate-500 line-clamp-2">
                  {p.caption || <span className="text-slate-400 italic">no caption</span>}
                </div>
              </a>
            ))
          )}
        </div>
      </HoverCardContent>
    </HoverCard>
  );
}

function ComparePostList({ posts }) {
  return (
    <table className="w-full text-xs">
      <thead>
        <tr className="text-[10px] uppercase tracking-widest font-mono text-slate-400 border-b border-slate-200">
          <th className="text-left px-3 py-2">Handle</th>
          <th className="text-left px-2 py-2">Type</th>
          <th className="text-left px-2 py-2">Caption</th>
          <th className="text-right px-2 py-2">Likes</th>
          <th className="text-left px-2 py-2">Date</th>
          <th className="text-right px-3 py-2">Link</th>
        </tr>
      </thead>
      <tbody>
        {posts.slice(0, 30).map((p) => (
          <tr key={`${p.shortcode || p.id || p.timestamp || p.post_date}-${p.handle || ""}-${p.is_collab ? "c" : "o"}`} className="border-b border-slate-200/40">
            <td className="px-3 py-2 text-slate-800">
              @{p.handle}
              {p.is_collab && (
                <span
                  className="ml-1.5 inline-flex items-center gap-0.5 px-1 py-0.5 rounded text-[8px] font-mono font-semibold uppercase tracking-wider bg-orange-500/15 text-orange-600 border border-orange-500/40"
                  title={`Owner: @${p.original_owner}`}
                >
                  <Users size={7} /> Collab
                </span>
              )}
            </td>
            <td className="px-2 py-2 text-slate-700 font-mono">{p.post_type}</td>
            <td className="px-2 py-2 text-slate-500 max-w-[300px] truncate">
              {p.caption || <span className="text-slate-400 italic">no caption</span>}
            </td>
            <td className="text-right px-2 font-mono text-pink-600">{formatNum(p.likes)}</td>
            <td className="px-2 py-2 text-slate-400 font-mono">
              {p.post_date ? new Date(p.post_date).toLocaleDateString() : "—"}
            </td>
            <td className="px-3 text-right">
              <a href={p.post_url} target="_blank" rel="noopener noreferrer" className="text-pink-600 hover:text-pink-600">
                <ExternalLink size={12} />
              </a>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
